import React, { useRef, useEffect, useState, useReducer } from 'react';
import adapter from 'webrtc-adapter';
import { io } from 'socket.io-client';
import SDPUtils from 'sdp';
import * as sdpTransform from 'sdp-transform';
import { v4 } from 'uuid';
import { detectDevice, Device } from 'mediasoup-client';
import EventEmitter$1, { EventEmitter } from 'events';
import nodeBtoa from 'btoa';
import axios from 'axios';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Debounce function.
 *
 * @param {Function} fn - Function to debounce.
 * @param {Object} [options] - Options.
 * @param {number} [options.delay=500] - Delay in milliseconds.
 * @param {boolean} [options.edges=false] - Whether to call the function on the
 * leading and trailing edges of the wait timeout.
 * @returns {Function} Debounced function.
 */
function debounce(fn, { delay = 500, edges } = {}) {
    let timeout;
    let nCalls = 0;
    return (...args) => {
        nCalls += 1;
        if (edges && nCalls === 1) {
            fn(...args);
        }
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            if (!edges || nCalls > 1) {
                fn(...args);
            }
            timeout = undefined;
            nCalls = 0;
        }, delay);
    };
}

var VideoView = (_a) => {
    var { muted, mirror = false, stream, onResize } = _a, rest = __rest(_a, ["muted", "mirror", "stream", "onResize"]);
    const videoEl = useRef(null);
    useEffect(() => {
        if (!videoEl.current || !onResize) {
            return;
        }
        const resizeObserver = new ResizeObserver(debounce(() => {
            if (videoEl.current && (stream === null || stream === void 0 ? void 0 : stream.id)) {
                onResize({
                    width: videoEl.current.clientWidth,
                    height: videoEl.current.clientHeight,
                    stream,
                });
            }
        }, { delay: 1000, edges: true }));
        resizeObserver.observe(videoEl.current);
        return () => {
            resizeObserver.disconnect();
        };
    }, [stream]);
    useEffect(() => {
        if (!videoEl.current) {
            return;
        }
        if (videoEl.current.srcObject !== stream) {
            videoEl.current.srcObject = stream;
        }
        // Handle muting programatically, not as video attribute
        // https://stackoverflow.com/questions/14111917/html5-video-muted-but-still-playing
        if (videoEl.current.muted !== muted) {
            videoEl.current.muted = Boolean(muted);
        }
    }, [muted, stream, videoEl]);
    return (React.createElement("video", Object.assign({ ref: videoEl, autoPlay: true, playsInline: true }, rest, { style: Object.assign({ transform: mirror ? "scaleX(-1)" : "none", width: "100%", height: "100%" }, rest.style) })));
};

const isSafari = adapter.browserDetails.browser === "safari";

// Expects format 640x360@25, returns [width, height, fps]
const parseResolution = (res) => res.split(/[^\d]/g).map((n) => parseInt(n, 10));

/**
 * Low level constraints helper, creates the actual object used for GUM constraints
 */
function getMediaConstraints({
    disableAEC,
    disableAGC,
    hd,
    lax,
    lowDataMode,
    preferredDeviceIds,
    resolution,
    simulcast,
    widescreen,
}) {
    let HIGH_HEIGHT = 480;
    let LOW_HEIGHT = 240;
    let LOW_FPS = 15;

    if (hd) {
        // respect user choice, but default to HD for pro, and SD for free
        HIGH_HEIGHT = lax || isSafari ? 720 : { min: 360, ideal: 720 };
    }
    if (simulcast) {
        if (hd === false) {
            HIGH_HEIGHT = 360;
            LOW_HEIGHT = 270;
        } else {
            LOW_HEIGHT = 360;
        }
        LOW_FPS = 30; // we still use 30fps because of assumptions about temporal layers
    }

    const constraints = {
        audio: { ...(preferredDeviceIds.audioId && { deviceId: preferredDeviceIds.audioId }) },
        video: {
            ...(preferredDeviceIds.videoId ? { deviceId: preferredDeviceIds.videoId } : { facingMode: "user" }),
            height: lowDataMode ? LOW_HEIGHT : HIGH_HEIGHT,
            ...(lowDataMode && { frameRate: LOW_FPS }),
        },
    };
    if (lax) {
        if (!constraints.audio.deviceId) constraints.audio = true;
        delete constraints.video.facingMode;
        return constraints;
    }

    if (resolution) {
        const [w, h, fps] = parseResolution(resolution);
        if (w) constraints.video.width = { exact: w };
        if (h) constraints.video.height = { exact: h };
        if (fps) constraints.video.frameRate = { exact: fps };
        // In default camera case, let's just constrain on resolution since you set it
        delete constraints.video.facingMode;
    } else {
        constraints.video.aspectRatio = widescreen ? 16 / 9 : 4 / 3;
    }

    if (disableAGC) constraints.audio.autoGainControl = false;
    if (disableAEC) constraints.audio.echoCancellation = false;
    return constraints;
}

/**
 * High level mediaConstraints helper
 */
function getConstraints({ devices, videoId, audioId, options, type = "ideal" }) {
    const audioDevices = devices.filter((d) => d.kind === "audioinput");
    const videoDevices = devices.filter((d) => d.kind === "videoinput");
    const useDefaultAudio = !audioId || !audioDevices.some((d) => d.deviceId === audioId);
    const useDefaultVideo = !videoId || !videoDevices.some((d) => d.deviceId === videoId);
    const constraints = getMediaConstraints({
        preferredDeviceIds: {
            audioId: useDefaultAudio ? null : { [type]: audioId },
            videoId: useDefaultVideo ? null : { [type]: videoId },
        },
        ...options,
    });

    if (audioId === false || !audioDevices.length) {
        delete constraints.audio;
    }
    if (videoId === false || !videoDevices.length) {
        delete constraints.video;
    }

    return constraints;
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

// Source: https://github.com/browserify/commonjs-assert
// License: MIT

class AssertionError extends Error {
    constructor(options) {
        super(options.message);

        this.name = "AssertionError";
        this.code = "ERR_ASSERTION";
        this.actual = options.actual;
        this.expected = options.expected;
        this.operator = options.operator;

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, options.stackStartFn);
        }
    }
}

function innerOk(fn, argLen, value, message) {
    if (!value) {
        let generatedMessage = false;

        if (argLen === 0) {
            generatedMessage = true;
            message = "No value argument passed to `assert.ok()`";
        } else if (message instanceof Error) {
            throw message;
        }

        const err = new AssertionError({
            actual: value,
            expected: true,
            message,
            operator: "==",
            stackStartFn: fn,
        });
        err.generatedMessage = generatedMessage;
        throw err;
    }
}

function innerFail(obj) {
    if (obj.message instanceof Error) throw obj.message;

    throw new AssertionError(obj);
}

// Pure assertion tests whether a value is truthy, as determined
// by !!value.
function ok(...args) {
    innerOk(ok, args.length, ...args);
}

const assert = ok;

assert.notEqual = function notEqual(actual, expected, message) {
    if (arguments.length < 2) {
        throw new Error("'actual' and 'expected' arguments are required");
    }
    // eslint-disable-next-line eqeqeq
    if (actual == expected) {
        innerFail({
            actual,
            expected,
            message,
            operator: "!=",
            stackStartFn: notEqual,
        });
    }
};

assert.ok = ok;

var assert_1 = assert;

var assert$1 = /*@__PURE__*/getDefaultExportFromCjs(assert_1);

const isMobile = /mobi/i.test(navigator.userAgent);

class NoDevicesError extends Error {
    constructor(...args) {
        super(...args);
        this.name = "nodevices";
    }
}

/**
 * Basically just wrapping getUserMedia
 */
function getUserMedia(constraints) {
    if (!constraints.audio && !constraints.video) {
        return Promise.reject(new NoDevicesError("No provided devices"));
    }

    return navigator.mediaDevices.getUserMedia(constraints).catch((error) => {
        const message = `${error}, ${JSON.stringify(constraints, null, 2)}`;
        console.error(`getUserMedia ${message}`);
        throw error;
    });
}

/**
 * Stops all tracks in a media stream.
 */
function stopStreamTracks(stream, only) {
    assert$1(!only || ["audio", "video"].includes(only), "only is invalid");
    if (!only || only === "audio") stream.getAudioTracks().forEach((t) => t.stop());
    if (!only || only === "video") stream.getVideoTracks().forEach((t) => t.stop());
}

/**
 * Replaces tracks in stream with tracks from newStream
 */
function replaceTracksInStream(stream, newStream, only) {
    // adds before remove to not make stream.ended fire
    // https://github.com/w3c/mediacapture-main/issues/519
    const replacedTracks = [];
    if (!only || only === "audio") {
        replacedTracks.push(...stream.getAudioTracks());
        newStream.getAudioTracks().forEach((track) => {
            track.replacement = true;
            stream.addTrack(track);
        });
    }
    if (!only || only === "video") {
        replacedTracks.push(...stream.getVideoTracks());
        newStream.getVideoTracks().forEach((track) => {
            track.replacement = true;
            stream.addTrack(track);
        });
    }
    replacedTracks.forEach((track) => {
        track.replaced = true;
        stream.removeTrack(track);
    });
    return replacedTracks;
}

/**
 * Gets stream or replace tracks in with some fallbacks
 *
 * @param constraintOpt - for mediaConstraints.getConstraints (audioId/videoId etc)
 * @param options.replaceStream - stream to put new tracks into (instead of returning fresh)
 * @param options.fallback - try to give working stream
 * @returns {Promise<{stream, error=null}>}
 */
async function getStream(constraintOpt, { replaceStream, fallback = true } = {}) {
    assert$1(constraintOpt, "constraints is required");

    let error;
    let newConstraints;
    let retryConstraintOpt;
    let stream;

    const only = (constraintOpt.audioId === false && "video") || (constraintOpt.videoId === false && "audio");
    // Mobile can't open two devices at once. Firefox also can't open two audio devices at once.
    // It looks nicer when we don't stop tracks while getting new streams, so we try to not do it
    // unless required.
    const stopTracks = isMobile || only !== "video";
    const constraints = getConstraints(constraintOpt);
    const addDetails = (err, orgErr) => {
        err.details = {
            constraints,
            constraint: err.constraint || orgErr?.constraint,
            newConstraints,
            fallback,
            stopTracks,
            ...(err !== error && { error: String(error) }),
        };
        return err;
    };
    if (stopTracks && replaceStream) stopStreamTracks(replaceStream, only);
    try {
        stream = await getUserMedia(constraints);
    } catch (e) {
        error = e;
        if (!fallback) {
            throw addDetails(e);
        }
        if (e.name === "OverconstrainedError") {
            retryConstraintOpt = {
                deviceId: { videoId: null, audioId: null },
                width: { lax: true },
                height: { lax: true },
                "": { videoId: null, audioId: null, lax: true },
            }[e.constraint || ""];
        } else if (e.name === "NotFoundError") {
            if (constraints.audio && constraints.video) {
                // Since we requested both audio and video, there's
                // a chance only one of the devices are NotFound,
                // let's try to only get one of them.
                try {
                    stream = await getUserMedia(getConstraints({ ...constraintOpt, audioId: false }));
                } catch (e2) {
                    if (e2.name !== "NotFoundError") {
                        throw addDetails(e2, e);
                    }
                }
                try {
                    if (!stream) stream = await getUserMedia(getConstraints({ ...constraintOpt, videoId: false }));
                } catch (e2) {
                    throw addDetails(e2, e);
                }
            }
        } else if (e.name === "NotAllowedError" || e.name === "NotReadableError" || e.name === "AbortError") {
            // NotAllowedError - User didn't allow us
            // NotReadableError - OS can't read
            // AbortError - Other error for browser giving us this

            // Try to revert back to what we had before
            if (replaceStream && !stopTracks) {
                // We didn't stop the tracks, so let's do that and retry
                stopStreamTracks(replaceStream, only);
                retryConstraintOpt = constraintOpt;
            }
            // No existing stream, try to get a new stream
            // if we weren't explicitly disallowed.
            else if (e.name !== "NotAllowedError") {
                // First of all, it seems like NotReadableError is REALLY a
                // OverconstrainedError in many cases, let's try with laxer constraints
                try {
                    stream = await getUserMedia(
                        getConstraints({ ...constraintOpt, options: { ...constraintOpt.options, lax: true } })
                    );
                } catch (e2) {
                    console.warn(`Tried getting stream again with laxer constraints, but failed: ${"" + e2}`);
                }
                // Message often hints at which was the problem, let's use that
                const errMsg = ("" + e).toLowerCase();
                const problemWith = { audio: "audioId", video: "videoId" }[/(video|audio)/.exec(errMsg)?.[0] || only];
                if (!stream && problemWith) {
                    try {
                        stream = await getUserMedia(getConstraints({ ...constraintOpt, [problemWith]: null }));
                    } catch (e2) {
                        console.warn(`Re-tried ${problemWith} with no constraints, but failed: ${"" + e2}`);
                    }
                }
                if (!stream) {
                    // Try disabling the kind we have trouble with
                    // to at least get SOMETHING working
                    const tryOnly = problemWith ? [problemWith] : ["videoId", "audioId"];
                    for (const kind of tryOnly) {
                        try {
                            stream = await getUserMedia(getConstraints({ ...constraintOpt, [kind]: false }));
                        } catch (e2) {
                            console.warn(`Re-tried without ${kind}, but failed: ${"" + e2}`);
                        }
                        if (stream) break;
                    }
                }
            }
        }
    }
    if (retryConstraintOpt) {
        newConstraints = getConstraints({
            ...constraintOpt,
            ...retryConstraintOpt,
            options: { ...constraintOpt.options, lax: retryConstraintOpt.lax },
            ...{ audio: { videoId: false }, video: { audioId: false } }[only],
        });
        try {
            stream = await getUserMedia(newConstraints);
        } catch (e) {
            throw addDetails(e, error);
        }
    }
    if (!stream) {
        throw addDetails(error);
    }
    let replacedTracks;
    if (replaceStream) {
        // In case we didn't do this earlier
        if (!stopTracks) stopStreamTracks(replaceStream, only);
        replacedTracks = replaceTracksInStream(replaceStream, stream, only);
        stream = replaceStream;
    }
    return { error: error && addDetails(error), stream, replacedTracks };
}

class LocalMediaEvent extends CustomEvent {
    constructor(eventType, eventInitDict) {
        super(eventType, eventInitDict);
    }
}
const TypedLocalMediaEventTarget = EventTarget;
class LocalMedia extends TypedLocalMediaEventTarget {
    constructor(optionsOrStream) {
        var _a, _b;
        super();
        this._options = null;
        this._devices = [];
        this._isTogglingCameraEnabled = false;
        if (optionsOrStream instanceof MediaStream) {
            this.stream = optionsOrStream;
        }
        else {
            this._options = optionsOrStream;
            this.stream = new MediaStream();
        }
        this._cameraEnabled = ((_a = this.stream.getVideoTracks()[0]) === null || _a === void 0 ? void 0 : _a.enabled) || false;
        this._microphoneEnabled = ((_b = this.stream.getAudioTracks()[0]) === null || _b === void 0 ? void 0 : _b.enabled) || false;
        this._rtcManagers = [];
        this.screenshareStream = undefined;
        navigator.mediaDevices.addEventListener("devicechange", this._updateDeviceList.bind(this));
    }
    addRtcManager(rtcManager) {
        this._rtcManagers.push(rtcManager);
    }
    removeRtcManager(rtcManager) {
        this._rtcManagers = this._rtcManagers.filter((r) => r !== rtcManager);
    }
    getCameraDeviceId() {
        return this._currentCameraDeviceId;
    }
    getMicrophoneDeviceId() {
        return this._currentMicrophoneDeviceId;
    }
    isCameraEnabled() {
        return this._cameraEnabled;
    }
    isMicrophoneEnabled() {
        return this._microphoneEnabled;
    }
    toggleCameraEnabled(enabled) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._isTogglingCameraEnabled) {
                return;
            }
            let track = this.stream.getVideoTracks()[0];
            const newValue = enabled !== null && enabled !== void 0 ? enabled : !(track === null || track === void 0 ? void 0 : track.enabled);
            if (this._cameraEnabled === newValue) {
                return;
            }
            this._cameraEnabled = newValue;
            this.dispatchEvent(new LocalMediaEvent("camera_enabled", { detail: { enabled: this._cameraEnabled } }));
            // Only stop tracks if we fully own the media stream
            const shouldStopTrack = !!this._options;
            this._isTogglingCameraEnabled = true;
            try {
                if (this._cameraEnabled) {
                    if (track) {
                        // We have existing video track, just enable it
                        track.enabled = true;
                    }
                    else {
                        // We dont have video track, get new one
                        yield getStream(Object.assign(Object.assign({}, this._getConstraintsOptions()), { audioId: false, videoId: this._currentCameraDeviceId, type: "exact" }), { replaceStream: this.stream });
                        track = this.stream.getVideoTracks()[0];
                    }
                }
                else {
                    if (!track) {
                        return;
                    }
                    track.enabled = false;
                    if (shouldStopTrack) {
                        track.stop();
                        this.stream.removeTrack(track);
                    }
                }
                // Dispatch event on stream to allow RTC layer effects
                this.stream.dispatchEvent(new LocalMediaEvent("stopresumevideo", { detail: { track, enable: this._cameraEnabled } }));
            }
            catch (error) {
                // TODO: Update error state
            }
            this._isTogglingCameraEnabled = false;
        });
    }
    toggleMichrophoneEnabled(enabled) {
        const audioTrack = this.stream.getAudioTracks()[0];
        if (!audioTrack) {
            return;
        }
        // Update internal state and dispatch event early
        this._microphoneEnabled = enabled !== null && enabled !== void 0 ? enabled : !audioTrack.enabled;
        this.dispatchEvent(new LocalMediaEvent("microphone_enabled", { detail: { enabled: this._microphoneEnabled } }));
        audioTrack.enabled = this._microphoneEnabled;
    }
    startScreenshare() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.screenshareStream) {
                return this.screenshareStream;
            }
            const screenshareStream = yield navigator.mediaDevices.getDisplayMedia();
            this.screenshareStream = screenshareStream;
            return this.screenshareStream;
        });
    }
    stopScreenshare() {
        var _a;
        (_a = this.screenshareStream) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((track) => track.stop());
        this.screenshareStream = undefined;
    }
    _getConstraintsOptions() {
        return {
            devices: this._devices,
            options: {
                disableAEC: false,
                disableAGC: false,
                hd: true,
                lax: false,
                lowDataMode: false,
                simulcast: true,
                widescreen: true,
            },
        };
    }
    _setDevice({ audioId, videoId }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { replacedTracks } = yield getStream(Object.assign(Object.assign({}, this._getConstraintsOptions()), { audioId,
                videoId, type: "exact" }), { replaceStream: this.stream });
            if (replacedTracks) {
                replacedTracks.forEach((oldTrack) => {
                    const newTrack = oldTrack.kind === "audio" ? this.stream.getAudioTracks()[0] : this.stream.getVideoTracks()[0];
                    this._rtcManagers.forEach((rtcManager) => {
                        rtcManager.replaceTrack(oldTrack, newTrack);
                    });
                });
            }
            this.dispatchEvent(new LocalMediaEvent("stream_updated", {
                detail: { stream: this.stream },
            }));
        });
    }
    setCameraDevice(deviceId) {
        return __awaiter(this, void 0, void 0, function* () {
            this._currentCameraDeviceId = deviceId;
            yield this._setDevice({ videoId: this._currentCameraDeviceId, audioId: false });
        });
    }
    setMicrophoneDevice(deviceId) {
        return __awaiter(this, void 0, void 0, function* () {
            this._currentMicrophoneDeviceId = deviceId;
            yield this._setDevice({ audioId: this._currentMicrophoneDeviceId, videoId: false });
        });
    }
    _updateDeviceList() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const devices = yield navigator.mediaDevices.enumerateDevices();
                this.dispatchEvent(new LocalMediaEvent("device_list_updated", {
                    detail: {
                        cameraDevices: devices.filter((d) => d.kind === "videoinput"),
                        microphoneDevices: devices.filter((d) => d.kind === "audioinput"),
                        speakerDevices: devices.filter((d) => d.kind === "audiooutput"),
                    },
                }));
                this._devices = devices;
            }
            catch (error) {
                this.dispatchEvent(new LocalMediaEvent("device_list_update_error", {
                    detail: {
                        error,
                    },
                }));
                throw error;
            }
        });
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._updateDeviceList();
            if (this._options) {
                yield getStream(Object.assign(Object.assign({}, this._getConstraintsOptions()), { audioId: this._options.audio, videoId: this._options.video }), { replaceStream: this.stream });
                const cameraTrack = this.stream.getVideoTracks()[0];
                if (cameraTrack) {
                    this._cameraEnabled = cameraTrack.enabled;
                    this._currentCameraDeviceId = cameraTrack.getSettings().deviceId;
                }
                const microphoneTrack = this.stream.getAudioTracks()[0];
                if (microphoneTrack) {
                    this._microphoneEnabled = microphoneTrack.enabled;
                    this._currentMicrophoneDeviceId = microphoneTrack.getSettings().deviceId;
                }
            }
            this.dispatchEvent(new LocalMediaEvent("stream_updated", {
                detail: { stream: this.stream },
            }));
            return this.stream;
        });
    }
    stop() {
        var _a;
        if (this._options) {
            (_a = this.stream) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((t) => {
                t.stop();
            });
        }
    }
}

const initialState$1 = {
    cameraDeviceError: null,
    cameraDevices: [],
    isSettingCameraDevice: false,
    isSettingMicrophoneDevice: false,
    isStarting: false,
    microphoneDeviceError: null,
    microphoneDevices: [],
    speakerDevices: [],
    startError: null,
};
function reducer$1(state, action) {
    switch (action.type) {
        case "DEVICE_LIST_UPDATED":
            return Object.assign(Object.assign({}, state), action.payload);
        case "LOCAL_STREAM_UPDATED":
            return Object.assign(Object.assign({}, state), { currentCameraDeviceId: action.payload.currentCameraDeviceId, currentMicrophoneDeviceId: action.payload.currentMicrophoneDeviceId, localStream: action.payload.stream });
        case "SET_CAMERA_DEVICE":
            return Object.assign(Object.assign({}, state), { cameraDeviceError: null, isSettingCameraDevice: true });
        case "SET_CAMERA_DEVICE_COMPLETE":
            return Object.assign(Object.assign({}, state), { isSettingCameraDevice: false });
        case "SET_CAMERA_DEVICE_ERROR":
            return Object.assign(Object.assign({}, state), { cameraDeviceError: action.payload, isSettingCameraDevice: false });
        case "SET_MICROPHONE_DEVICE":
            return Object.assign(Object.assign({}, state), { isSettingMicrophoneDevice: true, microphoneDeviceError: null });
        case "SET_MICROPHONE_DEVICE_COMPLETE":
            return Object.assign(Object.assign({}, state), { isSettingMicrophoneDevice: false });
        case "SET_MICROPHONE_DEVICE_ERROR":
            return Object.assign(Object.assign({}, state), { isSettingMicrophoneDevice: false, microphoneDeviceError: action.payload });
        case "START":
            return Object.assign(Object.assign({}, state), { isStarting: true, startError: null });
        case "START_COMPLETE":
            return Object.assign(Object.assign({}, state), { isStarting: false });
        case "START_ERROR":
            return Object.assign(Object.assign({}, state), { isStarting: false, startError: action.payload });
        default:
            return state;
    }
}
function useLocalMedia(optionsOrStream = { audio: true, video: true }) {
    const [localMedia] = useState(() => new LocalMedia(optionsOrStream));
    const [state, dispatch] = useReducer(reducer$1, initialState$1);
    useEffect(() => {
        localMedia.addEventListener("device_list_updated", (e) => {
            const { cameraDevices, microphoneDevices, speakerDevices } = e.detail;
            dispatch({ type: "DEVICE_LIST_UPDATED", payload: { cameraDevices, microphoneDevices, speakerDevices } });
        });
        localMedia.addEventListener("stream_updated", (e) => {
            const { stream } = e.detail;
            dispatch({
                type: "LOCAL_STREAM_UPDATED",
                payload: {
                    stream,
                    currentCameraDeviceId: localMedia.getCameraDeviceId(),
                    currentMicrophoneDeviceId: localMedia.getMicrophoneDeviceId(),
                },
            });
        });
        const start = () => __awaiter(this, void 0, void 0, function* () {
            dispatch({ type: "START" });
            try {
                yield localMedia.start();
                dispatch({ type: "START_COMPLETE" });
            }
            catch (error) {
                dispatch({ type: "START_ERROR", payload: error });
            }
        });
        start();
        // Perform cleanup on unmount
        return () => {
            localMedia.stop();
        };
    }, []);
    return {
        state,
        actions: {
            setCameraDevice: (...args) => __awaiter(this, void 0, void 0, function* () {
                dispatch({ type: "SET_CAMERA_DEVICE" });
                try {
                    yield localMedia.setCameraDevice(...args);
                    dispatch({ type: "SET_CAMERA_DEVICE_COMPLETE" });
                }
                catch (error) {
                    dispatch({ type: "SET_CAMERA_DEVICE_ERROR", payload: error });
                }
            }),
            setMicrophoneDevice: (...args) => __awaiter(this, void 0, void 0, function* () {
                dispatch({ type: "SET_MICROPHONE_DEVICE" });
                try {
                    yield localMedia.setMicrophoneDevice(...args);
                    dispatch({ type: "SET_MICROPHONE_DEVICE_COMPLETE" });
                }
                catch (error) {
                    dispatch({ type: "SET_MICROPHONE_DEVICE_ERROR", payload: error });
                }
            }),
            toggleCameraEnabled: (...args) => {
                return localMedia.toggleCameraEnabled(...args);
            },
            toggleMicrophoneEnabled: (...args) => {
                return localMedia.toggleMichrophoneEnabled(...args);
            },
        },
        _ref: localMedia,
    };
}

const EVENTS = {
    CLIENT_CONNECTION_STATUS_CHANGED: "client_connection_status_changed",
    STREAM_ADDED: "stream_added",
    RTC_MANAGER_CREATED: "rtc_manager_created",
    RTC_MANAGER_DESTROYED: "rtc_manager_destroyed",
    LOCAL_STREAM_TRACK_ADDED: "local_stream_track_added",
    LOCAL_STREAM_TRACK_REMOVED: "local_stream_track_removed",
    REMOTE_STREAM_TRACK_ADDED: "remote_stream_track_added",
    REMOTE_STREAM_TRACK_REMOVED: "remote_stream_track_removed",
};

const TYPES = {
    CONNECTING: "connecting",
    CONNECTION_FAILED: "connection_failed",
    CONNECTION_SUCCESSFUL: "connection_successful",
    CONNECTION_DISCONNECTED: "connection_disconnected",
};

// Protocol enum used for the CLIENT (Make sure to keep it in sync with its server counterpart)

// Requests: messages from the client to the server
const PROTOCOL_REQUESTS = {
    BLOCK_CLIENT: "block_client",
    CLAIM_ROOM: "claim_room",
    CLEAR_CHAT_HISTORY: "clear_chat_history",
    ENABLE_AUDIO: "enable_audio",
    ENABLE_VIDEO: "enable_video",
    END_STREAM: "end_stream",
    FETCH_MEDIASERVER_CONFIG: "fetch_mediaserver_config",
    HANDLE_KNOCK: "handle_knock",
    IDENTIFY_DEVICE: "identify_device",
    INVITE_CLIENT_AS_MEMBER: "invite_client_as_member",
    JOIN_ROOM: "join_room",
    KICK_CLIENT: "kick_client",
    KNOCK_ROOM: "knock_room",
    LEAVE_ROOM: "leave_room",
    SEND_CLIENT_METADATA: "send_client_metadata",
    SET_LOCK: "set_lock",
    SHARE_MEDIA: "share_media",
    START_NEW_STREAM: "start_new_stream",
    START_SCREENSHARE: "start_screenshare",
    STOP_SCREENSHARE: "stop_screenshare",
    START_URL_EMBED: "start_url_embed",
    STOP_URL_EMBED: "stop_url_embed",
    START_RECORDING: "start_recording",
    STOP_RECORDING: "stop_recording",
    SFU_TOKEN: "sfu_token",
};

// Responses: messages from the server to the client, in response to requests
const PROTOCOL_RESPONSES = {
    AUDIO_ENABLED: "audio_enabled",
    BACKGROUND_IMAGE_CHANGED: "background_image_changed",
    BLOCK_ADDED: "block_added",
    BLOCK_REMOVED: "block_removed",
    CHAT_HISTORY_CLEARED: "chat_history_cleared",
    CLIENT_BLOCKED: "client_blocked",
    CLIENT_INVITED_AS_MEMBER: "client_invited_as_member",
    CLIENT_KICKED: "client_kicked",
    CLIENT_LEFT: "client_left",
    CLIENT_METADATA_RECEIVED: "client_metadata_received",
    CLIENT_READY: "client_ready",
    CLIENT_ROLE_CHANGED: "client_role_changed",
    CLIENT_USER_ID_CHANGED: "client_user_id_changed",
    CONTACTS_UPDATED: "contacts_updated",
    DEVICE_IDENTIFIED: "device_identified",
    ROOM_ROLES_UPDATED: "room_roles_updated",
    KNOCK_HANDLED: "knock_handled",
    KNOCK_PAGE_BACKGROUND_CHANGED: "knock_page_background_changed",
    KNOCKER_LEFT: "knocker_left",
    MEDIASERVER_CONFIG: "mediaserver_config",
    MEDIA_SHARED: "media_shared",
    MEMBER_INVITE: "member_invite",
    NEW_CLIENT: "new_client",
    NEW_STREAM_STARTED: "new_stream_started",
    SCREENSHARE_STARTED: "screenshare_started",
    SCREENSHARE_STOPPED: "screenshare_stopped",
    OWNER_NOTIFIED: "owner_notified",
    OWNERS_CHANGED: "owners_changed",
    PLAY_CLIENT_STICKER: "play_client_sticker",
    ROOM_INTEGRATION_ENABLED: "room_integration_enabled",
    ROOM_INTEGRATION_DISABLED: "room_integration_disabled",
    ROOM_JOINED: "room_joined",
    ROOM_KNOCKED: "room_knocked",
    ROOM_LEFT: "room_left",
    ROOM_LOCKED: "room_locked",
    ROOM_PERMISSIONS_CHANGED: "room_permissions_changed",
    ROOM_LOGO_CHANGED: "room_logo_changed",
    ROOM_TYPE_CHANGED: "room_type_changed",
    ROOM_MODE_CHANGED: "room_mode_changed",
    SOCKET_USER_ID_CHANGED: "socket_user_id_changed",
    STICKERS_UNLOCKED: "stickers_unlocked",
    STREAM_ENDED: "stream_ended",
    URL_EMBED_STARTED: "url_embed_started",
    URL_EMBED_STOPPED: "url_embed_stopped",
    RECORDING_STARTED: "recording_started",
    RECORDING_STOPPED: "recording_stopped",
    USER_NOTIFIED: "user_notified",
    VIDEO_ENABLED: "video_enabled",
    CLIENT_UNABLE_TO_JOIN: "client_unable_to_join",
};

// Relays: messages between clients, relayed through the server
const RELAY_MESSAGES = {
    CHAT_MESSAGE: "chat_message",
    CHAT_READ_STATE: "chat_read_state",
    CHAT_STATE: "chat_state",
    ICE_CANDIDATE: "ice_candidate",
    ICE_END_OF_CANDIDATES: "ice_endofcandidates",
    READY_TO_RECEIVE_OFFER: "ready_to_receive_offer",
    REMOTE_CLIENT_MEDIA_REQUEST: "remote_client_media_request",
    SDP_ANSWER: "sdp_answer",
    SDP_OFFER: "sdp_offer",
    VIDEO_STICKER: "video_sticker",
};

const CAMERA_STREAM_ID$2 = "0";

const STREAM_TYPES = {
    CAMERA: "camera",
    SCREEN_SHARE: "screen_share",
};

class RtcStream {
    constructor(id, type) {
        assert$1.notEqual(id, undefined, "id is required");
        assert$1.notEqual(type, undefined, "type is required");

        this.id = "" + id;
        this.type = type;

        this.isEnabled = true;
        this.hasSupportForAutoSuperSize = false;
        this.isAudioEnabled = true;
        this.isVideoEnabled = true;
        this.status = TYPES.CONNECTING;
    }

    setup(stream) {
        this.stream = stream;
        this.streamId = stream.id;
        this.setVideoEnabled(this.isVideoEnabled && stream.getVideoTracks().length > 0);
        this.setAudioEnabled(this.isAudioEnabled && stream.getAudioTracks().length > 0);
        return this;
    }

    setStatus(status) {
        this.status = status;
        return this;
    }

    setVideoEnabled(isEnabled) {
        this.isVideoEnabled = isEnabled;
        if (!this.stream) {
            return;
        }
        this.stream.getVideoTracks().forEach((track) => {
            track.enabled = isEnabled;
        });
    }

    setAudioEnabled(isEnabled) {
        this.isAudioEnabled = isEnabled;
        if (!this.stream) {
            return;
        }
        this.stream.getAudioTracks().forEach((track) => {
            track.enabled = isEnabled;
        });
    }

    static getCameraId() {
        return CAMERA_STREAM_ID$2;
    }

    static getTypeFromId(id) {
        assert$1.notEqual(id, undefined, "id is required");

        const streamId = "" + id;
        return streamId === CAMERA_STREAM_ID$2 ? STREAM_TYPES.CAMERA : STREAM_TYPES.SCREEN_SHARE;
    }
}

const DEFAULT_SOCKET_PATH = "/protocol/socket.io/v4";

/**
 * Wrapper class that extends the Socket.IO client library.
 */
class ServerSocket {
    constructor(hostName, optionsOverrides) {
        this._socket = io(hostName, {
            path: DEFAULT_SOCKET_PATH,
            randomizationFactor: 0.5,
            reconnectionDelay: 250,
            reconnectionDelayMax: 5000,
            timeout: 5000,
            transports: ["websocket"],
            withCredentials: true,
            ...optionsOverrides,
        });
        this._socket.io.on("reconnect", () => {
            this._socket.sendBuffer = [];
        });
        this._socket.io.on("reconnect_attempt", () => {
            if (this._wasConnectedUsingWebsocket) {
                this._socket.io.opts.transports = ["websocket"];
                // only fallback to polling if not safari
                // safari doesn't support cross doamin cookies making load-balancer stickiness not work
                // and if socket.io reconnects to another signal instance with polling it will fail
                // remove if we move signal to a whereby.com subdomain
                if (adapter.browserDetails.browser !== "safari") delete this._wasConnectedUsingWebsocket;
            } else {
                this._socket.io.opts.transports = ["websocket", "polling"];
            }
        });
        this._socket.on("connect", () => {
            const transport = this.getTransport();
            if (transport === "websocket") {
                this._wasConnectedUsingWebsocket = true;
            }
        });
    }

    connect() {
        if (this.isConnected() || this.isConnecting()) {
            return;
        }
        this._socket.open();
    }

    disconnect() {
        this._socket.disconnect();
    }

    disconnectOnConnect() {
        this._socket.once("connect", () => {
            this._socket.disconnect();
        });
    }

    emit() {
        this._socket.emit.apply(this._socket, arguments);
    }

    emitIfConnected(eventName, data) {
        if (!this.isConnected()) {
            return;
        }
        this.emit(eventName, data);
    }

    getTransport() {
        return (
            this._socket &&
            this._socket.io &&
            this._socket.io.engine &&
            this._socket.io.engine.transport &&
            this._socket.io.engine.transport.name
        );
    }

    getManager() {
        return this._socket.io;
    }

    isConnecting() {
        return this._socket && this._socket.connecting;
    }

    isConnected() {
        return this._socket && this._socket.connected;
    }

    /**
     * Register a new event handler.
     *
     * @param {string} eventName - Name of the event to listen for.
     * @param {function} handler - The callback function that should be called for the event.
     * @returns {function} Function to deregister the listener.
     */
    on(eventName, handler) {
        this._socket.on(eventName, handler);

        return () => {
            this._socket.off(eventName, handler);
        };
    }

    /**
     * Register a new event handler to be triggered only once.
     *
     * @param {string} eventName - Name of the event to listen for.
     * @param {function} handler - The function that should be called for the event.
     */
    once(eventName, handler) {
        this._socket.once(eventName, handler);
    }

    /**
     * Deregister an event handler.
     *
     * @param {string} eventName - Name of the event the handler is registered for.
     * @param {function} handler - The callback that will be deregistered.
     */
    off(eventName, handler) {
        this._socket.off(eventName, handler);
    }
}

/**
 * Detect mic issue which seems to happen on OSX when the computer is woken up and sleeping
 * frequently. A browser restart fixes this.
 *
 * Should be called after the connection has been up for a while.
 *
 * @see Bug report {@link https://bugs.chromium.org/p/webrtc/issues/detail?id=4799}
 */
function detectMicrophoneNotWorking(pc) {
    if (
        adapter.browserDetails.browser !== "chrome" ||
        adapter.browserDetails.browser < 58 || // legacy getStats is no longer supported.
        pc.signalingState === "closed"
    ) {
        return Promise.resolve(false);
    }
    const sendingAudio = pc.getSenders().some((sender) => sender.track && sender.track.kind === "audio");
    const receivingAudio = pc.getReceivers().some((receiver) => receiver.track && receiver.track.kind === "audio");
    return pc.getStats(null).then((result) => {
        let microphoneFailed = false;
        result.forEach((report) => {
            if (
                report.type === "outbound-rtp" &&
                (report.kind === "audio" || report.mediaType === "audio") &&
                sendingAudio
            ) {
                if (report.bytesSent === 0) {
                    microphoneFailed = "outbound";
                }
            } else if (
                report.type === "inbound-rtp" &&
                (report.kind === "audio" || report.mediaType === "audio") &&
                receivingAudio
            ) {
                if (report.bytesReceived === 0) {
                    microphoneFailed = "inbound";
                }
            }
        });
        return microphoneFailed;
    });
}

var rtcManagerEvents = {
    CAMERA_NOT_WORKING: "camera_not_working",
    CONNECTION_BLOCKED_BY_NETWORK: "connection_blocked_by_network",
    MICROPHONE_NOT_WORKING: "microphone_not_working",
    MICROPHONE_STOPPED_WORKING: "microphone_stopped_working",
    SFU_CONNECTION_CLOSED: "sfu_connection_closed",
    COLOCATION_SPEAKER: "colocation_speaker",
    DOMINANT_SPEAKER: "dominant_speaker",
};

const browserName$3 = adapter.browserDetails.browser;
const browserVersion$1 = adapter.browserDetails.version;

function setCodecPreferenceSDP(sdp, vp9On, redOn) {
    try {
        const sdpObject = sdpTransform.parse(sdp);
        if (Array.isArray(sdpObject?.media)) {
            //audio
            const mediaAudio = sdpObject.media.find((m) => m.type === "audio");
            if (Array.isArray(mediaAudio?.rtp)) {
                const rtp = mediaAudio.rtp;
                for (let i = 0; i < rtp.length; i++) {
                    if (redOn && rtp[i].codec === "red") {
                        const payloads = mediaAudio.payloads.split(" ");
                        const pt = payloads.indexOf("" + rtp[i].payload);
                        if (pt && pt !== -1 && pt >= 0) {
                            payloads.unshift(payloads.splice(pt, 1)[0]);
                            mediaAudio.payloads = payloads.join(" ");
                        }
                    }
                }
            }
            //video
            const mediaVideo = sdpObject.media.find((m) => m.type === "video");
            if (Array.isArray(mediaVideo?.rtp)) {
                const rtp = mediaVideo.rtp;
                for (let i = 0; i < rtp.length; i++) {
                    if (vp9On && rtp[i].codec === "VP9") {
                        const payloads = mediaVideo.payloads.split(" ");
                        const pt = payloads.indexOf("" + rtp[i].payload);
                        if (pt && pt !== -1 && pt >= 0) {
                            payloads.unshift(payloads.splice(pt, 1)[0]);
                            mediaVideo.payloads = payloads.join(" ");
                        }
                    }
                }
            }
        }
        const newSdp = sdpTransform.write(sdpObject);
        return newSdp;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log("setCodecPreferenceSDP error:", error);
    }
}

// SDP mangling for deprioritizing H264
function deprioritizeH264(sdp) {
    return SDPUtils.splitSections(sdp)
        .map((section) => {
            // only modify video sections
            if (SDPUtils.getKind(section) !== "video") return section;

            // list of payloadTypes used in this sdp/section
            const h264payloadTypes = SDPUtils.matchPrefix(section, "a=rtpmap:")
                .map((line) => SDPUtils.parseRtpMap(line))
                .filter((codec) => /h264/i.test(codec.name))
                .map((codec) => "" + codec.payloadType);

            // return as is if no h264 found
            if (!h264payloadTypes.length) return section;

            // reorder and replace
            const mline = SDPUtils.matchPrefix(section, "m=video")[0];
            const mlinePayloadsSection = /(\s\d+)+$/i.exec(mline)[0];
            const mlinePayloadsNonH264 = mlinePayloadsSection
                .split(" ")
                .filter((payloadType) => payloadType && !h264payloadTypes.includes(payloadType));
            const reorderedPayloads = [...mlinePayloadsNonH264, ...h264payloadTypes].join(" ");
            const newmline = mline.replace(mlinePayloadsSection, " " + reorderedPayloads);
            return section.replace(mline, newmline);
        })
        .join("");
}

// TODO: currently assumes video, look at track.kind
// ensures that SSRCs in new description match ssrcs in old description
function replaceSSRCs(currentDescription, newDescription) {
    let ssrcs = currentDescription.match(/a=ssrc-group:FID (\d+) (\d+)\r\n/);
    let newssrcs = newDescription.match(/a=ssrc-group:FID (\d+) (\d+)\r\n/);
    // Firefox offers dont have a FID ssrc group (yet)
    if (!ssrcs) {
        ssrcs = currentDescription.match(/a=ssrc:(\d+) cname:(.*)\r\n/g)[1].match(/a=ssrc:(\d+)/);
        newssrcs = newDescription.match(/a=ssrc:(\d+) cname:(.*)\r\n/g)[1].match(/a=ssrc:(\d+)/);
    }
    for (let i = 1; i < ssrcs.length; i++) {
        newDescription = newDescription.replace(new RegExp(newssrcs[i], "g"), ssrcs[i]);
    }
    return newDescription;
}

// Firefox < 63 (but not Firefox ESR 60) is affected by this:
// https://bugzilla.mozilla.org/show_bug.cgi?id=1478685
// filter out the mid rtp header extension
function filterMidExtension(sdp) {
    if (browserName$3 !== "safari" && (browserName$3 !== "firefox" || browserVersion$1 >= 63 || browserVersion$1 === 60)) {
        return sdp;
    }
    return (
        SDPUtils.splitLines(sdp.trim())
            .filter((line) => {
                if (!line.startsWith("a=extmap:")) {
                    return true;
                }
                const extmap = SDPUtils.parseExtmap(line);
                return extmap.uri !== "urn:ietf:params:rtp-hdrext:sdes:mid";
            })
            .join("\r\n") + "\r\n"
    );
}

// Firefox < 68 (at least) is affected by this, although it is not
// clear that FF is to blame:
// https://bugzilla.mozilla.org/show_bug.cgi?id=1534673
// Filter out a:msid-semantic header
function filterMsidSemantic(sdp) {
    if (browserName$3 !== "firefox") {
        return sdp;
    }
    return (
        SDPUtils.splitLines(sdp.trim())
            .map((line) => (line.startsWith("a=msid-semantic:") ? "a=msid-semantic: WMS *" : line))
            .join("\r\n") + "\r\n"
    );
}

function isRelayed(pc) {
    return pc.getStats(null).then((result) => {
        let localCandidateType;
        let remoteCandidateType;
        result.forEach((report) => {
            // Chrome 58+ / spec
            if (report.type === "transport" && report.selectedCandidatePairId) {
                const transport = result.get(report.selectedCandidatePairId);
                if (!transport) {
                    return;
                }
                localCandidateType = result.get(transport.localCandidateId).candidateType;
                remoteCandidateType = result.get(transport.remoteCandidateId).candidateType;
                return;
            }
            // Firefox (missing type=transport)
            if (report.type === "candidate-pair" && report.selected) {
                localCandidateType = result.get(report.localCandidateId).candidateType;
                remoteCandidateType = result.get(report.remoteCandidateId).candidateType;
            }
        });
        return (
            localCandidateType === "relay" ||
            localCandidateType === "relayed" || // relay: spec-stats; relayed: Firefox (bug)
            remoteCandidateType === "relay" ||
            remoteCandidateType === "relayed"
        );
    });
}

// use https://w3c.github.io/webrtc-pc/#dom-rtcrtpsender-setparameters to change the video bandwidth.
function setVideoBandwidthUsingSetParameters(pc, bandwidth, logger = console) {
    const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
    if (!sender) {
        return Promise.resolve();
    }

    const parameters = sender.getParameters();
    if (parameters.encodings && parameters.encodings.length === 0) {
        return Promise.resolve();
    }

    if (!parameters.encodings) {
        // workaround for Firefox
        parameters.encodings = [{}];
    }

    if (bandwidth === 0) {
        delete parameters.encodings[0].maxBitrate;
    } else {
        parameters.encodings[0].maxBitrate = bandwidth * 1000; // convert to bps
    }

    return sender.setParameters(parameters).catch((err) => {
        logger.error("setParameters err: ", err);
    });
}

const MAXIMUM_TURN_BANDWIDTH = 1280; // kbps;
const MAXIMUM_TURN_BANDWIDTH_UNLIMITED = -1; // kbps;

const MEDIA_JITTER_BUFFER_TARGET = 400; // milliseconds;

class Session {
    constructor({ peerConnectionId, bandwidth, maximumTurnBandwidth, deprioritizeH264Encoding, logger = console }) {
        this.peerConnectionId = peerConnectionId;
        this.relayCandidateSeen = false;
        this.pc = null;
        this.wasEverConnected = false;
        this.connectionStatus = null;
        this.stats = {
            totalSent: 0,
            totalRecv: 0,
        };
        this.bandwidth = bandwidth || 0; // maximum bandwidth in kbps.
        this.maximumTurnBandwidth = maximumTurnBandwidth;
        this.pending = [];
        this.isOperationPending = false;
        this.streamIds = [];
        this.streams = [];
        this.earlyIceCandidates = [];
        this.afterConnected = new Promise((resolve) => {
            this.registerConnected = resolve;
        });
        this.offerOptions = { offerToReceiveAudio: true, offerToReceiveVideo: true };
        this._deprioritizeH264Encoding = deprioritizeH264Encoding;
        this._logger = logger;
    }

    setAndGetPeerConnection({ clientId, constraints, peerConnectionConfig, shouldAddLocalVideo }) {
        this.peerConnectionConfig = peerConnectionConfig;
        this.shouldAddLocalVideo = shouldAddLocalVideo;
        this.clientId = clientId;
        this.pc = new RTCPeerConnection(peerConnectionConfig, constraints);
        this.signalingState = this.pc.signalingState;

        this.pc.addEventListener("signalingstatechange", () => {
            if (this.signalingState === this.pc.signalingState) {
                return;
            }
            this.signalingState = this.pc.signalingState;
            // implements a simple queue of pending operations
            // like doing an ice restart or setting the bandwidth
            if (this.pc.signalingState === "stable") {
                this.isOperationPending = false;
                const action = this.pending.shift();
                if (action) {
                    action.apply();
                }
            }
        });
        return this.pc;
    }

    addStream(stream) {
        this.streamIds.push(stream.id);
        this.streams.push(stream);
        if (RTCPeerConnection.prototype.addTrack) {
            stream.getAudioTracks().forEach((track) => {
                this.pc.addTrack(track, stream);
            });
            stream.getVideoTracks().forEach((track) => {
                this.pc.addTrack(track, stream);
            });
        } else {
            // legacy addStream fallback.
            this.pc.addStream(stream);
        }
    }

    addTrack(track, stream) {
        if (!stream) {
            stream = this.streams[0];
        }
        stream.addTrack(track);
        this.pc.addTrack(track, stream);
    }

    removeTrack(track) {
        const stream = this.streams[0];
        stream.removeTrack(track);
        const sender = this.pc.getSenders().find((sender) => sender.track === track);
        if (sender) {
            this.pc.removeTrack(sender);
        }
    }

    removeStream(stream) {
        for (let i = 0; i < this.streamIds.length; i++) {
            if (this.streamIds[i] === stream.id) {
                this.streamIds.splice(i, 1);
                this.streams.splice(i, 1);
            }
        }

        if (this.pc) {
            if (this.pc.removeTrack) {
                stream.getTracks().forEach((track) => {
                    const sender = this.pc.getSenders().find((sender) => sender.track === track);
                    if (sender) {
                        this.pc.removeTrack(sender);
                    }
                });
            } else if (this.pc.removeStream) {
                this.pc.removeStream(stream);
            }
        }
    }

    _setRemoteDescription(desc) {
        // deprioritize H264 Encoding if set by option/flag
        if (this._deprioritizeH264Encoding) desc.sdp = deprioritizeH264(desc.sdp);

        // wrapper around SRD which stores a promise
        this.srdComplete = this.pc.setRemoteDescription(desc);
        return this.srdComplete.then(() => {
            this.earlyIceCandidates.forEach((candidate) => this.pc.addIceCandidate(candidate));
            this.earlyIceCandidates = [];
        });
    }

    handleOffer(message) {
        if (!this.canModifyPeerConnection()) {
            return new Promise((resolve) => {
                this.pending.push(() => this.handleOffer(message).then(resolve));
            });
        }
        this.isOperationPending = true;
        let sdp = message.sdp;

        sdp = filterMidExtension(sdp);
        sdp = filterMsidSemantic(sdp);

        const desc = { type: message.type, sdp };
        // Create an answer to send to the client that sent the offer
        let answerToSignal;

        return this._setRemoteDescription(desc)
            .then(() => {
                return this.pc.createAnswer();
            })
            .then((answer) => {
                answerToSignal = answer;
                return this.pc.setLocalDescription(answer);
            })
            .then(() => {
                return setVideoBandwidthUsingSetParameters(this.pc, this.bandwidth);
            })
            .then(() => {
                return answerToSignal;
            });
    }

    handleAnswer(message) {
        // workaround for https://bugzilla.mozilla.org/show_bug.cgi?id=1394602
        if (this.pendingOffer) {
            const pendingOffer = this.pendingOffer;
            delete this.pendingOffer;
            return this.pc.setLocalDescription(pendingOffer).then(() => this.handleAnswer(message));
        }
        let sdp = message.sdp;

        sdp = filterMsidSemantic(sdp);

        const desc = { type: message.type, sdp };
        return this._setRemoteDescription(desc).then(
            () => {
                return setVideoBandwidthUsingSetParameters(this.pc, this.bandwidth);
            },
            (e) => {
                this._logger.warn("Could not set remote description from remote answer: ", e);
            }
        );
    }

    addIceCandidate(candidate) {
        if (!this.srdComplete) {
            // In theory this is a protocol violation. However, our Javascript can signal an
            // answer after the first candidates.
            this.earlyIceCandidates.push(candidate);
            return;
        }
        this.srdComplete.then(() => {
            if (this.pc.signalingState === "closed") {
                return;
            }
            if (adapter.browserDetails.browser === "safari" && candidate && candidate.candidate === "") {
                // filter due to https://github.com/webrtcHacks/adapter/issues/863
                return;
            }
            this.pc.addIceCandidate(candidate).catch((e) => {
                this._logger.warn("Failed to add ICE candidate ('%s'): %s", candidate ? candidate.candidate : null, e);
            });
        });
    }

    canModifyPeerConnection() {
        return this.pc.signalingState === "stable" && !this.isOperationPending;
    }

    close() {
        const pc = this.pc;
        if (!pc) {
            return;
        }

        pc.oniceconnectionstatechange = null;
        pc.onicecandidate = null;
        pc.ontrack = null;
        try {
            // do not handle state change events when we close the connection explicitly
            pc.close();
        } catch (e) {
            console.warn("failures during close of session", e);
            // we're not interested in errors from RTCPeerConnection.close()
        }
    }

    hasConnectedPeerConnection() {
        return this.pc && this.pc.connectionState === "connected";
    }

    replaceTrack(oldTrack, newTrack) {
        const pc = this.pc;
        // This shouldn't really happen
        if (!pc) return false;
        const senders = pc.getSenders();
        function dbg(msg) {
            const tr = (t) => t && `id:${t.id},kind:${t.kind},state:${t.readyState}`;
            this._logger.warn(
                `${msg}. newTrack:${tr(newTrack)}, oldTrack:${tr(oldTrack)}, sender tracks: ${JSON.stringify(
                    senders.map((s) => `s ${tr(s.track)}`)
                )}, sender first codecs: ${JSON.stringify(senders.map((s) => (s.getParameters().codecs || [])[0]))}`
            );
        }
        if (!senders.length) {
            dbg("No senders!");
        }
        // If we didn't specify oldTrack, replace with first of its kind
        if (!oldTrack) {
            oldTrack = (senders.find((s) => s.track && s.track.kind === newTrack.kind) || {}).track;
            if (!oldTrack) {
                // odin: Temporary debug data, remove if you see after 2020-12-01
                dbg("No sender with same kind! Add new track then.");
            }
        }
        // Modern browsers makes things simple.
        if (window.RTCRtpSender && window.RTCRtpSender.prototype.replaceTrack) {
            if (oldTrack) {
                const process = () => {
                    for (let i = 0; i < senders.length; i++) {
                        const sender = senders[i];
                        const track = sender.track;
                        if (!sender && !track) {
                            // odin: Temporary debug data, remove if you see after 2020-12-01
                            dbg("One of the tracks is null!");
                        }
                        if (track.id === newTrack.id) {
                            return Promise.resolve(newTrack);
                        }
                        if (track.id === oldTrack.id) {
                            return senders[i].replaceTrack(newTrack);
                        }
                    }
                    return null;
                };
                let result = process();
                if (result) {
                    return result;
                }
                let resolve = null;
                let reject = null;
                result = new Promise((_resolve, _reject) => {
                    resolve = _resolve;
                    reject = _reject;
                });
                let retried = 0;
                let timer = setInterval(async () => {
                    const trackReplacedPromise = process();
                    if (!trackReplacedPromise) {
                        if (3 < ++retried) {
                            clearInterval(timer);
                            timer = null;
                            dbg("No sender track to replace");
                            reject("No sender track to replace");
                        }
                        return;
                    }
                    clearInterval(timer);
                    timer = null;
                    const trackReplaced = await trackReplacedPromise;
                    resolve(trackReplaced);
                }, 1000);
                // if we have an oldtrack, we should not go forward, because
                // we already know that the track has been added at least to the mediastream
                return result;
            }
            const stream = this.streams.find((s) => s.getTracks().find((t) => t.id === newTrack.id)) || this.streams[0];
            if (!stream) {
                dbg("No stream?");
                return Promise.reject(new Error("replaceTrack: No stream?"));
            }
            // Let's just add the track if we couldn't figure out a better way.
            // We'll get here if you had no camera and plugged one in.
            return pc.addTrack(newTrack, stream);
        }

        if (!this.canModifyPeerConnection()) {
            this.pending.push(() => {
                this.replaceTrack(oldTrack, newTrack);
            });
            return;
        }
        this.isOperationPending = true;
        const onn = pc.onnegotiationneeded;
        pc.onnegotiationneeded = null;
        this.removeTrack(oldTrack);
        this.addTrack(newTrack);
        setTimeout(() => {
            // negotiationneeded is fired async, restore it async.
            pc.onnegotiationneeded = onn;
        }, 0);

        if (pc.localDescription.type === "offer") {
            return pc
                .createOffer()
                .then((offer) => {
                    offer.sdp = replaceSSRCs(pc.localDescription.sdp, offer.sdp);
                    return pc.setLocalDescription(offer);
                })
                .then(() => {
                    return this._setRemoteDescription(pc.remoteDescription);
                });
        } else {
            return this._setRemoteDescription(pc.remoteDescription)
                .then(() => {
                    return pc.createAnswer();
                })
                .then((answer) => {
                    answer.sdp = replaceSSRCs(pc.localDescription.sdp, answer.sdp);
                    return pc.setLocalDescription(answer);
                });
        }
    }

    // Restricts the bandwidth based on whether we are using a relayed connection.
    // No signaling is required, this is done independently by both sides.
    // Only applies to unrestricted connections, not affecting the bandwidth tables
    // that depend on the number of participants.
    maybeRestrictRelayBandwidth() {
        if (this.maximumTurnBandwidth === MAXIMUM_TURN_BANDWIDTH_UNLIMITED) {
            return;
        }
        if (!this.pc.getStats) {
            return;
        }
        isRelayed(this.pc).then((isRelayed) => {
            if (isRelayed && this.bandwidth === 0) {
                this.changeBandwidth(this.maximumTurnBandwidth);
            }
        });
    }

    // no-signaling negotiation of bandwidth. Peer is NOT informed.
    // Prefers using RTCRtpSender.setParameters if possible.
    changeBandwidth(bandwidth) {
        // don't renegotiate if bandwidth is already set.
        if (bandwidth === this.bandwidth) {
            return;
        }

        if (!this.canModifyPeerConnection()) {
            this.pending.push(() => this.changeBandwidth(bandwidth));
            return;
        }

        this.bandwidth = bandwidth;
        if (!this.pc.localDescription || this.pc.localDescription.type === "") {
            return;
        }

        setVideoBandwidthUsingSetParameters(this.pc, this.bandwidth);
    }
}

// transforms a maplike to an object. Mostly for getStats +
// JSON.parse(JSON.stringify())
function map2obj(m) {
  if (!m.entries) {
    return m;
  }
  var o = {};
  m.forEach(function(v, k) {
    o[k] = v;
  });
  return o;
}

// apply a delta compression to the stats report. Reduces size by ~90%.
// To reduce further, report keys could be compressed.
function deltaCompression(oldStats, newStats) {
  newStats = JSON.parse(JSON.stringify(newStats));
  Object.keys(newStats).forEach(function(id) {
    var report = newStats[id];
    delete report.id;
    if (!oldStats[id]) {
      return;
    }
    Object.keys(report).forEach(function(name) {
      if (report[name] === oldStats[id][name]) {
        delete newStats[id][name];
      }
      if (Object.keys(report).length === 0) {
        delete newStats[id];
      } else if (Object.keys(report).length === 1 && report.timestamp) {
        delete newStats[id];
      }
    });
  });

  var timestamp = -Infinity;
  Object.keys(newStats).forEach(function(id) {
    var report = newStats[id];
    if (report.timestamp > timestamp) {
      timestamp = report.timestamp;
    }
  });
  Object.keys(newStats).forEach(function(id) {
    var report = newStats[id];
    if (report.timestamp === timestamp) {
      report.timestamp = 0;
    }
  });
  newStats.timestamp = timestamp;
  return newStats;
}

function dumpStream(stream) {
  return {
    id: stream.id,
    tracks: stream.getTracks().map(function(track) {
      return {
        id: track.id,                 // unique identifier (GUID) for the track
        kind: track.kind,             // `audio` or `video`
        label: track.label,           // identified the track source
        enabled: track.enabled,       // application can control it
        muted: track.muted,           // application cannot control it (read-only)
        readyState: track.readyState, // `live` or `ended`
      };
    }),
  };
}

/*
function filterBoringStats(results) {
  Object.keys(results).forEach(function(id) {
    switch (results[id].type) {
      case 'certificate':
      case 'codec':
        delete results[id];
        break;
      default:
        // noop
    }
  });
  return results;
}

function removeTimestamps(results) {
  // FIXME: does not work in FF since the timestamp can't be deleted.
  Object.keys(results).forEach(function(id) {
    delete results[id].timestamp;
  });
  return results;
}
*/

var rtcstats = function(trace, getStatsInterval, prefixesToWrap) {
  var peerconnectioncounter = 0;
  var isFirefox = !!window.mozRTCPeerConnection;
  var isEdge = !!window.RTCIceGatherer;
  prefixesToWrap.forEach(function(prefix) {
    if (!window[prefix + 'RTCPeerConnection']) {
      return;
    }
    if (prefix === 'webkit' && isEdge) {
      // dont wrap webkitRTCPeerconnection in Edge.
      return;
    }
    var origPeerConnection = window[prefix + 'RTCPeerConnection'];
    var peerconnection = function(config, constraints) {
      var pc = new origPeerConnection(config, constraints);
      var id = 'PC_' + peerconnectioncounter++;
      pc.__rtcStatsId = id;

      if (!config) {
        config = { nullConfig: true };
      }

      config = JSON.parse(JSON.stringify(config)); // deepcopy
      // don't log credentials
      ((config && config.iceServers) || []).forEach(function(server) {
        delete server.credential;
      });

      if (isFirefox) {
        config.browserType = 'moz';
      } else if (isEdge) {
        config.browserType = 'edge';
      } else {
        config.browserType = 'webkit';
      }

      trace('create', id, config);
      // TODO: do we want to log constraints here? They are chrome-proprietary.
      // http://stackoverflow.com/questions/31003928/what-do-each-of-these-experimental-goog-rtcpeerconnectionconstraints-do
      if (constraints) {
        trace('constraints', id, constraints);
      }

      pc.addEventListener('icecandidate', function(e) {
        trace('onicecandidate', id, e.candidate);
      });
      pc.addEventListener('addstream', function(e) {
        trace('onaddstream', id, e.stream.id + ' ' + e.stream.getTracks().map(function(t) { return t.kind + ':' + t.id; }));
      });
      pc.addEventListener('track', function(e) {
        trace('ontrack', id, e.track.kind + ':' + e.track.id + ' ' + e.streams.map(function(stream) { return 'stream:' + stream.id; }));
      });
      pc.addEventListener('removestream', function(e) {
        trace('onremovestream', id, e.stream.id + ' ' + e.stream.getTracks().map(function(t) { return t.kind + ':' + t.id; }));
      });
      pc.addEventListener('signalingstatechange', function() {
        trace('onsignalingstatechange', id, pc.signalingState);
      });
      pc.addEventListener('iceconnectionstatechange', function() {
        trace('oniceconnectionstatechange', id, pc.iceConnectionState);
      });
      pc.addEventListener('icegatheringstatechange', function() {
        trace('onicegatheringstatechange', id, pc.iceGatheringState);
      });
      pc.addEventListener('connectionstatechange', function() {
        trace('onconnectionstatechange', id, pc.connectionState);
      });
      pc.addEventListener('negotiationneeded', function() {
        trace('onnegotiationneeded', id, undefined);
      });
      pc.addEventListener('datachannel', function(event) {
        trace('ondatachannel', id, [event.channel.id, event.channel.label]);
      });

      var prev = {};
      var getStats = function() {
        pc.getStats(null).then(function(res) {
          var now = map2obj(res);
          var base = JSON.parse(JSON.stringify(now)); // our new prev
          trace('getstats', id, deltaCompression(prev, now));
          prev = base;
        });
      };
      // TODO: do we want one big interval and all peerconnections
      //    queried in that or one setInterval per PC?
      //    we have to collect results anyway so...
      if (!isEdge && getStatsInterval) {
        var interval = window.setInterval(function() {
          if (pc.signalingState === 'closed') {
            window.clearInterval(interval);
            return;
          }
          getStats();
        }, getStatsInterval);
      }
      if (!isEdge) {
        pc.addEventListener('connectionstatechange', function() {
          if (pc.connectionState === 'connected' || pc.connectionState === 'failed') {
            getStats();
          }
        });
      }
      return pc;
    };

    ['createDataChannel', 'close'].forEach(function(method) {
      var nativeMethod = origPeerConnection.prototype[method];
      if (nativeMethod) {
        origPeerConnection.prototype[method] = function() {
          trace(method, this.__rtcStatsId, arguments);
          return nativeMethod.apply(this, arguments);
        };
      }
    });

    ['addStream', 'removeStream'].forEach(function(method) {
      var nativeMethod = origPeerConnection.prototype[method];
      if (nativeMethod) {
        origPeerConnection.prototype[method] = function() {
          var stream = arguments[0];
          var streamInfo = stream.getTracks().map(function(t) {
            return t.kind + ':' + t.id;
          }).join(',');

          trace(method, this.__rtcStatsId, stream.id + ' ' + streamInfo);
          return nativeMethod.apply(this, arguments);
        };
      }
    });

    ['addTrack'].forEach(function(method) {
      var nativeMethod = origPeerConnection.prototype[method];
      if (nativeMethod) {
        origPeerConnection.prototype[method] = function() {
          var track = arguments[0];
          var streams = [].slice.call(arguments, 1);
          trace(method, this.__rtcStatsId, track.kind + ':' + track.id + ' ' + (streams.map(function(s) { return 'stream:' + s.id; }).join(';') || '-'));
          return nativeMethod.apply(this, arguments);
        };
      }
    });

    ['removeTrack'].forEach(function(method) {
      var nativeMethod = origPeerConnection.prototype[method];
      if (nativeMethod) {
        origPeerConnection.prototype[method] = function() {
          var track = arguments[0].track;
          trace(method, this.__rtcStatsId, track ? track.kind + ':' + track.id : 'null');
          return nativeMethod.apply(this, arguments);
        };
      }
    });

    ['createOffer', 'createAnswer'].forEach(function(method) {
      var nativeMethod = origPeerConnection.prototype[method];
      if (nativeMethod) {
        origPeerConnection.prototype[method] = function() {
          var rtcStatsId = this.__rtcStatsId;
          var args = arguments;
          var opts;
          if (arguments.length === 1 && typeof arguments[0] === 'object') {
            opts = arguments[0];
          } else if (arguments.length === 3 && typeof arguments[2] === 'object') {
            opts = arguments[2];
          }
          trace(method, this.__rtcStatsId, opts);
          return nativeMethod.apply(this, opts ? [opts] : undefined)
          .then(function(description) {
            trace(method + 'OnSuccess', rtcStatsId, description);
            if (args.length > 0 && typeof args[0] === 'function') {
              args[0].apply(null, [description]);
              return undefined;
            }
            return description;
          }, function(err) {
            trace(method + 'OnFailure', rtcStatsId, err.toString());
            if (args.length > 1 && typeof args[1] === 'function') {
              args[1].apply(null, [err]);
              return;
            }
            throw err;
          });
        };
      }
    });

    ['setLocalDescription', 'setRemoteDescription', 'addIceCandidate'].forEach(function(method) {
      var nativeMethod = origPeerConnection.prototype[method];
      if (nativeMethod) {
        origPeerConnection.prototype[method] = function() {
          var rtcStatsId = this.__rtcStatsId;
          var args = arguments;
          trace(method, this.__rtcStatsId, args[0]);
          return nativeMethod.apply(this, [args[0]])
          .then(function() {
            trace(method + 'OnSuccess', rtcStatsId, undefined);
            if (args.length >= 2 && typeof args[1] === 'function') {
              args[1].apply(null, []);
              return undefined;
            }
            return undefined;
          }, function(err) {
            trace(method + 'OnFailure', rtcStatsId, err.toString());
            if (args.length >= 3 && typeof args[2] === 'function') {
              args[2].apply(null, [err]);
              return undefined;
            }
            throw err;
          });
        };
      }
    });

    // wrap static methods. Currently just generateCertificate.
    if (origPeerConnection.generateCertificate) {
      Object.defineProperty(peerconnection, 'generateCertificate', {
        get: function() {
          return arguments.length ?
              origPeerConnection.generateCertificate.apply(null, arguments)
              : origPeerConnection.generateCertificate;
        },
      });
    }
    window[prefix + 'RTCPeerConnection'] = peerconnection;
    window[prefix + 'RTCPeerConnection'].prototype = origPeerConnection.prototype;
  });

  // getUserMedia wrappers
  prefixesToWrap.forEach(function(prefix) {
    var name = prefix + (prefix.length ? 'GetUserMedia' : 'getUserMedia');
    if (!navigator[name]) {
      return;
    }
    var origGetUserMedia = navigator[name].bind(navigator);
    var gum = function() {
      trace('getUserMedia', null, arguments[0]);
      var cb = arguments[1];
      var eb = arguments[2];
      origGetUserMedia(arguments[0],
        function(stream) {
          // we log the stream id, track ids and tracks readystate since that is ended GUM fails
          // to acquire the cam (in chrome)
          trace('getUserMediaOnSuccess', null, dumpStream(stream));
          if (cb) {
            cb(stream);
          }
        },
        function(err) {
          trace('getUserMediaOnFailure', null, err.name);
          if (eb) {
            eb(err);
          }
        }
      );
    };
    navigator[name] = gum.bind(navigator);
  });

  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    var origGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
    var gum = function() {
      trace('navigator.mediaDevices.getUserMedia', null, arguments[0]);
      return origGetUserMedia.apply(navigator.mediaDevices, arguments)
      .then(function(stream) {
        trace('navigator.mediaDevices.getUserMediaOnSuccess', null, dumpStream(stream));
        return stream;
      }, function(err) {
        trace('navigator.mediaDevices.getUserMediaOnFailure', null, err.name);
        return Promise.reject(err);
      });
    };
    navigator.mediaDevices.getUserMedia = gum.bind(navigator.mediaDevices);
  }

  // getDisplayMedia
  if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
    var origGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
    var gdm = function() {
      trace('navigator.mediaDevices.getDisplayMedia', null, arguments[0]);
      return origGetDisplayMedia.apply(navigator.mediaDevices, arguments)
      .then(function(stream) {
        trace('navigator.mediaDevices.getDisplayMediaOnSuccess', null, dumpStream(stream));
        return stream;
      }, function(err) {
        trace('navigator.mediaDevices.getDisplayMediaOnFailure', null, err.name);
        return Promise.reject(err);
      });
    };
    navigator.mediaDevices.getDisplayMedia = gdm.bind(navigator.mediaDevices);
  }

  // TODO: are there events defined on MST that would allow us to listen when enabled was set?
  //    no :-(
  /*
  Object.defineProperty(MediaStreamTrack.prototype, 'enabled', {
    set: function(value) {
      trace('MediaStreamTrackEnable', this, value);
    }
  });
  */
};

var rtcstats$1 = /*@__PURE__*/getDefaultExportFromCjs(rtcstats);

// ensure adapter is loaded first.

const RTCSTATS_PROTOCOL_VERSION = "1.0";

const clientInfo = {
    id: v4(), // shared id across rtcstats reconnects
    connectionNumber: 0,
};

// Inlined version of rtcstats/trace-ws with improved disconnect handling.
function rtcStatsConnection(wsURL, logger = console) {
    const buffer = [];
    let ws;
    let organizationId;
    let clientId;
    let displayName;
    let userRole;
    let roomSessionId;
    let connectionShouldBeOpen;
    let connectionAttempt = 0;
    let hasPassedOnRoomSessionId = false;

    const connection = {
        connected: false,
        trace: (...args) => {
            args.push(Date.now());

            if (args[0] === "customEvent" && args[2].type === "roomSessionId") {
                const oldRoomSessionIdValue = roomSessionId && roomSessionId[2].value.roomSessionId;
                const newRoomSessionIdValue = args[2].value.roomSessionId;
                roomSessionId = args;

                if (
                    hasPassedOnRoomSessionId &&
                    newRoomSessionIdValue &&
                    newRoomSessionIdValue !== oldRoomSessionIdValue
                ) {
                    // roomSessionId was already sent. It may have been reset to null, but anything after should be part of the same
                    // session. Now it is something else, and we force a reconnect to start a new session
                    if (ws) {
                        ws.close();
                        return;
                    }
                }
                if (newRoomSessionIdValue) hasPassedOnRoomSessionId = true;
            } else if (args[0] === "customEvent" && args[2].type === "clientId") {
                clientId = args;
            } else if (args[0] === "customEvent" && args[2].type === "organizationId") {
                organizationId = args;
            } else if (args[0] === "customEvent" && args[2].type === "displayName") {
                displayName = args;
            } else if (args[0] === "customEvent" && args[2].type === "userRole") {
                userRole = args;
            }

            if (ws.readyState === WebSocket.OPEN) {
                connectionAttempt = 0;
                ws.send(JSON.stringify(args));
            } else if (args[0] !== "getstats" && !(args[0] === "customEvent" && args[2].type === "insightsStats")) {
                // buffer all but stats when not connected
                buffer.push(args);
            }

            // reconnect when closed by anything else than client
            if (ws.readyState === WebSocket.CLOSED && connectionShouldBeOpen) {
                setTimeout(() => {
                    if (ws.readyState === WebSocket.CLOSED && connectionShouldBeOpen) {
                        connection.connect();
                    }
                }, 1000 * connectionAttempt);
            }
        },
        close: () => {
            connectionShouldBeOpen = false;
            if (ws) {
                ws.close();
            }
        },
        connect: () => {
            connectionShouldBeOpen = true;
            connectionAttempt += 1;
            if (ws) {
                ws.close();
            }
            connection.connected = true;
            ws = new WebSocket(wsURL + window.location.pathname, RTCSTATS_PROTOCOL_VERSION);

            ws.onerror = (e) => {
                connection.connected = false;
                logger.warn(`[RTCSTATS] WebSocket error`, e);
            };
            ws.onclose = (e) => {
                connection.connected = false;
                logger.info(`[RTCSTATS] Closed ${e.code}`);
            };
            ws.onopen = () => {
                // send client info after each connection, so analysis tools can handle reconnections
                clientInfo.connectionNumber++;
                ws.send(JSON.stringify(["clientInfo", null, clientInfo]));

                if (organizationId) {
                    ws.send(JSON.stringify(organizationId));
                }

                if (clientId) {
                    ws.send(JSON.stringify(clientId));
                }

                if (roomSessionId) {
                    ws.send(JSON.stringify(roomSessionId));
                }
                if (displayName) {
                    ws.send(JSON.stringify(displayName));
                }
                if (userRole) {
                    ws.send(JSON.stringify(userRole));
                }

                // send buffered events (non-getStats)
                while (buffer.length) {
                    ws.send(JSON.stringify(buffer.shift()));
                }
            };
        },
    };
    connection.connect();
    return connection;
}

const server = rtcStatsConnection("wss://rtcstats.srv.whereby.com" );
rtcstats$1(
    server.trace,
    10000, // query once every 10 seconds.
    [""] // only shim unprefixed RTCPeerConnecion.
);

const rtcStats = {
    sendEvent: (type, value) => {
        server.trace("customEvent", null, {
            type,
            value,
        });
    },
    sendAudioMuted: (muted) => {
        rtcStats.sendEvent("audio_muted", { muted });
    },
    sendVideoMuted: (muted) => {
        rtcStats.sendEvent("video_muted", { muted });
    },
    server,
};

const CAMERA_STREAM_ID$1 = RtcStream.getCameraId();
const browserName$2 = adapter.browserDetails.browser;
const browserVersion = adapter.browserDetails.version;

if (browserName$2 === "firefox") {
    adapter.browserShim.shimGetDisplayMedia(window, "screen");
}

let unloading$1 = false;
if (browserName$2 === "chrome") {
    window.document.addEventListener("beforeunload", () => {
        unloading$1 = true;
    });
}

class BaseRtcManager {
    constructor({ selfId, room, emitter, serverSocket, webrtcProvider, features, logger = console }) {
        assert$1.ok(selfId, "selfId is required");
        assert$1.ok(room, "room is required");
        assert$1.ok(emitter && emitter.emit, "emitter is required");
        assert$1.ok(serverSocket instanceof ServerSocket, "serverSocket is required");
        assert$1.ok(webrtcProvider, "webrtcProvider is required");

        const { name, session, iceServers, sfuServer, mediaserverConfigTtlSeconds } = room;

        this._selfId = selfId;
        this._roomName = name;
        this._roomSessionId = session && session.id;
        this.peerConnections = {};
        this.localStreams = {};
        this.enabledLocalStreamIds = [];
        this._socketListenerDeregisterFunctions = [];
        this._localStreamDeregisterFunction = null;
        this._emitter = emitter;
        this._serverSocket = serverSocket;
        this._webrtcProvider = webrtcProvider;
        this._features = features || {};
        this._logger = logger;

        this.offerOptions = { offerToReceiveAudio: true, offerToReceiveVideo: true };
        this._pendingActionsForConnectedPeerConnections = [];

        this._audioTrackOnEnded = () => {
            // There are a couple of reasons the microphone could stop working.
            // One of them is getting unplugged. The other is the Chrome audio
            // process crashing. The third is the tab being closed.
            // https://bugs.chromium.org/p/chromium/issues/detail?id=1050008
            rtcStats.sendEvent("audio_ended", { unloading: unloading$1 });
            this._emit(rtcManagerEvents.MICROPHONE_STOPPED_WORKING, {});
        };

        this._updateAndScheduleMediaServersRefresh({
            sfuServer,
            iceServers: iceServers.iceServers || [],
            mediaserverConfigTtlSeconds,
        });
    }

    numberOfPeerconnections() {
        return Object.keys(this.peerConnections).length;
    }

    numberOfRemotePeers() {
        return Object.values(this.peerConnections).filter((session) => session.clientId !== this._selfId).length;
    }

    _setConnectionStatus(session, newStatus, clientId) {
        const previousStatus = session.connectionStatus;
        if (previousStatus === newStatus) {
            return;
        }

        // prevent showing 'connecting' in the SFU case.
        if (session.peerConnectionId === this._selfId) {
            return;
        }

        // Never go from disconnected -> checking
        if (
            previousStatus === TYPES.CONNECTION_DISCONNECTED &&
            newStatus === TYPES.CONNECTING
        ) {
            return;
        }
        session.connectionStatus = newStatus;

        setTimeout(() => {
            this._emit(EVENTS.CLIENT_CONNECTION_STATUS_CHANGED, {
                streamIds: session.streamIds,
                clientId,
                status: newStatus,
                previous: previousStatus,
            });
        }, 0);
    }

    _setJitterBufferTarget(pc) {
        try {
            const receivers = pc.getReceivers();

            receivers.forEach((receiver) => {
                receiver.jitterBufferTarget = MEDIA_JITTER_BUFFER_TARGET;
                // Legacy Chrome API
                receiver.playoutDelayHint = MEDIA_JITTER_BUFFER_TARGET / 1000; // seconds
            });
        } catch (error) {
            this._logger.error("Error during setting jitter buffer target:", error);
        }
    }

    _emitServerEvent(eventName, data, callback) {
        this._serverSocket.emit(eventName, data, callback);
    }

    _emit(eventName, data) {
        this._emitter.emit(eventName, data);
    }

    isInitializedWith({ selfId, roomName, isSfu }) {
        return this._selfId === selfId && this._roomName === roomName && isSfu === !!this._sfuServer;
    }

    supportsScreenShareAudio() {
        return true;
    }

    _addEnabledLocalStreamId(streamId) {
        this.enabledLocalStreamIds.push(streamId);
    }

    _deleteEnabledLocalStreamId(streamId) {
        const index = this.enabledLocalStreamIds.indexOf(streamId);
        if (index !== -1) {
            this.enabledLocalStreamIds.splice(index, 1);
        }
    }

    _getSession(peerConnectionId) {
        if (!(peerConnectionId in this.peerConnections)) {
            return null;
        }
        return this.peerConnections[peerConnectionId];
    }

    _getOrCreateSession(peerConnectionId, initialBandwidth) {
        let session = this.peerConnections[peerConnectionId];

        if (session === undefined) {
            // Some macs + ios devices have troubles using h264 encoder since safari 14
            // this will make them encode VP8 instead if available
            const deprioritizeH264Encoding =
                browserName$2 === "safari" && browserVersion >= 14 && this._features.deprioritizeH264OnSafari;

            this.peerConnections[peerConnectionId] = session = new Session({
                peerConnectionId,
                bandwidth: initialBandwidth,
                maximumTurnBandwidth: this._features.unlimitedBandwidthWhenUsingRelayP2POn
                    ? MAXIMUM_TURN_BANDWIDTH_UNLIMITED
                    : MAXIMUM_TURN_BANDWIDTH,
                deprioritizeH264Encoding,
            });
        }
        return session;
    }

    _getLocalCameraStream() {
        return this.localStreams[CAMERA_STREAM_ID$1];
    }

    _getNonLocalCameraStreamIds() {
        return Object.keys(this.localStreams).filter((streamId) => streamId !== CAMERA_STREAM_ID$1);
    }

    _isScreensharingLocally() {
        return this._getNonLocalCameraStreamIds().length > 0;
    }

    _getFirstLocalNonCameraStream() {
        const streamIds = this._getNonLocalCameraStreamIds();
        return streamIds.length === 0 ? null : this.localStreams[streamIds[0]];
    }

    _transformIncomingSdp(original) {
        return { type: original.type, sdp: original.sdpU };
    }

    _transformOutgoingSdp(original) {
        return { type: original.type, sdpU: original.sdp };
    }

    _createSession({ clientId, initialBandwidth, isOfferer, peerConnectionId, shouldAddLocalVideo }) {
        if (!peerConnectionId) {
            throw new Error("peerConnectionId is missing");
        }
        if (!clientId) {
            throw new Error("clientId is missing");
        }
        const session = this._getOrCreateSession(peerConnectionId, initialBandwidth);
        const constraints = { optional: [] };
        if (browserName$2 === "chrome") {
            constraints.optional.push({
                googCpuOveruseDetection: true,
            });
        }

        // rtcstats integration
        constraints.optional.push({ rtcStatsRoomSessionId: this._roomSessionId });
        constraints.optional.push({ rtcStatsClientId: this._selfId });
        constraints.optional.push({ rtcStatsPeerId: peerConnectionId });
        constraints.optional.push({ rtcStatsConferenceId: this._roomName });

        const peerConnectionConfig = {
            iceServers: this._iceServers,
        };
        if (this._features.turnServerOverrideHost) {
            const host = this._features.turnServerOverrideHost;
            const port = host.indexOf(":") > 0 ? "" : ":443";
            const override = ":" + host + port;
            peerConnectionConfig.iceServers = peerConnectionConfig.iceServers.map((original) => {
                const entry = Object.assign({}, original);
                if (entry.url) {
                    entry.url = entry.url.replace(/:[^?]*/, override);
                }
                if (entry.urls) {
                    entry.urls = entry.urls.map((url) => url.replace(/:[^?]*/, override));
                }
                return entry;
            });
        }
        if (this._features.useOnlyTURN) {
            peerConnectionConfig.iceTransportPolicy = "relay";
            const filter = {
                onlyudp: /^turn:.*transport=udp$/,
                onlytcp: /^turn:.*transport=tcp$/,
                onlytls: /^turns:.*transport=tcp$/,
            }[this._features.useOnlyTURN];
            if (filter) {
                peerConnectionConfig.iceServers = peerConnectionConfig.iceServers.filter(
                    (entry) => entry.url && entry.url.match(filter)
                );
            }
        }

        if (browserName$2 === "chrome") {
            peerConnectionConfig.sdpSemantics = "unified-plan";
        }

        const pc = session.setAndGetPeerConnection({
            constraints,
            peerConnectionConfig,
            shouldAddLocalVideo,
            clientId,
        });

        pc.ontrack = (event) => {
            const stream = event.streams[0];
            if (stream.id === "default" && stream.getAudioTracks().length === 0) {
                // due to our PlanB / UnifiedPlan conversion we can run into this:
                // https://bugs.chromium.org/p/webrtc/issues/detail?id=8228
                // and ignore it.
                return;
            }
            // ontrack fires for each track of a stream. Emulate old onaddstream behaviour.
            if (session.streamIds.indexOf(stream.id) === -1) {
                session.streamIds.push(stream.id);
                this._emit(EVENTS.STREAM_ADDED, {
                    clientId,
                    stream,
                });

                // when adding a new stream to an already established connection
                // we need to tell the GUI about it.
                if (session.connectionStatus === TYPES.CONNECTION_SUCCESSFUL) {
                    setTimeout(() => {
                        this._emit(EVENTS.CLIENT_CONNECTION_STATUS_CHANGED, {
                            streamIds: session.streamIds,
                            clientId,
                            status: session.connectionStatus,
                            previous: TYPES.CONNECTING,
                        });
                    }, 0);
                }
            }
        };

        pc.oniceconnectionstatechange = () => {
            let newStatus;
            const currentStatus = session.connectionStatus;
            switch (pc.iceConnectionState) {
                case "checking":
                    newStatus = TYPES.CONNECTING;
                    break;
                case "connected":
                case "completed":
                    newStatus = TYPES.CONNECTION_SUCCESSFUL;
                    if (!session.wasEverConnected) {
                        this._pendingActionsForConnectedPeerConnections.forEach((action) => {
                            if (typeof action === "function") {
                                action();
                            }
                        });
                        this._pendingActionsForConnectedPeerConnections = [];
                    }
                    // working around the fact that chrome does not go to completed for the
                    // ice-controlled answerer
                    if (
                        !session.wasEverConnected &&
                        (pc.iceConnectionState.match(/connected|completed/) ||
                            (browserName$2 === "chrome" && pc.localDescription && pc.localDescription.type === "answer"))
                    ) {
                        session.wasEverConnected = true;
                        if (this._features.bandwidth !== "false") {
                            this.maybeRestrictRelayBandwidth(session);
                        }
                    }
                    session.registerConnected();
                    break;
                case "disconnected":
                    newStatus = TYPES.CONNECTION_DISCONNECTED;
                    setTimeout(() => {
                        if (pc.iceConnectionState === "disconnected") {
                            this._maybeRestartIce(clientId, session);
                        }
                    }, 2000);
                    break;
                case "failed":
                    newStatus = TYPES.CONNECTION_FAILED;
                    if (currentStatus !== newStatus) {
                        this._maybeRestartIce(clientId, session);
                    }
                    if (session.relayCandidateSeen === null && !session.wasEverConnected) {
                        // this means we did not gather any relay candidates.
                        // which shows that we are on a restricted network and it is an
                        // issue on the local end.
                        // Should not trigger if we  were ever connected.
                        this._emit(rtcManagerEvents.CONNECTION_BLOCKED_BY_NETWORK);
                    }
                    break;
                default:
                    // other states are not interesting
                    return;
            }
            this._setConnectionStatus(session, newStatus, clientId);
        };
        pc.onconnectionstate = () => {
            // since Chrome insists on not going to failed for unified-plan
            // (/new-iceconnectionstate)... listen for connectionState.
            // See also
            //   https://bugs.chromium.org/p/chromium/issues/detail?id=933786
            //   https://bugs.chromium.org/p/chromium/issues/detail?id=982793
            switch (pc.connectionState) {
                case "connected":
                    // try to detect audio problems.
                    // this waits 3 seconds after the connection is up
                    // to be sure the DTLS handshake is done even in Firefox.
                    setTimeout(() => {
                        detectMicrophoneNotWorking(session.pc).then((failureDirection) => {
                            if (failureDirection !== false) {
                                this._emit(rtcManagerEvents.MICROPHONE_NOT_WORKING, {
                                    failureDirection,
                                    clientId,
                                });
                            }
                        });
                    }, 3000);
                    session.registerConnected();
                    break;
                case "failed":
                    const newStatus = TYPES.CONNECTION_FAILED;
                    if (session.relayCandidateSeen === null && !session.wasEverConnected) {
                        // this means we did not gather any relay candidates.
                        // which shows that we are on a restricted network and it is an
                        // issue on the local end.
                        // Should not trigger if we  were ever connected.
                        this._emit(rtcManagerEvents.CONNECTION_BLOCKED_BY_NETWORK);
                    }
                    this._setConnectionStatus(session, newStatus, clientId);
                    break;
            }
        };

        const localCameraStream = this.localStreams[CAMERA_STREAM_ID$1];
        if (shouldAddLocalVideo && localCameraStream) {
            session.addStream(localCameraStream);
        }

        // Don't add existing screenshare-streams when using SFU as those will be
        // added in a separate session/peerConnection by SfuV2RtcManager
        if (shouldAddLocalVideo) {
            Object.keys(this.localStreams).forEach((id) => {
                if (id === CAMERA_STREAM_ID$1) {
                    return;
                }
                const screenshareStream = this.localStreams[id];
                // if we are offering it's safe to add screensharing streams in initial offer
                if (isOfferer) {
                    session.addStream(screenshareStream);
                } else {
                    // if not we are here because of reconnecting, and will need to start screenshare
                    // after connection is ready
                    session.afterConnected.then(() => {
                        this._emitServerEvent(PROTOCOL_REQUESTS.START_SCREENSHARE, {
                            receiverId: session.clientId,
                            streamId: screenshareStream.id,
                            hasAudioTrack: !!screenshareStream.getAudioTracks().length,
                        });
                        this._withForcedRenegotiation(session, () => session.addStream(screenshareStream));
                    });
                }
            });
        }

        return session;
    }

    _cleanup(peerConnectionId) {
        const session = this._getSession(peerConnectionId);
        if (!session) {
            this._logger.warn("No RTCPeerConnection in RTCManager.disconnect()", peerConnectionId);
            return;
        }
        session.close();
        delete this.peerConnections[peerConnectionId];
    }

    _forEachPeerConnection(func) {
        Object.keys(this.peerConnections).forEach((peerConnectionId) => {
            const peerConnection = this.peerConnections[peerConnectionId];
            func(peerConnection);
        });
    }

    _addStreamToPeerConnections(stream) {
        this._forEachPeerConnection((session) => {
            this._withForcedRenegotiation(session, () => session.addStream(stream));
        });
    }

    _addTrackToPeerConnections(track, stream) {
        this._forEachPeerConnection((session) => {
            this._withForcedRenegotiation(session, () => session.addTrack(track, stream));
        });
    }

    _replaceTrackToPeerConnections(oldTrack, newTrack) {
        const promises = [];
        this._forEachPeerConnection((session) => {
            if (!session.hasConnectedPeerConnection()) {
                this._logger.log("Session doesn't have a connected PeerConnection, adding pending action!");
                const pendingActions = this._pendingActionsForConnectedPeerConnections;
                if (!pendingActions) {
                    this._logger.warn(
                        `No pending action is created to repalce track, because the pending actions array is null`
                    );
                    return;
                }
                const promise = new Promise((resolve, reject) => {
                    const action = () => {
                        const replacedTrackPromise = session.replaceTrack(oldTrack, newTrack);
                        if (!replacedTrackPromise) {
                            this._logger.error("replaceTrack returned false!");
                            reject(`ReplaceTrack returned false`);
                            return;
                        }
                        replacedTrackPromise.then((track) => resolve(track)).catch((error) => reject(error));
                    };
                    pendingActions.push(action);
                });
                promises.push(promise);
                return;
            }
            const replacedTrackResult = session.replaceTrack(oldTrack, newTrack);
            if (!replacedTrackResult) {
                this._logger.error("replaceTrack returned false!");
                return;
            }
            promises.push(replacedTrackResult);
        });
        return Promise.all(promises);
    }

    _removeStreamFromPeerConnections(stream) {
        this._forEachPeerConnection((session) => {
            this._withForcedRenegotiation(session, () => session.removeStream(stream));
        });
    }

    _removeTrackFromPeerConnections(track) {
        this._forEachPeerConnection((session) => {
            this._withForcedRenegotiation(session, () => session.removeTrack(track));
        });
    }

    _addLocalStream(streamId, stream) {
        this._addEnabledLocalStreamId(streamId);
        this.localStreams[streamId] = stream;
    }

    _removeLocalStream(streamId) {
        delete this.localStreams[streamId];
        this._deleteEnabledLocalStreamId(streamId);
    }

    maybeRestrictRelayBandwidth(session) {
        session.maybeRestrictRelayBandwidth();
    }

    addNewStream(streamId, stream) {
        if (stream === this.localStreams[streamId]) {
            // this can happen after reconnect. We do not want to add the stream to the
            // peerconnection again.
            return;
        }

        this._addLocalStream(streamId, stream);

        if (streamId === CAMERA_STREAM_ID$1) {
            this._addStreamToPeerConnections(stream);
            const [audioTrack] = stream.getAudioTracks();
            if (audioTrack) {
                this._startMonitoringAudioTrack(audioTrack);
            }

            // This should not be needed, but checking nonetheless
            if (this._localStreamDeregisterFunction) {
                this._localStreamDeregisterFunction();
                this._localStreamDeregisterFunction = null;
            }

            const localStreamHandler = (e) => {
                const { enable, track } = e.detail;
                this._handleStopOrResumeVideo({ enable, track });
            };

            stream.addEventListener("stopresumevideo", localStreamHandler);
            this._localStreamDeregisterFunction = () => {
                stream.removeEventListener("stopresumevideo", localStreamHandler);
            };

            return;
        }

        // at this point it is clearly a screensharing stream.
        this._shareScreen(streamId, stream);
        return;
    }

    removeStream(streamId, stream) {
        // essentially we only ever remove screensharing streams.
        // SFU and P2P provide their own ways of doing this.
        this._removeLocalStream(streamId, stream);
    }

    replaceTrack(oldTrack, newTrack) {
        if (oldTrack && oldTrack.kind === "audio") {
            this._stopMonitoringAudioTrack(oldTrack);
        }
        if (newTrack && newTrack.kind === "audio") {
            this._startMonitoringAudioTrack(newTrack);
        }
        return this._replaceTrackToPeerConnections(oldTrack, newTrack);
    }

    accept({ clientId, shouldAddLocalVideo }) {
        return this.acceptNewStream({ streamId: clientId, clientId, shouldAddLocalVideo });
    }

    disconnectAll() {
        Object.keys(this.peerConnections).forEach((peerConnectionId) => {
            this.disconnect(peerConnectionId);
        });
        this.peerConnections = {};
        this._socketListenerDeregisterFunctions.forEach((func) => {
            func();
        });
        this._socketListenerDeregisterFunctions = [];

        if (this._localStreamDeregisterFunction) {
            this._localStreamDeregisterFunction();
            this._localStreamDeregisterFunction = null;
        }
    }

    // the user has muted/unmuted the audio track on the local stream.
    stopOrResumeAudio(/*localStream, enable*/) {}

    // the user has muted/unmuted the video track on the local stream.
    stopOrResumeVideo(/*localStream, enable*/) {}

    // handle the rtc side-effects of ^
    _handleStopOrResumeVideo(/* { localStream, enable, track } */) {}

    /* the Chrome audio process crashed (probably?)
     * try to fix it. Constraints are the audio device constraints
     * used in getUserMedia.
     */
    fixChromeAudio(constraints) {
        if (browserName$2 !== "chrome") {
            return;
        }
        const localStream = this._getLocalCameraStream();
        const audioTrack = localStream.getAudioTracks()[0];
        if (!audioTrack || audioTrack.readyState !== "ended") {
            return;
        }
        return navigator.mediaDevices.getUserMedia({ audio: constraints }).then((stream) => {
            const track = stream.getAudioTracks()[0];
            track.enabled = audioTrack.enabled; // retain mute state and don't accidentally unmute.
            localStream.removeTrack(audioTrack); // remove the old track.
            localStream.addTrack(track); // add the new track.
            return this.replaceTrack(audioTrack, track);
        });
    }

    _updateAndScheduleMediaServersRefresh({ iceServers, sfuServer, mediaserverConfigTtlSeconds }) {
        this._iceServers = iceServers;
        this._sfuServer = sfuServer;
        this._mediaserverConfigTtlSeconds = mediaserverConfigTtlSeconds;

        this._clearMediaServersRefresh();
        if (!mediaserverConfigTtlSeconds) {
            return;
        }
        this._fetchMediaServersTimer = setTimeout(
            () => this._emitServerEvent(PROTOCOL_REQUESTS.FETCH_MEDIASERVER_CONFIG),
            mediaserverConfigTtlSeconds * 1000
        );
    }

    _clearMediaServersRefresh() {
        if (!this._fetchMediaServersTimer) return;
        clearTimeout(this._fetchMediaServersTimer);
        this._fetchMediaServersTimer = null;
    }

    setupSocketListeners() {
        this._socketListenerDeregisterFunctions = [
            () => this._clearMediaServersRefresh(),

            this._serverSocket.on(PROTOCOL_RESPONSES.MEDIASERVER_CONFIG, (data) => {
                if (data.error) {
                    this._logger.warn("FETCH_MEDIASERVER_CONFIG failed:", data.error);
                    return;
                }
                this._updateAndScheduleMediaServersRefresh(data);
            }),

            this._serverSocket.on(RELAY_MESSAGES.READY_TO_RECEIVE_OFFER, (data) => {
                this._connect(data.clientId);
            }),

            this._serverSocket.on(RELAY_MESSAGES.ICE_CANDIDATE, (data) => {
                const session = this._getSession(data.clientId);
                if (!session) {
                    this._logger.warn("No RTCPeerConnection on ICE_CANDIDATE", data);
                    return;
                }
                session.addIceCandidate(data.message);
            }),

            this._serverSocket.on(RELAY_MESSAGES.ICE_END_OF_CANDIDATES, (data) => {
                const session = this._getSession(data.clientId);
                if (!session) {
                    this._logger.warn("No RTCPeerConnection on ICE_END_OF_CANDIDATES", data);
                    return;
                }
                session.addIceCandidate(null);
            }),

            // when a new SDP offer is received from another client
            this._serverSocket.on(RELAY_MESSAGES.SDP_OFFER, (data) => {
                const session = this._getSession(data.clientId);
                if (!session) {
                    this._logger.warn("No RTCPeerConnection on SDP_OFFER", data);
                    return;
                }
                const offer = this._transformIncomingSdp(data.message, session.pc);
                session.handleOffer(offer).then((answer) => {
                    this._emitServerEvent(RELAY_MESSAGES.SDP_ANSWER, {
                        receiverId: data.clientId,
                        message: this._transformOutgoingSdp(answer),
                    });
                });
            }),

            // when a new SDP answer is received from another client
            this._serverSocket.on(RELAY_MESSAGES.SDP_ANSWER, (data) => {
                const session = this._getSession(data.clientId);
                if (!session) {
                    this._logger.warn("No RTCPeerConnection on SDP_ANSWER", data);
                    return;
                }
                const answer = this._transformIncomingSdp(data.message, session.pc);
                session.handleAnswer(answer);
            }),
        ];
    }

    sendAudioMutedStats(muted) {
        rtcStats.sendEvent("audio_muted", { muted });
    }

    sendVideoMutedStats(muted) {
        rtcStats.sendEvent("video_muted", { muted });
    }

    sendStatsCustomEvent(eventName, data) {
        rtcStats.sendEvent(eventName, data);
    }

    rtcStatsDisconnect() {
        rtcStats.server.close();
    }

    rtcStatsReconnect() {
        if (!rtcStats.server.connected) {
            rtcStats.server.connect();
        }
    }

    _startMonitoringAudioTrack(track) {
        track.addEventListener("ended", this._audioTrackOnEnded);
    }

    _stopMonitoringAudioTrack(track) {
        track.removeEventListener("ended", this._audioTrackOnEnded);
    }

    setRoomSessionId(roomSessionId) {
        this._roomSessionId = roomSessionId;
    }
}

const lowPixelCount = 320 * 180;
const lowBitratePerPixel = 150000 / lowPixelCount;
const highPixelCount = 1280 * 720;
const highBitratePerPixel = 1000000 / highPixelCount;
const bitrateChangePerPixel = (highBitratePerPixel - lowBitratePerPixel) / (highPixelCount - lowPixelCount);

// calculates a bitrate for a given resolution+frameRate
function getOptimalBitrate(width, height, frameRate) {
    let targetPixelCount = width * height;
    if (targetPixelCount < lowPixelCount) targetPixelCount = lowPixelCount;
    if (targetPixelCount > highPixelCount) targetPixelCount = highPixelCount;

    let targetBitratePerPixel = lowBitratePerPixel;
    if (targetPixelCount > highPixelCount) targetBitratePerPixel = highBitratePerPixel;
    else if (targetPixelCount > lowPixelCount) {
        targetBitratePerPixel += (targetPixelCount - lowPixelCount) * bitrateChangePerPixel;
    }

    // we use the actual resolution for the target bitrate
    let targetBitrate = width * height * targetBitratePerPixel;

    // adjust bitrate down a bit if reduced framerate
    if (frameRate <= 15) targetBitrate = targetBitrate * 0.7;
    else if (frameRate <= 24) targetBitrate = targetBitrate * 0.9;

    return targetBitrate;
}

const CAMERA_STREAM_ID = RtcStream.getCameraId();
const browserName$1 = adapter.browserDetails.browser;

class P2pRtcManager extends BaseRtcManager {
    _connect(clientId) {
        this.rtcStatsReconnect();
        const shouldAddLocalVideo = true;
        let session = this._getSession(clientId);
        let bandwidth = (session && session.bandwidth) || 0;
        if (session) {
            this._logger.warn("Replacing peer session", clientId);
            this._cleanup(clientId);
        } else {
            bandwidth = this._changeBandwidthForAllClients(true);
        }
        session = this._createP2pSession(clientId, bandwidth, shouldAddLocalVideo, true);
        this._negotiatePeerConnection(clientId, session);
        return Promise.resolve(session);
    }

    _maybeRestartIce(clientId, session) {
        const pc = session.pc;
        if (!(pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed")) {
            return;
        }

        // Only automatically try to restart if you sent the original offer
        if (pc.localDescription.type === "offer") {
            // clean up some helpers.
            session.wasEverConnected = false;
            session.relayCandidateSeen = false;

            this._negotiatePeerConnection(
                clientId,
                session,
                Object.assign({}, this.offerOptions, { iceRestart: true })
            );
        }
    }

    _setCodecPreferences(pc, vp9On, av1On, redOn) {
        try {
            // audio
            const audioTransceivers = pc
                .getTransceivers()
                .filter((transceiver) => transceiver?.sender?.track?.kind === "audio");

            audioTransceivers.forEach((audioTransceiver) => {
                // If not implemented return
                if (typeof RTCRtpSender.getCapabilities === "undefined") return;
                const capabilities = RTCRtpSender.getCapabilities("audio");
                for (let i = 0; i < capabilities.codecs.length; i++) {
                    if (redOn && capabilities.codecs[i].mimeType.toLowerCase() === "audio/red") {
                        capabilities.codecs.unshift(capabilities.codecs.splice(i, 1)[0]);
                        break;
                    }
                }
                // If not implemented return
                if (typeof audioTransceiver.setCodecPreferences === "undefined") return;
                audioTransceiver.setCodecPreferences(capabilities.codecs);
            });
            // video
            const videoTransceivers = pc
                .getTransceivers()
                .filter((transceiver) => transceiver?.sender?.track?.kind === "video");

            videoTransceivers.forEach((videoTransceiver) => {
                // If not implemented return
                if (RTCRtpSender.getCapabilities === undefined) return;
                const capabilities = RTCRtpSender.getCapabilities("video");
                for (let i = 0; i < capabilities.codecs.length; i++) {
                    if (vp9On && capabilities.codecs[i].mimeType.toLowerCase() === "video/vp9") {
                        capabilities.codecs.unshift(capabilities.codecs.splice(i, 1)[0]);
                        break;
                    }
                    if (av1On && capabilities.codecs[i].mimeType.toLowerCase() === "video/av1") {
                        capabilities.codecs.unshift(capabilities.codecs.splice(i, 1)[0]);
                        break;
                    }
                }
                // If not implemented return
                if (videoTransceiver.setCodecPreferences === undefined) return;
                videoTransceiver.setCodecPreferences(capabilities.codecs);
            });
        } catch (error) {
            this._logger.error("Error during setting setCodecPreferences:", error);
        }
    }

    _negotiatePeerConnection(clientId, session, constraints) {
        if (!session) {
            this._logger.warn("No RTCPeerConnection in negotiatePeerConnection()", clientId);
            return;
        }
        const pc = session.pc;
        if (!session.canModifyPeerConnection()) {
            session.pending.push(() => {
                this._negotiatePeerConnection(clientId, session, constraints);
            });
            return;
        }
        session.isOperationPending = true;

        const { vp9On, av1On, redOn } = this._features;

        // Set codec preferences to video transceivers
        if (vp9On || av1On || redOn) {
            this._setCodecPreferences(pc, vp9On, av1On, redOn);
        }

        pc.createOffer(constraints || this.offerOptions)
            .then((offer) => {
                // SDP munging workaround for Firefox, because it doesn't support setCodecPreferences()
                // Only vp9 because FF does not support AV1 yet
                if ((vp9On || redOn) && browserName$1 === "firefox") {
                    offer.sdp = setCodecPreferenceSDP(offer.sdp, vp9On, redOn);
                }

                this._emitServerEvent(RELAY_MESSAGES.SDP_OFFER, {
                    receiverId: clientId,
                    message: this._transformOutgoingSdp(offer),
                });

                // workaround for https://bugzilla.mozilla.org/show_bug.cgi?id=1394602
                // make Chrome send media later when there are two (more more?) video tracks.
                if (
                    browserName$1 === "chrome" &&
                    pc.getSenders().filter((sender) => sender.track && sender.track.kind === "video").length >= 2
                ) {
                    session.pendingOffer = offer;
                    return;
                }
                pc.setLocalDescription(offer).catch((e) => {
                    this._logger.warn("RTCPeerConnection.setLocalDescription() failed with local offer", e);
                });
            })
            .catch((e) => {
                this._logger.warn("RTCPeerConnection.createOffer() failed to create local offer", e);
            });
    }

    _withForcedRenegotiation(session, action) {
        const pc = session.pc;
        const originalOnnegotationneeded = pc.onnegotationneeded;
        pc.onnegotiationneeded = null;
        action();
        this._negotiatePeerConnection(session.clientId, session);
        setTimeout(() => (pc.onnegotiationneeded = originalOnnegotationneeded), 0);
    }

    // implements a strategy to change the bandwidth for all clients (without negotiation)
    // returns bandwidth so it can be used as initial bandwidth for new client.
    _changeBandwidthForAllClients(isJoining) {
        let numPeers = this.numberOfPeerconnections();
        if (isJoining) {
            // client will be added to RTCManager.peerConnections afterwards
            numPeers += 1;
        }

        if (numPeers === 0) {
            return 0;
        }

        if (numPeers > 7) {
            // use last table value.
            numPeers = 7;
        }

        // We use a slightly different curve in premium to give better quality when
        // there are few participants.
        let bandwidth = this._features.bandwidth
            ? parseInt(this._features.bandwidth, 10)
            : {
                  1: this._features.cap2pBitrate ? 1000 : 0,
                  2: this._features.highP2PBandwidth ? 768 : 384,
                  3: this._features.highP2PBandwidth ? 512 : 256,
                  4: 192,
                  5: 128,
                  6: 128,
                  7: 64,
              }[numPeers];

        if (bandwidth === undefined) {
            return 0;
        }

        if (this._features.adjustBitratesFromResolution) {
            const cameraStream = this._getLocalCameraStream();
            if (cameraStream) {
                const cameraTrack = cameraStream && cameraStream.getVideoTracks()[0];
                if (cameraTrack) {
                    const { width, height, frameRate } = cameraTrack.getSettings();
                    if (width && height && frameRate) {
                        const optimalBandwidth = Math.round(getOptimalBitrate(width, height, frameRate) / 1000);
                        bandwidth = Math.min(optimalBandwidth, bandwidth || optimalBandwidth);
                    }
                }
            }
        }

        if (this._features.higherP2PBitrates) {
            bandwidth = bandwidth * 1.5;
        }

        this._forEachPeerConnection((session) => {
            session.changeBandwidth(bandwidth);
        });

        return bandwidth;
    }

    _createP2pSession(clientId, initialBandwidth, shouldAddLocalVideo, isOfferer) {
        const session = this._createSession({
            peerConnectionId: clientId,
            clientId,
            initialBandwidth,
            shouldAddLocalVideo,
            isOfferer,
        });
        const pc = session.pc;

        if (this._features.increaseIncomingMediaBufferOn) {
            this._setJitterBufferTarget(pc);
        }

        /*
         * Explicitly add the video track so that stopOrResumeVideo() can
         * replace it when the video is re-enabled.
         */
        const localCameraStream = this.localStreams[CAMERA_STREAM_ID];
        if (
            shouldAddLocalVideo &&
            localCameraStream &&
            !localCameraStream.getVideoTracks().length &&
            this._stoppedVideoTrack
        ) {
            pc.addTrack(this._stoppedVideoTrack, localCameraStream);
        }

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                session.relayCandidateSeen = session.relayCandidateSeen || event.candidate.type === "relay";
                this._emitServerEvent(RELAY_MESSAGES.ICE_CANDIDATE, {
                    receiverId: clientId,
                    message: event.candidate,
                });
            } else {
                this._emitServerEvent(RELAY_MESSAGES.ICE_END_OF_CANDIDATES, {
                    receiverId: clientId,
                });
            }
        };

        pc.onnegotiationneeded = () => {
            if (pc.iceConnectionState === "new" || !session.connectionStatus) {
                // initial negotiation is handled by our CLIENT_READY/READY_TO_RECEIVE_OFFER exchange
                return;
            }
            this._negotiatePeerConnection(clientId, session);
        };
        return session;
    }

    /**
     * Possibly start a new peer connection for the new stream if needed.
     */
    acceptNewStream({ streamId, clientId, shouldAddLocalVideo }) {
        let session = this._getSession(clientId);
        if (session && streamId !== clientId) {
            // we are adding a screenshare stream to existing session/pc
            return session;
        }
        let bandwidth = (session && session.bandwidth) || 0;
        if (session) {
            // this will happen on a signal-server reconnect
            // before we tried an ice-restart here, now we recreate the session/pc
            this._logger.warn("Replacing peer session", clientId);
            this._cleanup(clientId); // will cleanup and delete session/pc
        } else {
            // we adjust bandwidth based on number of sessions/pcs
            // so only needed when streamId === clientId (camera) and we're not replacing beacuse of reconnect
            bandwidth = this._changeBandwidthForAllClients(true);
        }
        session = this._createP2pSession(clientId, bandwidth, shouldAddLocalVideo);
        this._emitServerEvent(RELAY_MESSAGES.READY_TO_RECEIVE_OFFER, {
            receiverId: clientId,
        });
        return session;
    }

    disconnect(clientId) {
        this._cleanup(clientId);
        this._changeBandwidthForAllClients(false);
        const numPeers = this.numberOfPeerconnections();
        if (numPeers === 0) {
            setTimeout(() => {
                const numPeers = this.numberOfPeerconnections();
                if (numPeers === 0) {
                    this.rtcStatsDisconnect();
                }
            }, 60 * 1000);
        }
    }

    // this does not (currently) make sense for peer-to-peer connections
    updateStreamResolution(/* streamId, clientId, resolution */) {}

    stopOrResumeAudio(/*localStream, enable*/) {
        // detaches the audio from the peerconnection. No-op in P2P mode.
    }

    _handleStopOrResumeVideo({ enable, track }) {
        if (!enable) {
            this._stoppedVideoTrack = track;
        } else {
            if (this._stoppedVideoTrack) {
                this._replaceTrackToPeerConnections(this._stoppedVideoTrack, track);
                delete this._stoppedVideoTrack;
            } else {
                this._addTrackToPeerConnections(track);
            }
        }
    }

    stopOrResumeVideo(localStream, enable) {
        // actually turn off the camera. Chrome-only (Firefox has different plans)
        if (!["chrome", "safari"].includes(browserName$1)) {
            return;
        }
        if (enable === false) {
            // try to stop the local camera so the camera light goes off.
            setTimeout(() => {
                localStream.getVideoTracks().forEach((track) => {
                    if (track.enabled === false) {
                        track.stop();
                        localStream.removeTrack(track);
                        this._emit(EVENTS.LOCAL_STREAM_TRACK_REMOVED, {
                            stream: localStream,
                            track,
                        });

                        this._handleStopOrResumeVideo({ enable, track });
                    }
                });
            }, 5000);
        } else {
            if (localStream.getVideoTracks().length === 0) {
                // re-enable the stream
                const constraints = this._webrtcProvider.getMediaConstraints().video;
                if (!constraints) {
                    // user was screensharing with no-devices, the video
                    // device has been plugged out or similar
                    return;
                }
                navigator.mediaDevices.getUserMedia({ video: constraints }).then((stream) => {
                    const track = stream.getVideoTracks()[0];
                    localStream.addTrack(track);
                    this._emit(EVENTS.LOCAL_STREAM_TRACK_ADDED, {
                        streamId: localStream.id,
                        tracks: [track],
                        screenShare: false,
                    });

                    this._handleStopOrResumeVideo({ enable, track });
                });
            }
        }
    }

    _shareScreen(streamId, stream) {
        this._emitServerEvent(PROTOCOL_REQUESTS.START_SCREENSHARE, {
            streamId,
            hasAudioTrack: !!stream.getAudioTracks().length,
        });
        this._addStreamToPeerConnections(stream);
    }

    removeStream(streamId, stream, requestedByClientId) {
        super.removeStream(streamId, stream);
        this._removeStreamFromPeerConnections(stream);
        this._emitServerEvent(PROTOCOL_REQUESTS.STOP_SCREENSHARE, { streamId, requestedByClientId });
    }
}

class SfuV2Parser {
    static parse(raw) {
        let object;

        try {
            object = JSON.parse(raw);
        } catch (error) {
            return;
        }
        if (typeof object !== "object" || Array.isArray(object)) {
            return;
        }

        // Will return message if valid, null if invalid
        if (object.request) {
            // Request
            return SfuV2Parser._handleRequest(object);
        } else if (object.response) {
            // Response
            return SfuV2Parser._handleResponse(object);
        } else if (object.message) {
            // Message
            return SfuV2Parser._handleMessage(object);
        } else {
            // Invalid.
            return;
        }
    }

    static _handleRequest(rawMessage) {
        const message = {};
        message.request = true;

        if (typeof rawMessage.method !== "string") {
            return;
        }

        if (typeof rawMessage.id !== "number") {
            return;
        }

        message.id = rawMessage.id;
        message.method = rawMessage.method;
        message.data = rawMessage.data || {};

        return message;
    }

    static _handleResponse(rawMessage) {
        const message = {};
        message.response = true;

        if (typeof rawMessage.id !== "number") {
            return;
        }

        message.id = rawMessage.id;

        // Success
        if (rawMessage.ok) {
            message.ok = true;
            message.data = rawMessage.data || {};
        } else {
            // Error
            message.ok = false;
            message.errorCode = rawMessage.errorCode;
            message.errorReason = rawMessage.errorReason;
        }

        return message;
    }

    static _handleMessage(rawMessage) {
        const message = {};
        message.message = true;

        if (typeof rawMessage.method !== "string") {
            return;
        }

        message.method = rawMessage.method;
        message.data = rawMessage.data || {};

        return message;
    }

    static createRequest(method, data) {
        return {
            request: true,
            id: Math.round(Math.random() * 10000000),
            method,
            data: data || {},
        };
    }

    static createSuccessResponse(request, data) {
        return {
            response: true,
            id: request.id,
            ok: true,
            data: data || {},
        };
    }

    static createErrorResponse(request, errorCode, errorReason) {
        return {
            response: true,
            id: request.id,
            errorCode,
            errorReason,
        };
    }

    static createMessage(method, data) {
        return {
            message: true,
            method,
            data: data || {},
        };
    }
}

class VegaConnection extends EventEmitter {
    constructor(wsUrl, logger, protocol = "whereby-sfu#v4") {
        super();

        this.wsUrl = wsUrl;
        this.logger = logger;
        this.protocol = protocol;

        // This is the map of sent requests that are waiting for a response
        this.sents = new Map();
        this._setupSocket();
    }

    _setupSocket() {
        this.socket = new WebSocket(this.wsUrl, this.protocol);
        this.socket.onopen = this._onOpen.bind(this);
        this.socket.onmessage = this._onMessage.bind(this);
        this.socket.onclose = this._onClose.bind(this);
        this.socket.onerror = this._onError.bind(this);
    }

    _tearDown() {
        this.socket.onopen = null;
        this.socket.onmessage = null;
        this.socket.onclose = null;
        this.socket.onerror = null;
        this.socket = null;

        this.sents.forEach((sent) => sent.close());

        this.emit("close");
    }

    close() {
        this.socket?.close();
    }

    _onOpen() {
        this.logger.log("VegaConnectionManager: Connected");

        this.emit("open");
    }

    _onMessage(event) {
        const socketMessage = SfuV2Parser.parse(event.data);

        this.logger.log("VegaConnectionManager: Received message", socketMessage);

        if (socketMessage?.response) {
            this._handleResponse(socketMessage);
        } else if (socketMessage?.message) {
            this.emit("message", socketMessage);
        }
    }

    _onClose() {
        this.logger.log("VegaConnectionManager: Disconnected");

        this._tearDown();
    }

    _onError(error) {
        this.logger.log("VegaConnectionManager: Error", error);
    }

    _handleResponse(socketMessage) {
        const sent = this.sents.get(socketMessage.id);

        if (socketMessage.ok) {
            sent.resolve(socketMessage.data);
        } else {
            const error = new Error(socketMessage.errorReason);
            sent.reject(error);
        }
    }

    send(message) {
        try {
            this.socket.send(JSON.stringify(message));
        } catch (error) {}
    }

    message(method, data = {}) {
        const message = SfuV2Parser.createMessage(method, data);
        this.send(message);
    }

    request(method, data = {}, timeout = 1500 * (15 + 0.1 * this.sents.size)) {
        const request = SfuV2Parser.createRequest(method, data);

        this.send(request);

        return new Promise((pResolve, pReject) => {
            const sent = {
                id: request.id,
                method: request.method,
                resolve: (data2) => {
                    if (!this.sents.delete(request.id)) return;

                    clearTimeout(sent.timer);
                    pResolve(data2);
                },
                reject: (error) => {
                    if (!this.sents.delete(request.id)) return;

                    clearTimeout(sent.timer);
                    pReject(error);
                },
                timer: setTimeout(() => {
                    if (!this.sents.delete(request.id)) return;

                    pReject(new Error("request timeout"));
                }, timeout),
                close: () => {
                    clearTimeout(sent.timer);
                    pReject(new Error("transport closed"));
                },
            };

            this.sents.set(request.id, sent);
        });
    }
}

const AUDIO_SETTINGS = {
    codecOptions: {
        opusDtx: true,
        opusFec: true,
    },
    encodings: [{ dtx: true }],
};

const VIDEO_SETTINGS_HD = {
    codecOptions: {
        videoGoogleStartBitrate: 500,
    },
    encodings: [
        { scaleResolutionDownBy: 4, maxBitrate: 150000 },
        { scaleResolutionDownBy: 2, maxBitrate: 500000 },
        { scaleResolutionDownBy: 1, maxBitrate: 1000000 },
    ],
};

const VIDEO_SETTINGS_SD = {
    codecOptions: {
        videoGoogleStartBitrate: 500,
    },
    encodings: [
        { scaleResolutionDownBy: 2, maxBitrate: 150000 },
        { scaleResolutionDownBy: 1, maxBitrate: 500000 },
    ],
};

const VIDEO_SETTINGS_VP9 = {
    codecOptions: {
        videoGoogleStartBitrate: 500,
    },
    encodings: [{ scalabilityMode: "L3T2_KEY" }],
};

const SCREEN_SHARE_SETTINGS = {
    encodings: [{}],
};

const SCREEN_SHARE_SIMULCAST_SETTINGS = {
    encodings: [
        { scaleResolutionDownBy: 2, dtx: true, maxBitrate: 500000 },
        { scaleResolutionDownBy: 1, dtx: true, maxBitrate: 1500000 },
    ],
};

const SCREEN_SHARE_SETTINGS_VP9 = {
    encodings: [{ dtx: true }],
};

const getMediaSettings = (kind, isScreenShare, features) => {
    const { lowDataModeEnabled, simulcastScreenshareOn, vp9On } = features;

    if (kind === "audio") {
        return AUDIO_SETTINGS;
    }

    if (isScreenShare) {
        if (vp9On) return SCREEN_SHARE_SETTINGS_VP9;
        if (simulcastScreenshareOn) return SCREEN_SHARE_SIMULCAST_SETTINGS;

        return SCREEN_SHARE_SETTINGS;
    } else {
        if (vp9On) return VIDEO_SETTINGS_VP9;
        if (lowDataModeEnabled) return VIDEO_SETTINGS_SD;

        return VIDEO_SETTINGS_HD;
    }
};

const modifyMediaCapabilities = (routerRtpCapabilities, features) => {
    const { vp9On, h264On } = features;

    if (vp9On) {
        const { preferredPayloadType } = routerRtpCapabilities.codecs.find(
            (codec) => codec.mimeType.toLowerCase() === "video/vp9"
        );

        const { preferredPayloadType: aptPreferredPayloadType } = routerRtpCapabilities.codecs.find(
            (codec) => codec.mimeType.toLowerCase() === "video/rtx" && codec.parameters.apt === preferredPayloadType
        );

        routerRtpCapabilities.codecs = routerRtpCapabilities.codecs.filter(
            (codec) =>
                codec.kind === "audio" ||
                codec.preferredPayloadType === preferredPayloadType ||
                codec.preferredPayloadType === aptPreferredPayloadType
        );
    } else if (h264On) {
        const { preferredPayloadType } = routerRtpCapabilities.codecs.find(
            (codec) => codec.mimeType.toLowerCase() === "video/h264"
        );

        const { preferredPayloadType: aptPreferredPayloadType } = routerRtpCapabilities.codecs.find(
            (codec) => codec.mimeType.toLowerCase() === "video/rtx" && codec.parameters.apt === preferredPayloadType
        );

        routerRtpCapabilities.codecs = routerRtpCapabilities.codecs.filter(
            (codec) =>
                codec.kind === "audio" ||
                codec.preferredPayloadType === preferredPayloadType ||
                codec.preferredPayloadType === aptPreferredPayloadType
        );
    }
};

const getHandler = () => {
    let handlerName =
        detectDevice() || (/applecoremedia|applewebkit|safari/i.test(navigator.userAgent) ? "Safari12" : undefined);

    // Since custom browsers on iOS/iPadOS are using webkit under the hood, we must use
    // the Safari handler even if detected as something else (like Chrome)
    if (/iphone|ipad/i.test(navigator.userAgent)) handlerName = "Safari12";

    return handlerName;
};

class KalmanFilter {
    /**
     * Create 1-dimensional kalman filter
     * @param  {Number} options.R Process noise
     * @param  {Number} options.Q Measurement noise
     * @param  {Number} options.A State vector
     * @param  {Number} options.B Control vector
     * @param  {Number} options.C Measurement vector
     * @return {KalmanFilter}
     */
    constructor({ R = 0.01, Q = 2, A = 1.1, B = 1, C = 1.2 } = { R: 0.01, Q: 2, A: 1.1, B: 1, C: 1.2 }) {
        this.R = R; // noise power desirable
        this.Q = Q; // noise power estimated

        this.A = A;
        this.C = C;
        this.B = B;
        this.cov = NaN;
        this.x = NaN; // estimated signal without noise
    }

    /**
     * Filter a new value
     * @param  {Number} z Measurement
     * @param  {Number} u Control
     * @return {Number}
     */
    filter(z, u = 0) {
        if (isNaN(this.x)) {
            this.x = (1 / this.C) * z;
            this.cov = (1 / this.C) * this.Q * (1 / this.C);
        } else {
            // Compute prediction
            const predX = this.predict(u);
            const predCov = this.uncertainty();

            // Kalman gain
            const K = predCov * this.C * (1 / (this.C * predCov * this.C + this.Q));

            // Correction
            this.x = predX + K * (z - this.C * predX);
            this.cov = predCov - K * this.C * predCov;
        }

        return this.x;
    }

    /**
     * Predict next value
     * @param  {Number} [u] Control
     * @return {Number}
     */
    predict(u = 0) {
        return this.A * this.x + this.B * u;
    }

    /**
     * Return uncertainty of filter
     * @return {Number}
     */
    uncertainty() {
        return this.A * this.cov * this.A + this.R;
    }

    /**
     * Return the last filtered measurement
     * @return {Number}
     */
    lastMeasurement() {
        return this.x;
    }

    /**
     * Set measurement noise Q
     * @param {Number} noise
     */
    setMeasurementNoise(noise) {
        this.Q = noise;
    }

    /**
     * Set the process noise R
     * @param {Number} noise
     */
    setProcessNoise(noise) {
        this.R = noise;
    }
}

const createACFCalculator = () => {
    let ewma = -1;
    let lastMax = 0;
    let counter = 0;
    return (arr) => {
        const max = arr.reduce((acc, val) => acc + val, 0) / arr.length;
        // const max = Math.max(...arr);
        if (ewma < 0) {
            ewma = max;
        } else {
            ewma = ewma * 0.95 + max * 0.05;
        }
        if (++counter < 2) {
            lastMax = max;
            return 0;
        }
        // const acf = ((max - ewma) * (lastMax - ewma)) / ((max - ewma) ** 2);
        const acf = (max - ewma) * (lastMax - ewma);
        lastMax = max;
        return acf;
    };
};

const calculateStd = (arr) => {
    const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length;
    return arr.reduce((acc, val) => acc.concat((val - mean) ** 2), []).reduce((acc, val) => acc + val, 0) / arr.length;
};

const standardDeviation = (arr /*, usePopulation = false*/) => {
    const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length;
    return {
        mean,
        SD: Math.sqrt(arr.reduce((acc, val) => acc + (val - mean) ** 2, 0) / arr.length),
    };
};

const variance = (arr, usePopulation = false) => {
    const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length;
    return (
        arr.reduce((acc, val) => acc.concat((val - mean) ** 2), []).reduce((acc, val) => acc + val, 0) /
        (arr.length - (usePopulation ? 0 : 1))
    );
};

var scriptFunctions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    KalmanFilter: KalmanFilter,
    calculateStd: calculateStd,
    createACFCalculator: createACFCalculator,
    standardDeviation: standardDeviation,
    variance: variance
});

const defaultParams = {
    minDecibels: -80, // value 0 will be -80db and below
    maxDecibels: -10, // value 1 will be -10db and higher
    smoothingTimeConstant: 0.8, // smoothing the bins. will depend on sampleRate below
    aggSize: 1, // average/max over last N samples
    sampleRate: 10, // samples/s. smoothing constant's effect will depend on this.
    reportRate: 2, // report every N sample. if doubling samplerate, we should half this to keep the same update rate to SFU
    bins: 32, // number of frequency bands. linear spaced from 0 to audio sample rate / 2 (48khz -> 24khz)
    bands: [
        [0, 1, 2, 3],
        [4, 5, 6, 7],
    ],
    //initScript: ["const kalmanFilter3 = new f.KalmanFilter(); return { kalmanFilter3 };"],
    separateTrack: {
        autoGainControl: false,
        noiseSupression: false,
    },
    // scoreScript: [
    //     "let sumLF = 0;",
    //     "let sumHF = 0;",
    //     "for (let i = 0; i < 4; i++) {",
    //     "    sumLF += bins[i];",
    //     "    sumHF += bins[i + 4];",
    //     "}",
    //     "const meanLF = sumLF / 4;",
    //     "const meanHF = sumHF / 4;",
    //     "const normalizedMean = (meanHF) / Math.max(meanLF + meanHF, 0.01);",
    //     "return normalizedMean * 300;",
    // ],
    binFormula: "bin", // if we need to convert/map bin values. index param is available.
    scoreFormula: "300 * bands[1].avg / Math.max(bands[1].avg + bands[0].avg, 0.01)", // the score that will be aggregated (aggSize)
    outFormula: "score", // the out/score sent to SFU
};

function createMicAnalyser({ micTrack, params, onScoreUpdated }) {
    // todo: might need to reuse existing in PWA
    const audioCtx = new AudioContext();

    let analyser = null;

    // recreates source (connects track to analyser)
    let source = null;
    let lastTrack = null;
    let lastTrackWasOurs = false;
    const setTrack = async (track) => {
        // stop last track if get by us
        if (lastTrack && lastTrackWasOurs) {
            lastTrack.stop();
        }
        lastTrackWasOurs = false;
        if (track && params.separateTrack) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: { deviceId: lastTrack?.getSettings().deviceId, ...params.separateTrack },
                    video: false,
                });
                track = stream.getAudioTracks()[0];
                lastTrackWasOurs = true;
            } catch (ex) {
                console.warn("unable to fetch new track for colocation speaker analysis, using current", ex);
            }
        }

        lastTrack = track;
        if (source) {
            source.disconnect();
            source = null;
        }
        if (track) {
            const stream = new MediaStream([track]);
            source = audioCtx.createMediaStreamSource(stream);
            source.connect(analyser);
        }
    };

    // updates analyzer params and restarts sampler
    let samplerInterval;
    const setParams = (newParams) => {
        clearInterval(samplerInterval);

        params = { ...defaultParams, ...newParams };

        if (!analyser || analyser.fftSize !== params.bins * 2) {
            let restoreTrack = null;
            if (analyser) {
                restoreTrack = lastTrack;
                setTrack(null);
            }
            analyser = audioCtx.createAnalyser();
            analyser.fftSize = params.bins * 2;
            if (restoreTrack) setTrack(restoreTrack);
        }

        const bins = new Uint8Array(analyser.frequencyBinCount);
        const scores = [];

        analyser.minDecibels = params.minDecibels;
        analyser.maxDecibels = params.maxDecibels;
        analyser.smoothingTimeConstant = params.smoothingTimeConstant;

        // todo: we have to guard this somehow - so that it can't be used for js-injection in meetings
        // it is needed for tweaking the scoring algorithm and the output sent to SFU

        const initScriptFunc =
            // eslint-disable-next-line no-new-func
            params.initScript && new Function("f", "setTrack", "lastTrack", params.initScript.join("\n"));

        // eslint-disable-next-line no-new-func
        const scoreScriptFunc = params.scoreScript && new Function("bins", "g", "f", params.scoreScript.join("\n"));

        // eslint-disable-next-line no-new-func
        const calcScore = params.scoreFormula && new Function("bands", `return ${params.scoreFormula}`);
        const getOutValue =
            // eslint-disable-next-line no-new-func
            params.outFormula && new Function("score", "avg", "max", "scores", `return ${params.outFormula}`);
        // eslint-disable-next-line no-new-func
        const binMap = params.binFormula && new Function("bin", "index", `return ${params.binFormula}`);

        let reportRateCounter = 0;

        const globals = initScriptFunc && initScriptFunc(scriptFunctions, setTrack, lastTrack);

        // fetches data, caluclates score, and updates client
        const sampler = () => {
            analyser.getByteFrequencyData(bins);

            let score = 0;
            if (calcScore) {
                const processedBands = params.bands.map((range) => {
                    const bands = range.map((index) => binMap(bins[index] / 255, index));
                    return {
                        avg: bands.reduce((sum, current) => sum + current, 0) / range.length,
                        min: bands.reduce((min, current) => Math.min(min, current / 255), Number.MAX_VALUE),
                        max: bands.reduce((max, current) => Math.max(max, current / 255), 0),
                    };
                });
                score = calcScore(processedBands) || 0;
            } else if (scoreScriptFunc) {
                score = scoreScriptFunc(bins, globals, scriptFunctions);
                if (isNaN(score)) score = 0;
            }
            scores.push(score);
            if (scores.length > params.aggSize) scores.shift();

            reportRateCounter++;
            if (reportRateCounter >= params.reportRate) {
                reportRateCounter = 0;
                const max = scores.reduce((a, b) => Math.max(a, b), 0);
                const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
                const out = getOutValue(score, avg, max, scores);
                onScoreUpdated({ bins, score, max, avg, out });
            }
        };

        samplerInterval = setInterval(sampler, 1000 / params.sampleRate);
    };

    // initializaton
    setParams(params);
    setTrack(micTrack);

    return {
        setParams,
        setTrack,
        close() {
            setTrack(null);
            clearInterval(samplerInterval);
            audioCtx.close();
        },
    };
}

const maybeTurnOnly = (transportConfig, features) => {
    if (!features.useOnlyTURN) {
        return;
    }

    transportConfig.iceTransportPolicy = "relay";

    // if value is more specific we can filter ICE server list based on protocol (udp/tcp/tls)
    const filter = {
        onlyudp: /^turn:.*transport=udp$/,
        onlytcp: /^turn:.*transport=tcp$/,
        onlytls: /^turns:.*transport=tcp$/,
    }[features.useOnlyTURN];

    if (filter) {
        transportConfig.iceServers = transportConfig.iceServers.filter((entry) => entry.url && entry.url.match(filter));
    }
};

const browserName = adapter.browserDetails.browser;
let unloading = false;

const RESTARTICE_ERROR_RETRY_THRESHOLD_IN_MS = 3500;
const RESTARTICE_ERROR_MAX_RETRY_COUNT = 5;
const OUTBOUND_CAM_OUTBOUND_STREAM_ID = v4();
const OUTBOUND_SCREEN_OUTBOUND_STREAM_ID = v4();

if (browserName === "chrome") window.document.addEventListener("beforeunload", () => (unloading = true));

class VegaRtcManager {
    constructor({ selfId, room, emitter, serverSocket, webrtcProvider, features, eventClaim, logger = console }) {
        assert$1.ok(selfId, "selfId is required");
        assert$1.ok(room, "room is required");
        assert$1.ok(emitter && emitter.emit, "emitter is required");
        assert$1.ok(serverSocket instanceof ServerSocket, "serverSocket is required");
        assert$1.ok(webrtcProvider, "webrtcProvider is required");

        const { session, iceServers, sfuServer, mediaserverConfigTtlSeconds } = room;

        this._selfId = selfId;
        this._room = room;
        this._roomSessionId = session?.id;
        this._emitter = emitter;
        this._serverSocket = serverSocket;
        this._webrtcProvider = webrtcProvider;
        this._features = features || {};
        this._eventClaim = eventClaim;
        this._logger = logger;

        this._vegaConnection = null;

        this._micAnalyser = null;
        this._micAnalyserDebugger = null;

        this._mediasoupDevice = new Device({ handlerName: getHandler() });
        this._routerRtpCapabilities = null;

        this._sendTransport = null;
        this._receiveTransport = null;

        // Mapped by clientId
        this._clientStates = new Map();

        // Used for setting preferred layers based on streamId
        this._streamIdToVideoConsumerId = new Map();

        // All consumers we have from the SFU
        this._consumers = new Map();
        this._dataConsumers = new Map();

        // Function to clean up event listeners on the local stream
        this._localStreamDeregisterFunction = null;

        this._micTrack = null;
        this._webcamTrack = null;
        this._screenVideoTrack = null;
        this._screenAudioTrack = null;

        this._micProducer = null;
        this._micProducerPromise = null;
        this._micPaused = false;
        this._micScoreProducer = null;
        this._micScoreProducerPromise = null;
        this._webcamProducer = null;
        this._webcamProducerPromise = null;
        this._webcamPaused = false;
        this._screenVideoProducer = null;
        this._screenVideoProducerPromise = null;
        this._screenAudioProducer = null;
        this._screenAudioProducerPromise = null;

        this._sndTransportIceRestartPromise = null;
        this._rcvTransportIceRestartPromise = null;

        this._colocation = null;

        this._stopCameraTimeout = null;
        this._audioTrackOnEnded = () => {
            // There are a couple of reasons the microphone could stop working.
            // One of them is getting unplugged. The other is the Chrome audio
            // process crashing. The third is the tab being closed.
            // https://bugs.chromium.org/p/chromium/issues/detail?id=1050008
            rtcStats.sendEvent("audio_ended", { unloading });
            this._emitToPWA(rtcManagerEvents.MICROPHONE_STOPPED_WORKING, {});
        };

        this._updateAndScheduleMediaServersRefresh({
            sfuServer,
            iceServers: iceServers.iceServers || [],
            mediaserverConfigTtlSeconds,
        });

        this._socketListenerDeregisterFunctions = [];

        // Retry if connection closed until disconnectAll called;
        this._reconnect = true;
        this._reconnectTimeOut = null;
    }

    _updateAndScheduleMediaServersRefresh({ iceServers, sfuServer, mediaserverConfigTtlSeconds }) {
        this._iceServers = iceServers;
        this._sfuServer = sfuServer;
        this._mediaserverConfigTtlSeconds = mediaserverConfigTtlSeconds;

        this._sendTransport?.updateIceServers({ iceServers: this._iceServers });
        this._receiveTransport?.updateIceServers({ iceServers: this._iceServers });

        this._clearMediaServersRefresh();
        if (!mediaserverConfigTtlSeconds) {
            return;
        }
        this._fetchMediaServersTimer = setTimeout(
            () => this._emitToSignal(PROTOCOL_REQUESTS.FETCH_MEDIASERVER_CONFIG),
            mediaserverConfigTtlSeconds * 1000
        );
    }

    _clearMediaServersRefresh() {
        if (!this._fetchMediaServersTimer) return;
        clearTimeout(this._fetchMediaServersTimer);
        this._fetchMediaServersTimer = null;
    }

    setupSocketListeners() {
        this._socketListenerDeregisterFunctions.push(
            () => this._clearMediaServersRefresh(),

            this._serverSocket.on(PROTOCOL_RESPONSES.MEDIASERVER_CONFIG, (data) => {
                if (data.error) {
                    this._logger.warn("FETCH_MEDIASERVER_CONFIG failed:", data.error);
                    return;
                }
                this._updateAndScheduleMediaServersRefresh(data);
            }),
            this._serverSocket.on(PROTOCOL_RESPONSES.ROOM_JOINED, () => {
                if (this._screenVideoTrack) this._emitScreenshareStarted();
            })
        );

        this._connect();
    }

    _emitScreenshareStarted() {
        this._emitToSignal(PROTOCOL_REQUESTS.START_SCREENSHARE, {
            streamId: OUTBOUND_SCREEN_OUTBOUND_STREAM_ID,
            hasAudioTrack: Boolean(this._screenAudioTrack),
        });
    }

    _connect() {
        const host = this._features.sfuServerOverrideHost || [this._sfuServer.url];
        const searchParams = new URLSearchParams({
            clientId: this._selfId,
            organizationId: this._room.organizationId,
            roomName: this._room.name,
            eventClaim: this._room.isClaimed ? this._eventClaim : null,
            lowBw: true,
        });
        const queryString = searchParams.toString();
        const wsUrl = `wss://${host}?${queryString}`;

        this._vegaConnection = new VegaConnection(wsUrl, this._logger);
        this._vegaConnection.on("open", () => this._join());
        this._vegaConnection.on("close", () => this._onClose());
        this._vegaConnection.on("message", (message) => this._onMessage(message));
    }

    _onClose() {
        this._logger.debug("_onClose()");

        // These will clean up any and all producers/consumers
        this._sendTransport?.close();
        this._receiveTransport?.close();
        this._sendTransport = null;
        this._receiveTransport = null;

        // Clear all mappings we have
        this._streamIdToVideoConsumerId.clear();
        if (this._reconnect) {
            this._reconnectTimeOut = setTimeout(() => this._connect(), 1000);
        }
    }

    async _join() {
        this._logger.debug("join()");

        try {
            // We need to always do this, as this triggers the join logic on the SFU
            const { routerRtpCapabilities } = await this._vegaConnection.request("getCapabilities");

            if (!this._routerRtpCapabilities) {
                modifyMediaCapabilities(routerRtpCapabilities, this._features);

                this._routerRtpCapabilities = routerRtpCapabilities;
                await this._mediasoupDevice.load({ routerRtpCapabilities });
            }

            this._vegaConnection.message("setCapabilities", {
                rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
            });

            if (this._colocation) this._vegaConnection.message("setColocation", { colocation: this._colocation });

            await Promise.all([this._createTransport(true), this._createTransport(false)]);

            const mediaPromises = [];

            if (this._micTrack && !this._micProducer && !this._micProducerPromise)
                mediaPromises.push(this._internalSendMic());
            if (this._webcamTrack && !this._webcamProducer && !this._webcamProducerPromise)
                mediaPromises.push(this._internalSendWebcam());
            if (this._screenVideoTrack && !this._screenVideoProducer && !this._screenVideoProducerPromise)
                mediaPromises.push(this._internalSendScreenVideo());
            if (this._screenAudioTrack && !this._screenAudioProducer && !this._screenAudioProducerPromise)
                mediaPromises.push(this._internalSendScreenAudio());

            await Promise.all(mediaPromises);
        } catch (error) {
            this._logger.error("_join() [error:%o]", error);
            // TODO: handle this error, rejoin?
        }
    }

    async _createTransport(send) {
        const creator = send ? "createSendTransport" : "createRecvTransport";

        const transportOptions = await this._vegaConnection.request("createTransport", {
            producing: send,
            consuming: !send,
            enableTcp: true,
            enableUdp: true,
            preferUdp: true,
            sctpParameters: {
                enableSctp: true,
                numSctpStreams: {
                    OS: 1024,
                    MIS: 1024,
                },
                maxSctpMessageSize: 262144,
                sctpSendBufferSize: 262144,
            },
        });

        transportOptions.iceServers = this._iceServers;

        maybeTurnOnly(transportOptions, this._features);

        const transport = this._mediasoupDevice[creator](transportOptions);
        const onConnectionStateListener = async (connectionState) => {
            this._logger.debug(`Transport ConnectionStateChanged ${connectionState}`);
            if (connectionState !== "disconnected" && connectionState !== "failed") {
                return;
            }
            if (send) {
                if (this._sndTransportIceRestartPromise) {
                    return;
                }
                this._sndTransportIceRestartPromise = this._restartIce(transport).finally(() => {
                    this._sndTransportIceRestartPromise = null;
                });
            } else {
                if (this._rcvTransportIceRestartPromise) {
                    return;
                }
                this._rcvTransportIceRestartPromise = this._restartIce(transport).finally(() => {
                    this._rcvTransportIceRestartPromise = null;
                });
            }
        };
        transport
            .on("connect", ({ dtlsParameters }, callback) => {
                this._vegaConnection?.message("connectTransport", {
                    transportId: transport.id,
                    dtlsParameters,
                });

                callback();
            })
            .on("connectionstatechange", onConnectionStateListener);

        transport.observer.once("close", () => {
            transport.removeListener(onConnectionStateListener);
        });

        if (send) {
            transport.on("produce", async ({ kind, rtpParameters, appData }, callback, errback) => {
                try {
                    const { paused } = appData;

                    const { id } = await this._vegaConnection?.request("produce", {
                        transportId: transport.id,
                        kind,
                        rtpParameters,
                        paused,
                        appData,
                    });

                    callback({ id });
                } catch (error) {
                    errback(error);
                }
            });
            transport.on("producedata", async ({ appData, sctpStreamParameters }, callback, errback) => {
                try {
                    const { id } = await this._vegaConnection?.request("produceData", {
                        transportId: transport.id,
                        sctpStreamParameters,
                        appData,
                    });
                    callback({ id });
                } catch (error) {
                    errback(error);
                }
            });

            this._sendTransport = transport;
        } else {
            this._receiveTransport = transport;
        }
    }

    async _restartIce(transport, retried = 0) {
        if (!transport || !("closed" in transport) || !("connectionState" in transport)) {
            this._logger.debug("_restartIce: No transport or property closed or connectionState!");
            return;
        }

        if (transport.closed) {
            this._logger.debug("_restartIce: Transport is closed!");
            return;
        }

        if (transport.connectionState !== "disconnected" && transport.connectionState !== "failed") {
            this._logger.debug("_restartIce: Connection is healthy ICE restart no loneger needed!");
            return;
        }

        // Prevent too fast iceRestarts
        const { iceRestartStarted } = transport.appData;
        const now = Date.now();
        if (Number.isFinite(iceRestartStarted) && now - iceRestartStarted < RESTARTICE_ERROR_RETRY_THRESHOLD_IN_MS) {
            return;
        }
        transport.appData.iceRestartStarted = now;

        if (RESTARTICE_ERROR_MAX_RETRY_COUNT <= retried) {
            this._logger.debug("_restartIce: Reached restart ICE  maximum retry count!");
            return;
        }

        if (!this._vegaConnection) {
            this._logger.debug(`_restartIce: Connection is undefined`);
            return;
        }
        const { iceParameters } = await this._vegaConnection.request("restartIce", { transportId: transport.id });

        this._logger.debug("_restartIce: ICE restart iceParameters received from SFU: ", iceParameters);
        const error = await transport
            .restartIce({ iceParameters })
            .then(() => null)
            .catch((err) => err);

        if (error) {
            this._logger.error(`_restartIce: ICE restart failed: ${error}`);
            switch (error.message) {
                case "missing transportId":
                case "no such transport":
                    // no retry
                    break;
                default:
                    // exponential backoff
                    await new Promise((resolve) => {
                        setTimeout(
                            () => {
                                resolve();
                            },
                            Math.min(RESTARTICE_ERROR_RETRY_THRESHOLD_IN_MS * 2 ** retried, 60000)
                        );
                    });
                    await this._restartIce(transport, retried + 1);
                    break;
            }
            return;
        }
        await new Promise((resolve) => {
            setTimeout(
                () => {
                    resolve();
                },
                60000 * Math.min(8, retried + 1)
            );
        });
        if (transport.connectionState === "failed" || transport.connectionState === "disconnected") {
            await this._restartIce(transport, retried + 1);
            return;
        }
    }

    async _internalSendMic() {
        this._logger.debug("_internalSendMic()");

        this._micProducerPromise = (async () => {
            try {
                // Have any of our resources disappeared while we were waiting to be executed?
                if (!this._micTrack || !this._sendTransport || this._micProducer) {
                    this._micProducerPromise = null;
                    return;
                }

                const currentPaused = this._micPaused;

                const producer = await this._sendTransport.produce({
                    track: this._micTrack,
                    disableTrackOnPause: false,
                    stopTracks: false,
                    ...getMediaSettings("audio", false, this._features),
                    appData: {
                        streamId: OUTBOUND_CAM_OUTBOUND_STREAM_ID,
                        sourceClientId: this._selfId,
                        screenShare: false,
                        source: "mic",
                        paused: currentPaused,
                    },
                });

                currentPaused ? producer.pause() : producer.resume();

                this._micProducer = producer;

                producer.observer.once("close", () => {
                    this._logger.debug('micProducer "close" event');

                    if (producer.appData.localClosed)
                        this._vegaConnection?.message("closeProducers", { producerIds: [producer.id] });

                    this._micProducer = null;
                    this._micProducerPromise = null;
                });

                if (this._micTrack !== this._micProducer.track) await this._replaceMicTrack();
                if (this._micPaused !== this._micProducer.paused) this._pauseResumeMic();
            } catch (error) {
                this._logger.error("micProducer failed:%o", error);
            } finally {
                this._micProducerPromise = null;

                // Has the track disappeared while we were waiting to be executed?
                if (!this._micTrack) {
                    this._stopProducer(this._micProducer);
                    this._micProducer = null;
                }
            }
        })();
    }

    async _internalSetupMicScore() {
        this._logger.debug("_internalSetupMicScore()");

        this._micScoreProducerPromise = (async () => {
            try {
                // Have any of our resources disappeared while we were waiting to be executed?
                if (!this._micProducer || !this._colocation || this._micScoreProducer) {
                    this._micScoreProducerPromise = null;
                    return;
                }

                const producer = await this._sendTransport.produceData({
                    ordered: false,
                    maxPacketLifeTime: 3000,
                    label: "micscore",
                    appData: {
                        producerId: this._micProducer.id,
                        clientId: this._selfId,
                    },
                });

                this._micScoreProducer = producer;

                producer.observer.once("close", () => {
                    this._logger.debug('micScoreProducer "close" event');
                    if (producer.appData.localClosed) {
                        this._vegaConnection?.message("closeDataProducers", { dataProducerIds: [producer.id] });
                    }

                    this._micScoreProducer = null;
                    this._micScoreProducerPromise = null;
                });
            } catch (error) {
                this._logger.error("micScoreProducer failed:%o", error);
            } finally {
                this._micScoreProducerPromise = null;

                // Has the mic producer disappeared or colocation turned off while we were waiting?
                if (!this._micProducer || !this._colocation) {
                    this._stopMicScoreProducer();
                }
            }
        })();
    }

    _stopMicScoreProducer() {
        this._stopProducer(this._micScoreProducer);
        this._micScoreProducer = null;
    }

    async _replaceMicTrack() {
        this._logger.debug("_replaceMicTrack()");

        if (!this._micTrack || !this._micProducer || this._micProducer.closed) return;

        if (this._micProducer.track !== this._micTrack) {
            await this._micProducer.replaceTrack({ track: this._micTrack });
            this._micAnalyser?.setTrack(this._micTrack);

            // Recursively call replaceMicTrack() until the new track is used.
            // This is needed because someone could have called sendXXX() while we
            // were waiting for the new track to be used.
            await this._replaceMicTrack();
        }
    }

    _pauseResumeMic() {
        this._logger.debug("_pauseResumeMic()");

        if (!this._micProducer || this._micProducer.closed) return;

        if (this._micPaused !== this._micProducer.paused) {
            if (this._micPaused) {
                this._micProducer.pause();

                this._vegaConnection?.message("pauseProducers", {
                    producerIds: [this._micProducer.id],
                });
            } else {
                this._micProducer.resume();

                this._vegaConnection?.message("resumeProducers", {
                    producerIds: [this._micProducer.id],
                });
            }

            // Recursively call pauseResumeMic() until the new paused state is used.
            // This is needed because someone could have called sendXXX() while we
            // were waiting for the new paused state to be used.
            this._pauseResumeMic();
        }
    }

    async _sendMic(track) {
        this._logger.debug("_sendMic() [track:%o]", track);

        this._micTrack = track;

        if (this._micProducer) {
            return await this._replaceMicTrack();
        } else if (this._micProducerPromise) {
            // This promise will make sure to call replaceMicTrack() once the
            // previous _sendMic() promise is resolved, so we can simply return.
            return;
        }

        // We don't do anything if we don't have a sendTransport yet.
        // The join logic will call _internalSendMic() again once the sendTransport is ready.
        if (this._sendTransport) return await this._internalSendMic();
    }

    _sendMicScore(score) {
        // drop invalid scores
        if (isNaN(score)) return;
        if (!isFinite(score)) return;

        if (this._micScoreProducer) {
            // bug in mediasoup? seems once("close") is not always fired, so we need another check here
            if (this._micScoreProducer.closed) {
                this._stopMicScoreProducer();
                return;
            }

            try {
                this._micScoreProducer.send(JSON.stringify({ score }));
            } catch (ex) {
                console.error("_sendMicScore failed [error:%o]", ex);
            }
            return;
        }
        // create producer on first non-zero score when it doesn't exist
        // it needs the _micProducer.id. we don't care about dropping a few intial scores while this is set up
        if (!this._micScoreProducerPromise && this._micProducer && this._colocation && score !== 0) {
            this._internalSetupMicScore();
        }
    }

    async _internalSendWebcam() {
        this._logger.debug("_internalSendWebcam()");

        this._webcamProducerPromise = (async () => {
            try {
                // Have any of our resources disappeared while we were waiting to be executed?
                if (!this._webcamTrack || !this._sendTransport || this._webcamProducer) {
                    this._webcamProducerPromise = null;
                    return;
                }

                const currentPaused = this._webcamPaused;

                const producer = await this._sendTransport.produce({
                    track: this._webcamTrack,
                    disableTrackOnPause: false,
                    stopTracks: false,
                    ...getMediaSettings("video", false, this._features),
                    appData: {
                        streamId: OUTBOUND_CAM_OUTBOUND_STREAM_ID,
                        sourceClientId: this._selfId,
                        screenShare: false,
                        source: "webcam",
                        paused: currentPaused,
                    },
                });

                currentPaused ? producer.pause() : producer.resume();

                this._webcamProducer = producer;
                producer.observer.once("close", () => {
                    this._logger.debug('webcamProducer "close" event');

                    if (producer.appData.localClosed)
                        this._vegaConnection?.message("closeProducers", { producerIds: [producer.id] });

                    this._webcamProducer = null;
                    this._webcamProducerPromise = null;
                });

                // Has someone replaced the track?
                if (this._webcamTrack !== this._webcamProducer.track) await this._replaceWebcamTrack();
                if (this._webcamPaused !== this._webcamProducer.paused) this._pauseResumeWebcam();
            } catch (error) {
                this._logger.error("webcamProducer failed:%o", error);
            } finally {
                this._webcamProducerPromise = null;

                // Has the track disappeared while we were waiting to be executed?
                if (!this._webcamTrack) {
                    await this._stopProducer(this._webcamProducer);
                    this._webcamProducer = null;
                }
            }
        })();
    }

    async _replaceWebcamTrack() {
        this._logger.debug("_replaceWebcamTrack()");

        if (!this._webcamTrack || !this._webcamProducer || this._webcamProducer.closed) return;

        if (this._webcamProducer.track !== this._webcamTrack) {
            await this._webcamProducer.replaceTrack({ track: this._webcamTrack });
            await this._replaceWebcamTrack();
        }
    }

    _pauseResumeWebcam() {
        this._logger.debug("_pauseResumeWebcam()");

        if (!this._webcamProducer || this._webcamProducer.closed) return;

        if (this._webcamPaused !== this._webcamProducer.paused) {
            if (this._webcamPaused) {
                this._webcamProducer.pause();

                this._vegaConnection?.message("pauseProducers", {
                    producerIds: [this._webcamProducer.id],
                });
            } else {
                this._webcamProducer.resume();

                this._vegaConnection?.message("resumeProducers", {
                    producerIds: [this._webcamProducer.id],
                });
            }

            this._pauseResumeWebcam();
        }
    }

    async _sendWebcam(track) {
        this._logger.debug("_sendWebcam() [track:%o]", track);

        this._webcamTrack = track;

        if (this._webcamProducer) {
            return await this._replaceWebcamTrack();
        } else if (this._webcamProducerPromise) {
            return;
        }

        if (this._sendTransport) return await this._internalSendWebcam();
    }

    async _internalSendScreenVideo() {
        this._logger.debug("_internalSendScreenVideo()");

        this._screenVideoProducerPromise = (async () => {
            try {
                // Have any of our resources disappeared while we were waiting to be executed?
                if (!this._screenVideoTrack || !this._sendTransport || this._screenVideoProducer) {
                    this._screenVideoProducerPromise = null;
                    return;
                }

                const producer = await this._sendTransport.produce({
                    track: this._screenVideoTrack,
                    disableTrackOnPause: false,
                    stopTracks: false,
                    ...getMediaSettings("video", true, this._features),
                    appData: {
                        streamId: OUTBOUND_SCREEN_OUTBOUND_STREAM_ID,
                        sourceClientId: this._selfId,
                        screenShare: true,
                        source: "screenvideo",
                        paused: false,
                    },
                });

                this._screenVideoProducer = producer;
                producer.observer.once("close", () => {
                    this._logger.debug('screenVideoProducer "close" event');

                    if (producer.appData.localClosed)
                        this._vegaConnection?.message("closeProducers", { producerIds: [producer.id] });

                    this._screenVideoProducer = null;
                    this._screenVideoProducerPromise = null;
                });

                // Has someone replaced the track?
                if (this._screenVideoTrack !== this._screenVideoProducer.track) await this._replaceScreenVideoTrack();
            } catch (error) {
                this._logger.error("screenVideoProducer failed:%o", error);
            } finally {
                this._screenVideoProducerPromise = null;

                // Has the track disappeared while we were waiting to be executed?
                if (!this._screenVideoTrack) {
                    await this._stopProducer(this._screenVideoProducer);
                    this._screenVideoProducer = null;
                }
            }
        })();
    }

    async _replaceScreenVideoTrack() {
        this._logger.debug("_replaceScreenVideoTrack()");

        if (!this._screenVideoTrack || !this._screenVideoProducer || this._screenVideoProducer.closed) return;

        if (this._screenVideoProducer.track !== this._screenVideoTrack) {
            await this._screenVideoProducer.replaceTrack({ track: this._screenVideoTrack });
            await this._replaceScreenVideoTrack();
        }
    }

    async _sendScreenVideo(track) {
        this._logger.debug("_sendScreenVideo() [track:%o]", track);

        this._screenVideoTrack = track;

        if (this._screenVideoProducer) {
            return await this._replaceScreenVideoTrack();
        } else if (this._screenVideoProducerPromise) {
            return;
        }

        if (this._sendTransport) return await this._internalSendScreenVideo();
    }

    async _internalSendScreenAudio() {
        this._logger.debug("_internalSendScreenAudio()");

        this._screenAudioProducerPromise = (async () => {
            try {
                // Have any of our resources disappeared while we were waiting to be executed?
                if (!this._screenAudioTrack || !this._sendTransport || this._screenAudioProducer) {
                    this._screenAudioProducerPromise = null;
                    return;
                }

                const producer = await this._sendTransport.produce({
                    track: this._screenAudioTrack,
                    disableTrackOnPause: false,
                    stopTracks: false,
                    ...getMediaSettings("video", true, this._features),
                    appData: {
                        streamId: OUTBOUND_SCREEN_OUTBOUND_STREAM_ID,
                        sourceClientId: this._selfId,
                        screenShare: true,
                        source: "screenaudio",
                        paused: false,
                    },
                });

                this._screenAudioProducer = producer;
                producer.observer.once("close", () => {
                    this._logger.debug('screenAudioProducer "close" event');

                    if (producer.appData.localClosed)
                        this._vegaConnection?.message("closeProducers", { producerIds: [producer.id] });

                    this._screenAudioProducer = null;
                    this._screenAudioProducerPromise = null;
                });

                // Has someone replaced the track?
                if (this._screenAudioTrack !== this._screenAudioProducer.track) await this._replaceScreenAudioTrack();
            } catch (error) {
                this._logger.error("screenAudioProducer failed:%o", error);
            } finally {
                this._screenAudioProducerPromise = null;

                // Has the track disappeared while we were waiting to be executed?
                if (!this._screenAudioTrack) {
                    await this._stopProducer(this._screenAudioProducer);
                    this._screenAudioProducer = null;
                }
            }
        })();
    }

    async _replaceScreenAudioTrack() {
        this._logger.debug("_replaceScreenAudioTrack()");

        if (!this._screenAudioTrack || !this._screenAudioProducer || this._screenAudioProducer.closed) return;

        if (this._screenAudioProducer.track !== this._screenAudioTrack) {
            await this._screenAudioProducer.replaceTrack({ track: this._screenAudioTrack });
            await this._replaceScreenAudioTrack();
        }
    }

    async _sendScreenAudio(track) {
        this._logger.debug("_sendScreenAudio() [track:%o]", track);

        this._screenAudioTrack = track;

        if (this._screenAudioProducer) {
            return await this._replaceScreenAudioTrack();
        } else if (this._screenAudioProducerPromise) {
            return;
        }

        if (this._sendTransport) return await this._internalSendScreenAudio();
    }

    _stopProducer(producer) {
        this._logger.debug("_stopProducer()");

        if (!producer || producer.closed) return;

        producer.appData.localClosed = true;
        producer.close();
    }

    /**
     * This is called from the RTCDispatcher when the signal socket reconnects to
     * verify that the RTCManager is still valid.
     *
     * @param {{
     *     selfId: string,
     *     roomName: string,
     *     isSfu: boolean,
     * }} options
     * @returns {boolean}
     */
    isInitializedWith({ selfId, roomName, isSfu }) {
        return this._selfId === selfId && this._room.name === roomName && Boolean(isSfu);
    }

    /**
     * This gets called from the RTCDispatcher when the signal socket reconnects.
     *
     * @param {string} eventClaim
     */
    setEventClaim(eventClaim) {
        this._eventClaim = eventClaim;

        this._vegaConnection?.message("eventClaim", { eventClaim });
    }

    /**
     * This gets called when the user joins a colocation group, where the group is
     * the string identified for the group that is shared between the users.
     *
     * @param {string} colocation
     */
    setColocation(colocation) {
        this._colocation = colocation;
        this._vegaConnection?.message("setColocation", { colocation });

        // stop mic score producer when leaving colocation
        // (it will start on demand when score is attempted sent and mic track is ready)
        if (!colocation && this._micScoreProducer && !this._micScoreProducerPromise) {
            this._stopMicScoreProducer();
        }

        this._syncMicAnalyser();

        rtcStats.sendEvent("colocation_changed", { colocation });
    }

    /**
     * The unique identifier for this room session.
     *
     * @param {string} roomSessionId
     */
    setRoomSessionId(roomSessionId) {
        this._roomSessionId = roomSessionId;
    }

    /**
     * The first parameter is either a clientId if the client actually leaves
     * or a streamId if the client stops its screen share. The eventClaim must
     * be sent to the SFU to ensure that the SFU knows that the client has left.
     *
     * @param {string} clientIdOrStreamId
     * @param {ignored} _activeBreakout
     * @param {string} eventClaim
     */
    disconnect(clientIdOrStreamId, _activeBreakout, eventClaim) {
        this._logger.debug("disconnect() [clientIdOrStreamId:%s, eventClaim:%s]", clientIdOrStreamId, eventClaim);

        if (this._clientStates.has(clientIdOrStreamId)) {
            // In this case this is a disconnect from an actual client, not just a screen share.

            const clientState = this._clientStates.get(clientIdOrStreamId);

            clientState.hasAcceptedWebcamStream = false;
            clientState.hasAcceptedScreenStream = false;

            this._syncIncomingStreamsWithPWA(clientIdOrStreamId);
        }

        if (eventClaim) {
            this._eventClaim = eventClaim;
            this._vegaConnection?.message("eventClaim", { eventClaim });
        }
    }

    /**
     * Only used for webcam/mic stream.
     *
     * We can ignore the oldTrack and match based on kind instead.
     *
     * This is called when the PWA does a internal track replacement because of
     * ex. effects added and so on.
     *
     * @param {MediaStreamTrack | null} _oldTrack
     * @param {MediaStreamTrack} track
     */
    replaceTrack(oldTrack, track) {
        if (oldTrack && oldTrack.kind === "audio") {
            this._stopMonitoringAudioTrack(oldTrack);
        }

        if (track.kind === "audio") {
            this._startMonitoringAudioTrack(track);
            this._micTrack = track;
            this._replaceMicTrack();
        }

        if (track.kind === "video") {
            this._webcamTrack = track;
            this._replaceWebcamTrack();
        }
    }

    /**
     * Only used for screen sharing.
     *
     * This is called when the user stops sharing their screen, and it can
     * be requested from a remote client.
     *
     * @param {string} streamId
     * @param {MediaStream} _stream
     * @param {string} requestedByClientId
     */
    removeStream(streamId, _stream, requestedByClientId) {
        this._logger.debug("removeStream() [streamId:%s, requestedByClientId:%s]", streamId, requestedByClientId);

        this._emitToSignal(PROTOCOL_REQUESTS.STOP_SCREENSHARE, {
            streamId: OUTBOUND_SCREEN_OUTBOUND_STREAM_ID,
            requestedByClientId,
        });

        this._stopProducer(this._screenVideoProducer);
        this._screenVideoProducer = null;
        this._screenVideoTrack = null;
        this._stopProducer(this._screenAudioProducer);
        this._screenAudioProducer = null;
        this._screenAudioTrack = null;
    }

    _onMicAnalyserScoreUpdated(data) {
        this._micAnalyserDebugger?.onScoreUpdated?.(data);
        this._sendMicScore(this._micPaused ? 0 : data.out);
    }

    /**
     * streamId is "0" if this is the webcam/mic stream, and a proper streamId
     * if this is a screen share.
     *
     * For the webcam/mic stream, this is only called once, on RTC initialization.
     * For screen shares, this is called every time a new screen share is started.
     *
     * This is called when the user wants to start sending a stream. This
     * can be either a screen sharing stream or a camera stream with their
     * respective audio tracks.
     *
     * @param {string | "0"} streamId
     * @param {MediaStream} stream
     */
    addNewStream(streamId, stream, audioPaused, videoPaused) {
        if (streamId === "0") {
            this._micPaused = audioPaused;
            this._webcamPaused = videoPaused;

            const videoTrack = stream.getVideoTracks()[0];
            const audioTrack = stream.getAudioTracks()[0];

            if (videoTrack) this._sendWebcam(videoTrack);
            if (audioTrack) {
                this._sendMic(audioTrack);
                this._syncMicAnalyser();
                this._startMonitoringAudioTrack(audioTrack);
            }

            // This should not be needed, but checking nonetheless
            if (this._localStreamDeregisterFunction) {
                this._localStreamDeregisterFunction();
                this._localStreamDeregisterFunction = null;
            }

            const localStreamHandler = (e) => {
                const { enable, track } = e.detail;

                // This is a hack
                this._webcamPaused = !enable;
                this._pauseResumeWebcam();

                this._handleStopOrResumeVideo({ enable, track });
            };

            stream.addEventListener("stopresumevideo", localStreamHandler);
            this._localStreamDeregisterFunction = () => {
                stream.removeEventListener("stopresumevideo", localStreamHandler);
            };
        } else {
            const videoTrack = stream.getVideoTracks()[0];
            const audioTrack = stream.getAudioTracks()[0];

            if (videoTrack) this._sendScreenVideo(videoTrack);
            if (audioTrack) this._sendScreenAudio(audioTrack);

            this._emitScreenshareStarted();
        }
    }

    _syncMicAnalyser() {
        if (this._micTrack && this._colocation && !this._micAnalyser) {
            this._micAnalyser = createMicAnalyser({
                micTrack: this._micTrack,
                onScoreUpdated: this._onMicAnalyserScoreUpdated.bind(this),
            });
            return;
        }
        if (this._micAnalyser && !this._colocation) {
            this._micAnalyser.close();
            this._micAnalyser = null;
            return;
        }
    }

    /**
     * Only for mic.
     *
     * This is called when the PWA toggles the audio on or off.
     *
     * @param {MediaStream} stream
     * @param {boolean} enabled
     */
    stopOrResumeAudio(stream, enabled) {
        this._logger.debug("stopOrResumeAudio() [enabled:%s]", enabled);

        this._micPaused = !enabled;

        this._pauseResumeMic();
    }

    _handleStopOrResumeVideo({ enable, track }) {
        if (!enable) {
            if (this._webcamProducer && !this._webcamProducer.closed && this._webcamProducer.track === track) {
                this._stopProducer(this._webcamProducer);
                this._webcamProducer = null;
                this._webcamTrack = null;
            }
        } else {
            this._sendWebcam(track);
        }
    }
    /**
     * Only for webcam.
     *
     * This is called when the PWA toggles the webcam on or off.
     * @param {MediaStream} stream
     * @param {boolean} enabled
     */
    stopOrResumeVideo(localStream, enable) {
        this._logger.debug("stopOrResumeVideo() [enable:%s]", enable);

        this._webcamPaused = !enable;

        this._pauseResumeWebcam();

        if (!["chrome", "safari"].includes(browserName)) {
            return;
        }

        // actually turn off the camera. Chrome-only (Firefox etc. has different plans)

        if (!enable) {
            clearTimeout(this._stopCameraTimeout);

            // try to stop the local camera so the camera light goes off.
            this._stopCameraTimeout = setTimeout(() => {
                localStream.getVideoTracks().forEach((track) => {
                    if (track.enabled === false) {
                        track.stop();
                        localStream.removeTrack(track);

                        this._emitToPWA(EVENTS.LOCAL_STREAM_TRACK_REMOVED, {
                            stream: localStream,
                            track,
                        });

                        this._handleStopOrResumeVideo({ enable, track });
                    }
                });
            }, 5000);
        } else if (localStream.getVideoTracks().length === 0) {
            // re-enable the stream
            const constraints = this._webrtcProvider.getMediaConstraints().video;
            navigator.mediaDevices.getUserMedia({ video: constraints }).then((stream) => {
                const track = stream.getVideoTracks()[0];
                localStream.addTrack(track);

                this._emitToPWA(EVENTS.LOCAL_STREAM_TRACK_ADDED, {
                    streamId: localStream.id,
                    tracks: [track],
                    screenShare: false,
                });

                this._handleStopOrResumeVideo({ enable, track });
            });
        }
    }

    supportsScreenShareAudio() {
        return true;
    }

    /**
     * If the streamId and clientId are the same, this is a webcam/mic stream.
     * Otherwise, this is a screen share stream.
     *
     * @param {{
     *     streamId: string,
     *     clientId: string,
     * }} streamOptions
     */
    acceptNewStream({ streamId, clientId }) {
        this._logger.debug("acceptNewStream()", { streamId, clientId });

        // make sure we have rtcStats connection
        this.rtcStatsReconnect();

        const clientState = this._getOrCreateClientState(clientId);
        const isScreenShare = streamId !== clientId;

        if (isScreenShare) {
            clientState.hasAcceptedScreenStream = true;
            clientState.hasEmittedScreenStream = false; // re-emit stream if re-accepted
        } else {
            clientState.hasAcceptedWebcamStream = true;
            clientState.hasEmittedWebcamStream = false; // re-emit stream if re-accepted
        }

        this._syncIncomingStreamsWithPWA(clientId);
    }

    /**
     * This is called when the user resizes their window such that we neee to
     * update the consumer layers.
     *
     * @param {string} streamId
     * @param {ignored} _ignored
     * @param {{
     *    width: number,
     *    height: number,
     * }} size
     */
    updateStreamResolution(streamId, _ignored, { width, height }) {
        this._logger.debug("updateStreamResolution()", { streamId, width, height });

        const consumerId = this._streamIdToVideoConsumerId.get(streamId);
        const consumer = this._consumers.get(consumerId);

        if (!consumer) return;

        let numberOfActiveVideos = 0;
        this._consumers.forEach((c) => {
            if (c._closed || c._paused) return;
            if (c._appData?.source === "webcam" || c._appData?.source === "screenvideo") numberOfActiveVideos++;
        });

        // assume it is using T2 mode unless we detect otherwise
        let numberOfTemporalLayers = 2;
        if (/T3/.test(consumer._rtpParameters?.encodings?.[0]?.scalabilityMode || "")) numberOfTemporalLayers = 3;

        const maxSide = Math.max(width, height);
        let spatialLayer = maxSide >= 480 ? (maxSide >= 960 ? 2 : 1) : 0;
        let temporalLayer = numberOfTemporalLayers - 1; // default to full framerate

        // if we are rendering tile-like sizes we reduce framerate by 50%
        if (maxSide < 100) {
            temporalLayer = Math.max(numberOfTemporalLayers - 2, 0);
        }

        // if we are rendering many videos, we reduce framerate by 50% for all videos with lowest spatial layer
        if (numberOfActiveVideos > 8 && spatialLayer === 0) {
            temporalLayer = Math.max(numberOfTemporalLayers - 2, 0);
        }

        // increase resolution if few videos (like 1:1 on phones)
        // todo: consider spatialLayer 1 for maxSide > 200. 4 in a room is considerable higher quality on phones this way
        // ... but might conflict with our intent of mobile-mode
        if (numberOfActiveVideos < 4 && maxSide > 300 && spatialLayer === 0) {
            spatialLayer = 1;
        }

        if (consumer.appData.spatialLayer !== spatialLayer || consumer.appData.temporalLayer !== temporalLayer) {
            consumer.appData.spatialLayer = spatialLayer;
            consumer.appData.temporalLayer = temporalLayer;

            this._vegaConnection?.message("setConsumersPreferredLayers", {
                consumerIds: [consumerId],
                spatialLayer,
                temporalLayer,
            });
        }
    }

    disconnectAll() {
        this._reconnect = false;
        if (this._reconnectTimeOut) {
            clearTimeout(this._reconnectTimeOut);
            this._reconnectTimeOut = null;
        }
        this._socketListenerDeregisterFunctions.forEach((func) => {
            func();
        });

        if (this._localStreamDeregisterFunction) {
            this._localStreamDeregisterFunction();
            this._localStreamDeregisterFunction = null;
        }

        this._socketListenerDeregisterFunctions = [];

        this._vegaConnection?.removeAllListeners();
        this._vegaConnection?.close();

        // These will clean up any and all producers/consumers
        this._sendTransport?.close();
        this._receiveTransport?.close();

        this._micTrack = null;
        this._webcamTrack = null;
        this._screenVideoTrack = null;
        this._screenAudioTrack = null;

        this._streamIdToVideoConsumerId.clear();

        this._mediasoupDevice = null;
    }

    sendAudioMutedStats(muted) {
        rtcStats.sendEvent("audio_muted", { muted });
    }

    sendVideoMutedStats(muted) {
        rtcStats.sendEvent("video_muted", { muted });
    }

    sendStatsCustomEvent(eventName, data) {
        rtcStats.sendEvent(eventName, data);
    }

    rtcStatsDisconnect() {
        rtcStats.server.close();
    }

    rtcStatsReconnect() {
        if (!rtcStats.server.connected) {
            rtcStats.server.connect();
        }
    }

    _startMonitoringAudioTrack(track) {
        track.addEventListener("ended", this._audioTrackOnEnded);
    }

    _stopMonitoringAudioTrack(track) {
        track.removeEventListener("ended", this._audioTrackOnEnded);
    }

    async _onMessage(message) {
        const { method, data } = message;
        return Promise.resolve()
            .then(() => {
                switch (method) {
                    case "consumerReady":
                        return this._onConsumerReady(data);
                    case "consumerClosed":
                        return this._onConsumerClosed(data);
                    case "consumerPaused":
                        return this._onConsumerPaused(data);
                    case "consumerResumed":
                        return this._onConsumerResumed(data);
                    case "dataConsumerReady":
                        return this._onDataConsumerReady(data);
                    case "dataConsumerClosed":
                        return this._onDataConsumerClosed(data);
                    case "dominantSpeaker":
                        return this._onDominantSpeaker(data);
                    default:
                        this._logger.debug(`unknown message method "${method}"`);
                        return;
                }
            })
            .catch((error) => {
                console.error('"message" failed [error:%o]', error);
            });
    }

    async _onConsumerReady(options) {
        this._logger.debug("_onConsumerReady()", { id: options.id, producerId: options.producerId });

        const consumer = await this._receiveTransport.consume(options);

        consumer.pause();
        consumer.appData.localPaused = true;
        consumer.appData.spatialLayer = 2;

        this._consumers.set(consumer.id, consumer);
        consumer.observer.once("close", () => {
            this._consumers.delete(consumer.id);

            this._consumerClosedCleanup(consumer);
        });

        if (this._features.increaseIncomingMediaBufferOn && consumer.rtpReceiver) {
            try {
                consumer.rtpReceiver.jitterBufferTarget = MEDIA_JITTER_BUFFER_TARGET;
                // Legacy Chrome API
                consumer.rtpReceiver.playoutDelayHint = MEDIA_JITTER_BUFFER_TARGET / 1000; // seconds
            } catch (error) {
                this._logger.error("Error during setting jitter buffer target:", error);
            }
        }

        const { sourceClientId: clientId, screenShare, streamId } = consumer.appData;
        const clientState = this._getOrCreateClientState(clientId);

        if (screenShare) {
            clientState.hasEmittedScreenStream = false; // re-emit stream if updated
            clientState.screenShareStreamId = streamId;
        } else {
            clientState.hasEmittedWebcamStream = false; // re-emit stream if updated
            clientState.camStreamId = streamId;
        }

        let stream = screenShare ? clientState.screenStream : clientState.webcamStream;

        if (!stream || stream.inboundId !== streamId) {
            stream = new MediaStream();

            stream.inboundId = streamId;
            screenShare ? (clientState.screenStream = stream) : (clientState.webcamStream = stream);
        }

        if (consumer.kind === "video") {
            this._streamIdToVideoConsumerId.set(stream.id, consumer.id);
        }

        stream.addTrack(consumer.track);
        this._syncIncomingStreamsWithPWA(clientId);
    }

    async _onConsumerClosed({ consumerId, reason }) {
        this._logger.debug("_onConsumerClosed()", { consumerId, reason });

        this._consumers.get(consumerId)?.close();
    }

    _onConsumerPaused({ consumerId }) {
        this._logger.debug("_onConsumerPaused()", { consumerId });

        const consumer = this._consumers.get(consumerId);

        if (!consumer) return;

        consumer.appData.remotePaused = true;
        consumer.pause();
    }

    _onConsumerResumed({ consumerId }) {
        this._logger.debug("_onConsumerResumed()", { consumerId });

        const consumer = this._consumers.get(consumerId);

        if (!consumer) return;

        consumer.appData.remotePaused = false;

        if (!consumer.appData.localPaused) {
            consumer.resume();
        }
    }

    async _onDataConsumerReady(options) {
        this._logger.debug("_onDataConsumerReady()", { id: options.id, producerId: options.producerId });
        const consumer = await this._receiveTransport.consumeData(options);

        this._dataConsumers.set(consumer.id, consumer);
        consumer.once("close", () => {
            this._dataConsumers.delete(consumer.id);
        });

        const { clientId } = consumer.appData;

        consumer.on("message", (message) => {
            // for now we only use this for score, so ignore messages when no debugger is attached
            if (!this._micAnalyserDebugger) return;

            if (typeof message !== "string") return;

            const { score } = JSON.parse(message);
            if (score) {
                this._micAnalyserDebugger?.onConsumerScore(consumer.appData.clientId, score);
            }
        });

        this._syncIncomingStreamsWithPWA(clientId);
    }

    async _onDataConsumerClosed({ dataConsumerId, reason }) {
        this._logger.debug("_onDataConsumerClosed()", { dataConsumerId, reason });
        const consumer = this._dataConsumers.get(dataConsumerId);
        consumer?.close();
    }

    _onDominantSpeaker({ consumerId }) {
        const consumer = this._consumers.get(consumerId);

        if (!consumer) return;

        const { sourceClientId: clientId } = consumer.appData;

        this._emitToPWA(rtcManagerEvents.DOMINANT_SPEAKER, { clientId });
    }

    _consumerClosedCleanup(consumer) {
        const { sourceClientId: clientId, screenShare } = consumer.appData;
        const clientState = this._getOrCreateClientState(clientId);
        const stream = screenShare ? clientState.screenStream : clientState.webcamStream;

        if (!stream) return;

        stream.removeTrack(consumer.track);

        if (stream.getTracks().length === 0) {
            this._streamIdToVideoConsumerId.delete(stream.id);

            // We need to clean up our clientState
            // TODO: @geirbakke investigate missing mic audio if screenshare starts during reconnect
            if (screenShare) {
                clientState.screenStream = null;
                clientState.hasEmittedScreenStream = false;
                clientState.screenShareStreamId = null;
            } else {
                // We never reset the webcam stream
                clientState.hasEmittedWebcamStream = false;
                clientState.camStreamId = null;
            }
        }
    }

    _syncIncomingStreamsWithPWA(clientId) {
        const clientState = this._getOrCreateClientState(clientId);

        const {
            webcamStream,
            screenStream,
            hasEmittedWebcamStream,
            hasEmittedScreenStream,
            hasAcceptedWebcamStream,
            hasAcceptedScreenStream,
            screenShareStreamId,
            camStreamId,
        } = clientState;

        // Need to pause/resume any consumers that are part of a stream that has been
        // accepted or dosconnected by the PWA
        const toPauseConsumers = [];
        const toResumeConsumers = [];

        this._consumers.forEach((consumer) => {
            if (consumer.appData.sourceClientId !== clientId) return;

            const hasAccepted = consumer.appData.screenShare ? hasAcceptedScreenStream : hasAcceptedWebcamStream;

            if (!consumer.appData.localPaused !== hasAccepted) {
                if (hasAccepted) {
                    if (!consumer.appData.remotePaused) {
                        consumer.resume();
                    }
                    consumer.appData.localPaused = false;
                    toResumeConsumers.push(consumer.id);
                } else {
                    consumer.pause();
                    consumer.appData.localPaused = true;
                    toPauseConsumers.push(consumer.id);
                }
            }
        });

        if (toPauseConsumers.length > 0) {
            this._vegaConnection?.message("pauseConsumers", {
                consumerIds: toPauseConsumers,
            });
        }

        if (toResumeConsumers.length > 0) {
            this._vegaConnection?.message("resumeConsumers", {
                consumerIds: toResumeConsumers,
            });
        }

        // If the webcam stream has not been emitted, we emit it.
        if (webcamStream && !hasEmittedWebcamStream && hasAcceptedWebcamStream) {
            this._emitToPWA(EVENTS.STREAM_ADDED, {
                clientId,
                stream: webcamStream,
                streamId: camStreamId,
                streamType: "webcam",
            });

            clientState.hasEmittedWebcamStream = true;
        }

        // If the screen stream has not been emitted, we emit it.
        if (screenStream && !hasEmittedScreenStream && hasAcceptedScreenStream) {
            this._emitToPWA(EVENTS.STREAM_ADDED, {
                clientId,
                stream: screenStream,
                streamId: screenShareStreamId,
                streamType: "screenshare",
            });

            clientState.hasEmittedScreenStream = true;
        }
    }

    _getOrCreateClientState(clientId) {
        let clientState = this._clientStates.get(clientId);

        if (!clientState) {
            clientState = {
                hasAcceptedWebcamStream: false,
                hasAcceptedScreenStream: false,
                hasEmittedWebcamStream: false,
                hasEmittedScreenStream: false,
                webcamStream: null,
                screenStream: null,
                screenShareStreamId: null,
                camStreamId: null,
            };

            this._clientStates.set(clientId, clientState);
        }

        return clientState;
    }

    _emitToPWA(eventName, data) {
        this._emitter.emit(eventName, data);
    }

    _emitToSignal(eventName, data, callback) {
        this._serverSocket.emit(eventName, data, callback);
    }

    // this tells PWA that clients/streams should be accepted from both sides (in contrast with current P2P manager)
    shouldAcceptStreamsFromBothSides() {
        return true;
    }

    setMicAnalyserDebugger(analyserDebugger) {
        this._micAnalyserDebugger = analyserDebugger;
    }

    setMicAnalyserParams(params) {
        this._micAnalyser?.setParams(params);
    }
}

class RtcManagerDispatcher {
    constructor({ emitter, serverSocket, webrtcProvider, features }) {
        this.emitter = emitter;
        this.currentManager = null;
        serverSocket.on(PROTOCOL_RESPONSES.ROOM_JOINED, ({ room, selfId, error, eventClaim }) => {
            if (error) return; // ignore error responses which lack room
            const config = { selfId, room, emitter, serverSocket, webrtcProvider, features, eventClaim };
            const isSfu = !!room.sfuServer;
            if (this.currentManager) {
                if (this.currentManager.isInitializedWith({ selfId, roomName: room.name, isSfu })) {
                    if (this.currentManager.setEventClaim && eventClaim) {
                        this.currentManager.setEventClaim(eventClaim);
                    }
                    return;
                }
                this.currentManager.disconnectAll();
                emitter.emit(EVENTS.RTC_MANAGER_DESTROYED);
            }
            let rtcManager = null;
            if (isSfu) {
                rtcManager = new VegaRtcManager(config);
            } else {
                rtcManager = new P2pRtcManager(config);
            }
            rtcManager.setupSocketListeners();
            emitter.emit(EVENTS.RTC_MANAGER_CREATED, { rtcManager });
            this.currentManager = rtcManager;
        });
    }

    stopRtcManager() {
        if (this.currentManager) {
            this.currentManager.disconnectAll();
            this.emitter.emit(EVENTS.RTC_MANAGER_DESTROYED);
        }
    }
}

const defaultSubdomainPattern = /^(?:([^.]+)[.])?((:?[^.]+[.]){1,}[^.]+)$/;
const localstackPattern = /^(?:([^.]+)-)?(ip-[^.]*[.](?:hereby[.]dev|rfc1918[.]disappear[.]at)(?::\d+|))$/;
const localhostPattern = /^(?:([^.]+)[.])?(localhost:?\d*)/;
const serverPattern = /^(?:([^.]+)[.])?(server:?\d*)/;
const ipv4Pattern = /^(?:([^.]+)[.])?((\d+[.]){3}:?\d*)$/;

const subdomainPatterns = [
    { pattern: serverPattern, separator: "." },
    { pattern: localhostPattern, separator: "." },
    { pattern: ipv4Pattern, separator: "." },
    { pattern: localstackPattern, separator: "-" },
    { pattern: defaultSubdomainPattern, separator: "." },
];

/**
 * @param {Location} location - the location object to use to resolve branding information.
 * @return {Object} urls - urls created from this location
 */
function fromLocation({ host = "whereby.com", protocol = "https:" } = {}) {
    let subdomain = "";
    let domain = host;
    let subdomainSeparator = ".";
    for (const { separator, pattern } of subdomainPatterns) {
        const match = pattern.exec(host);
        if (match) {
            subdomain = match[1] || "";
            domain = match[2];
            subdomainSeparator = separator;
            break;
        }
    }
    const organizationDomain = !subdomain ? domain : `${subdomain}${subdomainSeparator}${domain}`;

    return {
        domain,
        domainWithSeparator: `${subdomainSeparator}${domain}`,
        organizationDomain,
        organization: `${protocol}//${organizationDomain}`,
        service: `${protocol}//${domain}`,
        subdomain,
    };
}

fromLocation(window && window.location);

class Response {
    constructor(initialValues = {}) {
        this.data = initialValues.data === undefined ? {} : initialValues.data;
        this.headers = initialValues.headers || {};
        this.status = initialValues.status || 200;
        this.statusText = initialValues.statusText || "OK";
        this.url = initialValues.url || null;
    }
}

/**
 * Asserts that value is truthy.
 *
 * @param value - The value to check.
 * @param {string} parameterName - The name of the parameter.
 */
function assertTruthy(value, parameterName) {
    assert$1.ok(value, `${parameterName} is required`);
    return value;
}
/**
 * Asserts that value is a number.
 *
 * @param value - The value to check.
 * @param {string} parameterName - The name of the parameter.
 */
function assertBoolean(value, parameterName) {
    assert$1.ok(typeof value === "boolean", `${parameterName}<boolean> is required`);
    return value;
}
/**
 * Asserts that value is a number.
 *
 * @param value - The value to check.
 * @param {string} parameterName - The name of the parameter.
 */
function assertNumber(value, parameterName) {
    assert$1.ok(typeof value === "number", `${parameterName}<number> is required`);
    return value;
}
/**
 * Asserts that value is a string.
 *
 * @param value - The value to check.
 * @param {string} parameterName - The name of the parameter.
 */
function assertString(value, parameterName) {
    assert$1.ok(typeof value === "string", `${parameterName}<string> is required`);
    return value;
}
/**
 * Asserts that value is of the provided type.
 *
 * @param value - The value to check.
 * @param type - The type that value should be.
 * @param {string} [parameterName] - The name of the parameter.
 */
function assertInstanceOf(value, type, parameterName) {
    const resolvedParameterName = parameterName || type.name[0].toLowerCase() + type.name.substring(1);
    assert$1.ok(value instanceof type, `${resolvedParameterName}<${type.name}> is required`);
    return value;
}
/**
 * Asserts that the provided room name is a valid roomName.
 *
 * @param roomName - The roomName to check.
 * @param {string} [parameterName="roomName"] - The name of the parameter.
 */
function assertRoomName(roomName, parameterName = "roomName") {
    assertString(roomName, parameterName);
    assert$1.ok(typeof roomName === "string" && roomName[0] === "/", `${parameterName} must begin with a '/'`);
    return roomName;
}
/**
 * Asserts that the provided array is a valid array.
 *
 * @param array - The array to check.
 * @param {string} [parameterName] - The name of the parameter.
 */
function assertArray(array, parameterName) {
    assert$1.ok(Array.isArray(array), `${parameterName}<array> is required`);
    return array;
}
/**
 * Asserts that value is one of the values provided in an array
 *
 * @param value - The value to check.
 * @param allowedValues - An array of allowed values
 * @param {string} parameterName - The name of the parameter.
 */
function assertOneOf(value, allowedValues, parameterName) {
    assertTruthy(value, "value");
    assertArray(allowedValues, "allowedValues");
    const isAllowed = allowedValues.includes(value);
    if (!isAllowed) {
        throw new Error(`${parameterName}<string> must be one of the following: ${allowedValues.join(", ")}`);
    }
    return value;
}
/**
 * Asserts that the provided reference is a record.
 *
 * @param {*} ref - The reference (variable or constant) to test.
 * @param {string} name - The name of the reference, will be used in the error message.
 */
function assertRecord(ref, name) {
    if (ref === null || ref === undefined || typeof ref !== "object" || Array.isArray(ref)) {
        throw new Error(`${name} must be a record. ${JSON.stringify(ref)}`);
    }
    return ref;
}
/**
 * Asserts that the provided reference is either null or of the provided primitive type.
 *
 * @param {*} ref - The reference (variable or constant) to test.
 * @param {string} type - The expected primitive type.
 * @param {string} name - The name of the reference, will be used in the error message.
 * @param {string} [message] - A custom message to use when the assertion fails.
 */
function assertNullOrType(ref, type, name, message) {
    assertString(name, "name");
    const errorMessage = message || `${name} must be null or of type ${type}`;
    assert$1.ok(ref === null || typeof ref === type, errorMessage);
}
/**
 * Asserts that the provided reference is either null or a string.
 *
 * @param {*} ref - The reference (variable or constant) to test.
 * @param {string} name - The name of the reference, will be used in the error message.
 * @param {string} [message] - A custom message to use when the assertion fails.
 */
function assertNullOrString(ref, name, message) {
    assertString(name, "name");
    assertNullOrType(ref, "string", name, message);
}

function _getAbsoluteUrl({ baseUrl, url }) {
    return baseUrl ? baseUrl + url : url;
}
/**
 * Class used for making http calls. This is just a pure
 * wrapper around the http lib that we decide to use,
 * so that we can switch implementations.
 */
class HttpClient {
    /**
     * Creates an HttpClient instance.
     *
     * @param {string} [baseUrl] - The base URL where all requests are made.
     */
    constructor({ baseUrl }) {
        assertString(baseUrl, "baseUrl");
        this._baseUrl = baseUrl;
    }
    _requestAxios(url, options) {
        const axiosOptions = Object.assign({}, options, {
            url,
            baseURL: this._baseUrl,
        });
        return axios.request(axiosOptions);
    }
    /**
     * Wrapper for the axios API
     *
     * @param {string} url - Required. URL (appended to base URL) where API call will be made
     * @param {object} options - Required. Contains the data needed for the fetch API
     * @return {Promise<Response>} - A promise which will return a Response object (https://developer.mozilla.org/en-US/docs/Web/API/Response)
     */
    request(url, options) {
        assertString(url, "url");
        assert$1.ok(url[0] === "/", 'url<String> only accepts relative URLs beginning with "/".');
        assert$1.ok(options, "options are required");
        return this._requestAxios(url, options)
            .then((response) => {
            const { data, headers, status, statusText, config } = response;
            const requestUrl = config && config.url ? _getAbsoluteUrl({ baseUrl: config.baseURL, url: config.url }) : null;
            return new Response({
                data,
                headers,
                status,
                statusText,
                url: requestUrl,
            });
        })
            .catch((error) => {
            const responseObject = error.response;
            if (!responseObject) {
                // Either server error or something else.
                throw new Error("Could not make the request.");
            }
            const { data, headers, status, statusText, config } = responseObject;
            const requestUrl = config && config.url ? _getAbsoluteUrl({ baseUrl: config.baseURL, url: config.url }) : null;
            return Promise.reject(new Response({
                data,
                headers,
                status,
                statusText,
                url: requestUrl,
            }));
        });
    }
}

/**
 * ApiClient for doing multipart/form-data requests.
 */
class MultipartHttpClient {
    constructor({ httpClient }) {
        assert$1.ok(httpClient, "httpClient is required");
        this._httpClient = httpClient;
    }
    /**
     * Convert the provided object to a FormData object containing the same keys and values.
     * @param {object} data - the data to convert.
     * @returns {FormData}
     */
    static dataToFormData(data) {
        assert$1.ok(data, "data is required");
        const fd = new FormData();
        Object.keys(data).forEach((key) => {
            const value = data[key];
            fd.append(key, value);
        });
        return fd;
    }
    /**
     * Request a resource using multipart/form-data encoding.
     *
     * @param {string} url - the url to request
     * @param {object} options - Required. Contains the data needed for the fetch API
     * @return {Promise<Response>} - A promise which will return a Response object (https://developer.mozilla.org/en-US/docs/Web/API/Response)
     */
    request(url, options = {}) {
        const headers = Object.assign(options.headers || {}, {
            "Content-Type": undefined,
        });
        return this._httpClient.request(url, Object.assign(options, {
            headers,
            transformRequest: MultipartHttpClient.dataToFormData,
        }));
    }
}

let btoa;
if (typeof window === "object") {
    btoa = window.btoa || nodeBtoa;
}
else if (typeof global === "object") {
    btoa = global.btoa || nodeBtoa;
}
else {
    btoa = nodeBtoa;
}
/**
 * Create an object, which should be merged into the request header
 * object, which deals with the header's authorisation
 */
function _getAuthHeader(credentials) {
    if (credentials && credentials.credentials) {
        const btoaStr = `${credentials.credentials.uuid}:${credentials.hmac}`;
        return { Authorization: `Basic ${btoa(btoaStr)}` };
    }
    return {};
}
const noCredentials = () => Promise.resolve(null);
class AuthenticatedHttpClient {
    constructor({ httpClient, fetchDeviceCredentials }) {
        this._httpClient = httpClient;
        this._fetchDeviceCredentials = fetchDeviceCredentials;
    }
    request(url, options) {
        return this._fetchDeviceCredentials().then((credentials) => {
            const headers = Object.assign({}, options.headers, _getAuthHeader(credentials), {
                "X-Appearin-Device-Platform": "web",
            });
            const httpClientOptions = Object.assign({}, options, { headers });
            return this._httpClient.request(url, httpClientOptions);
        });
    }
}
/**
 * Class used for all Whereby API calls.
 */
class ApiClient {
    /**
     * Create an ApiClient instance.
     */
    constructor({ baseUrl = "https://api.appearin.net", fetchDeviceCredentials = noCredentials, } = {}) {
        this.authenticatedHttpClient = new AuthenticatedHttpClient({
            httpClient: new HttpClient({
                baseUrl,
            }),
            fetchDeviceCredentials,
        });
        this.authenticatedFormDataHttpClient = new MultipartHttpClient({ httpClient: this.authenticatedHttpClient });
    }
    /**
     * Wrapper for the fetch API
     */
    request(url, options) {
        assertString(url, "url");
        assert$1.ok(url[0] === "/", 'url<String> only accepts relative URLs beginning with "/".');
        assert$1.ok(options, "options are required");
        return this.authenticatedHttpClient.request(url, options);
    }
    /**
     * Performs a multipart request where data is multipart/form-data encoded.
     */
    requestMultipart(url, options) {
        assertString(url, "url");
        assert$1.ok(url[0] === "/", 'url<String> only accepts relative URLs beginning with "/".');
        assert$1.ok(options, "options are required");
        return this.authenticatedFormDataHttpClient.request(url, options);
    }
}

/**
 * Utility function to transform the given extract function to allow
 * `undefined` and `null` values.
 * @param extract - Function to transform
 * @returns the transformed function.
 */
function nullOrExtract(extract) {
    return (data, propertyName) => {
        const record = assertRecord(data, "data");
        const value = record[propertyName];
        return value === null || value === undefined ? null : extract(data, propertyName);
    };
}
/**
 * Extract a string from the given Json object.
 * If the value is not a string, an error is thrown.
 *
 * @param data - the object to extract the value from
 * @param propertyName - the name of the parameter to extract
 * @returns the extracted value
 */
function extractString(data, propertyName) {
    const record = assertRecord(data, "data");
    return assertString(record[propertyName], propertyName);
}
const extractNullOrString = nullOrExtract(extractString);
/**
 * Extract a Date from the given Json object.
 * If the value is not a valid Date, an error is thrown.
 *
 * @param data - the object to extract the value from
 * @param propertyName - the name of the parameter to extract
 * @returns the extracted value
 */
function extractDate(data, propertyName) {
    const dateString = extractString(data, propertyName);
    const d = new Date(dateString);
    if (isNaN(d.getTime())) {
        throw new Error(`Invalid date for ${dateString}`);
    }
    return d;
}
/**
 * Extract an Array from the given Json object.
 * If the value is not a valid Array, an error is thrown.
 *
 * @param data - the object to extract the value from
 * @param propertyName - the name of the parameter to extract
 * @returns the extracted value
 **/
function extractArrayOfJson(data, propertyName) {
    const record = assertRecord(data, "data");
    return assertArray(record[propertyName], propertyName);
}
/**
 * Extract an Array from the given Json object and run the provided
 * transformer on all of the provided values.
 * If the value is not a valid Array, an error is thrown.
 *
 * @param data - the object to extract the value from
 * @param propertyName - the name of the parameter to extract
 * @param transformer - A function to transform all the Json elements extracted from the parameter
 * @returns the extracted value
 **/
function extractArray(data, propertyName, transformer) {
    return extractArrayOfJson(data, propertyName).map((value) => transformer(value));
}
/**
 * Extract a Json object from the given Json object.
 * If the value is not a valid Json object, an error is thrown.
 *
 * Note, if the extracted value is undefined, null will be returned
 * since the extractor functions don't distinguish an undefined value
 * and a null value.
 *
 * @param data - the object to extract the value from
 * @param propertyName - the name of the parameter to extract
 * @returns the extracted value
 */
function extractJson(data, propertyName) {
    const record = assertRecord(data, "data");
    const value = record[propertyName];
    return value === undefined ? null : value;
}

class Credentials {
    constructor(uuid, hmac, userId = undefined) {
        this.credentials = {
            uuid,
        };
        this.hmac = hmac;
        this.userId = userId;
    }
    toJson() {
        return Object.assign({ credentials: this.credentials, hmac: this.hmac }, (this.userId && { userId: this.userId }));
    }
    static fromJson(json) {
        return new Credentials(extractString(extractJson(json, "credentials"), "uuid"), extractString(json, "hmac"), extractNullOrString(json, "userId") || undefined);
    }
}

/**
 * Related to device calls needed to obtain credentials
 */
class DeviceService {
    constructor({ apiClient }) {
        this._apiClient = apiClient;
    }
    /**
     * Get's the device credentials needed for most of the other API services
     *
     * @return {Promise} A promise which is fulfilled or failed based on the
     * response.
     */
    getCredentials() {
        return this._apiClient
            .request("/devices", {
            method: "post",
        })
            .then(({ data }) => {
            return Credentials.fromJson(data);
        })
            .catch((error) => {
            if (error.response) {
                // The request was made, but the server responded with a status code
                // that falls out of the range of 2xx
                if (error.response.status === 404) {
                    return null;
                }
            }
            // Something happened in setting up the request that triggered an Error
            throw error;
        });
    }
}

class ChromeStorageStore {
    constructor(key, chromeStorage) {
        this._key = key;
        this._chromeStorage = chromeStorage;
    }
    /**
     * Returns the stored value for this store, or a default if not available.
     *
     * @param {(Object|Array.|string|number)} [defaultValue] - Value returned if the object can't be retrieved.
     * @return {?(Object|Array.|string|number)} Value stored, or defaultValue if not available.
     */
    loadOrDefault(defaultValue) {
        return new Promise((resolve) => {
            this._chromeStorage.get(this._key, (result) => {
                const unknownResult = result;
                resolve(unknownResult[this._key] || defaultValue);
            });
        });
    }
    /**
     * Change the value stored for this key.
     *
     * @param {(Object|Array.|string|number)} [value] - New value, will replace any previously stored.
     * @return {Promise<void>}
     */
    save(value) {
        return new Promise((resolve) => {
            this._chromeStorage.set({ [this._key]: value }, () => {
                resolve();
            });
        });
    }
}

/**
 * Utility to ease handling localStorage.
 */
class LocalStorageStore {
    constructor(key, localStorage) {
        assertTruthy(localStorage, "localStorage");
        this._key = assertString(key, "key");
        this._localStorage = localStorage;
    }
    /**
     * Returns the stored value for this store, or a default if not available.
     *
     * @param {(Object|Array.|string|number)} [defaultValue] - Value returned if the object can't be retrieved.
     * @return {?(Object|Array.|string|number)} Value stored, or defaultValue if not available.
     */
    loadOrDefault(defaultValue) {
        try {
            const value = this._localStorage.getItem(this._key);
            if (value) {
                try {
                    return Promise.resolve(JSON.parse(value));
                }
                catch (e) {
                    /* NOOP */
                }
            }
            return Promise.resolve(defaultValue);
        }
        catch (e) {
            // Cookies are blocked
            console.warn("Error getting access to storage. Are cookies blocked?", e); // eslint-disable-line no-console
            return Promise.resolve(defaultValue);
        }
    }
    /**
     * Change the value stored for this key.
     *
     * @param {(Object|Array.|string|number)} [value] - New value, will replace any previously stored.
     */
    save(value) {
        try {
            this._localStorage.setItem(this._key, JSON.stringify(value));
            return Promise.resolve();
        }
        catch (e) {
            /* NOOP, cookies are blocked */
            console.warn("Error getting access to storage. Are cookies blocked?", e); // eslint-disable-line no-console
            return Promise.reject(e);
        }
    }
}

let localStorage;
try {
    localStorage = self.localStorage;
}
catch (e) {
    localStorage = {
        getItem: () => undefined,
        key: () => undefined,
        setItem: () => undefined,
        removeItem: () => undefined,
        hasOwnProperty: () => undefined,
        length: 0,
    };
}
var localStorage$1 = localStorage;

/**
 * Events triggered by this service
 */
const events = {
    CREDENTIALS_SAVED: "credentials_saved",
};
class CredentialsService extends EventEmitter$1 {
    /**
     * Service to manage Whereby's Rest API credentials.
     *
     * @param {ObjectStore} credentialsStore - Store to manage the credentials.
     */
    constructor({ deviceService, credentialsStore, }) {
        super();
        this._deviceService = deviceService;
        this._credentialsStore = credentialsStore;
    }
    static create({ baseUrl, storeName = "CredentialsStorage", storeType = "localStorage", }) {
        const deviceService = new DeviceService({
            apiClient: new ApiClient({ baseUrl }),
        });
        let credentialsStore = null;
        if (storeType === "localStorage") {
            credentialsStore = new LocalStorageStore(storeName, localStorage$1);
        }
        else if (storeType === "chromeStorage") {
            credentialsStore = new ChromeStorageStore(storeName, window["chrome"].storage.local);
        }
        else {
            throw new Error(`Unknown store type: ${storeType}`);
        }
        return new CredentialsService({
            deviceService,
            credentialsStore,
        });
    }
    /**
     * Contacts the REST API to get new credentials. DO NOT USE directly, call getCredentials() instead.
     *
     * @see getCredentials
     * @returns {Promise.<Credentials>} - Promise that resolves with the credentials.
     */
    _fetchNewCredentialsFromApi() {
        const credentialsStore = this._credentialsStore;
        return new Promise((resolve) => {
            const fetchCredentials = () => {
                this._deviceService
                    .getCredentials()
                    .then((credentials) => {
                    return credentialsStore
                        .save(credentials ? credentials.toJson() : null)
                        .then(() => resolve(credentials));
                })
                    .catch(() => {
                    setTimeout(fetchCredentials, 2000);
                });
            };
            fetchCredentials();
        });
    }
    /**
     * Returns the current credentials without triggering an API request to get new ones.
     * If no credentials are currently available it will return null.
     *
     * @returns {?Credentials} - The credentials currently in use, null otherwise.
     */
    getCurrentCredentials() {
        return this._credentialsStore.loadOrDefault(null).then((json) => (json ? Credentials.fromJson(json) : null));
    }
    /**
     * Returns a promise that will contain the credentials for this client.
     * If no credentials are available in local storage, new ones will be fetched from the server.
     *
     * @returns {Promise.<Credentials>} - Promise that resolves with the credentials.
     */
    getCredentials() {
        if (!this.credentialsPromise) {
            this.credentialsPromise = this.getCurrentCredentials().then((storedCredentials) => {
                if (storedCredentials) {
                    return storedCredentials;
                }
                return this._fetchNewCredentialsFromApi();
            });
        }
        return this.credentialsPromise;
    }
    /**
     * Saves new credentials which replace the existing ones and abort any pending request to get new ones.
     *
     * @param {Credentials} credentials - New credentials to store.
     */
    saveCredentials(credentials) {
        this.credentialsPromise = undefined;
        return this._credentialsStore.save(credentials.toJson()).then(() => {
            this.emit(events.CREDENTIALS_SAVED, credentials);
            return credentials;
        });
    }
    /**
     * It will set the userId for the current credentials.
     *
     * @param userId - The user id to set.
     */
    setUserId(userId) {
        // Used to ensure that the userId is changed if localStorage has been modified by another tab
        return this.getCurrentCredentials()
            .then((storedCredentials) => {
            if (!storedCredentials) {
                console.error("Illegal state: no credentials to set user id for."); // eslint-disable-line no-console
            }
            const userIdChangedFromLocalStorage = storedCredentials === null || storedCredentials.userId !== userId;
            if (!userIdChangedFromLocalStorage) {
                return undefined;
            }
            return this._credentialsStore.save(Object.assign({}, storedCredentials === null || storedCredentials === void 0 ? void 0 : storedCredentials.toJson(), { userId }));
        })
            .then(() => undefined);
    }
}

const noOrganization = () => Promise.resolve(undefined);
/**
 * Class used for all Whereby organization API calls.
 */
class OrganizationApiClient {
    /**
     * Create an OrganizationApiClient instance.
     *
     * @param {Object} options - The options for the OrganizationApiClient.
     * @param {ApiClient} [options.apiClient] - The apiClient to use.
     * @param {Function} [options.fetchOrganization] - function that returns a promise with the organization.
     */
    constructor({ apiClient, fetchOrganization = noOrganization, }) {
        this._apiClient = apiClient;
        this._fetchOrganization = fetchOrganization;
        this._apiClient = apiClient;
    }
    _callRequestMethod(method, url, options) {
        assertString(url, "url");
        assert$1.ok(url[0] === "/", 'url<String> only accepts relative URLs beginning with "/".');
        assert$1.ok(options, "options are required");
        return this._fetchOrganization().then((organization) => {
            if (!organization) {
                return this._apiClient[method](url, options);
            }
            const { organizationId } = organization;
            return this._apiClient[method](`/organizations/${encodeURIComponent(organizationId)}${url}`, options);
        });
    }
    request(url, options) {
        return this._callRequestMethod("request", url, options);
    }
    requestMultipart(url, options) {
        return this._callRequestMethod("requestMultipart", url, options);
    }
}

class EmbeddedFreeTierStatus {
    constructor({ isExhausted, renewsAt, totalMinutesLimit, totalMinutesUsed, }) {
        this.isExhausted = isExhausted;
        this.renewsAt = renewsAt;
        this.totalMinutesLimit = totalMinutesLimit;
        this.totalMinutesUsed = totalMinutesUsed;
    }
    static fromJson(data) {
        return new EmbeddedFreeTierStatus({
            isExhausted: assertBoolean(data.isExhausted, "isExhausted"),
            renewsAt: new Date(assertString(data.renewsAt, "renewsAt")),
            totalMinutesLimit: assertNumber(data.totalMinutesLimit, "totalMinutesLimit"),
            totalMinutesUsed: assertNumber(data.totalMinutesUsed, "totalMinutesUsed"),
        });
    }
}

class Account {
    constructor({ basePlanId, embeddedFreeTierStatus, isDeactivated, isOnTrial, onTrialUntil, trialStatus, }) {
        this.basePlanId = basePlanId;
        this.isDeactivated = isDeactivated;
        this.isOnTrial = isOnTrial;
        this.onTrialUntil = onTrialUntil || null;
        this.trialStatus = trialStatus || null;
        this.embeddedFreeTierStatus = embeddedFreeTierStatus || null;
    }
    static fromJson(data) {
        return new Account({
            basePlanId: typeof data.basePlanId === "string" ? data.basePlanId : null,
            isDeactivated: assertBoolean(data.isDeactivated, "isDeactivated"),
            isOnTrial: assertBoolean(data.isOnTrial, "isOnTrial"),
            onTrialUntil: typeof data.onTrialUntil === "string" ? new Date(data.onTrialUntil) : null,
            trialStatus: typeof data.trialStatus === "string" ? data.trialStatus : null,
            embeddedFreeTierStatus: data.embeddedFreeTierStatus
                ? EmbeddedFreeTierStatus.fromJson(data.embeddedFreeTierStatus)
                : null,
        });
    }
}

function hasValue(value) {
    return value !== null && value !== undefined;
}
function createOrganizationLimits(limits = {}) {
    return {
        maxNumberOfInvitationsAndUsers: hasValue(limits === null || limits === void 0 ? void 0 : limits.maxNumberOfInvitationsAndUsers)
            ? Number(limits === null || limits === void 0 ? void 0 : limits.maxNumberOfInvitationsAndUsers)
            : null,
        maxNumberOfClaimedRooms: hasValue(limits === null || limits === void 0 ? void 0 : limits.maxNumberOfClaimedRooms)
            ? Number(limits === null || limits === void 0 ? void 0 : limits.maxNumberOfClaimedRooms)
            : null,
        maxRoomLimitPerOrganization: hasValue(limits === null || limits === void 0 ? void 0 : limits.maxRoomLimitPerOrganization)
            ? Number(limits === null || limits === void 0 ? void 0 : limits.maxRoomLimitPerOrganization)
            : null,
        trialMinutesLimit: hasValue(limits === null || limits === void 0 ? void 0 : limits.trialMinutesLimit) ? Number(limits === null || limits === void 0 ? void 0 : limits.trialMinutesLimit) : null,
        includedUnits: hasValue(limits === null || limits === void 0 ? void 0 : limits.includedUnits) ? Number(limits === null || limits === void 0 ? void 0 : limits.includedUnits) : null,
    };
}
class Organization {
    constructor(properties) {
        this.logoImageUrl = null;
        this.roomBackgroundImageUrl = null;
        this.roomBackgroundThumbnailUrl = null;
        this.roomKnockPageBackgroundImageUrl = null;
        this.roomKnockPageBackgroundThumbnailUrl = null;
        this.preferences = null;
        this.onboardingSurvey = null;
        this.type = null;
        assertInstanceOf(properties, Object, "properties");
        assertString(properties.organizationId, "organizationId");
        assertString(properties.organizationName, "organizationName");
        assertString(properties.subdomain, "subdomain");
        assertInstanceOf(properties.permissions, Object, "permissions");
        assertInstanceOf(properties.limits, Object, "limits");
        this.organizationId = properties.organizationId;
        this.organizationName = properties.organizationName;
        this.subdomain = properties.subdomain;
        this.permissions = properties.permissions;
        this.limits = properties.limits;
        this.account = properties.account ? new Account(properties.account) : null;
        this.logoImageUrl = properties.logoImageUrl;
        this.roomBackgroundImageUrl = properties.roomBackgroundImageUrl;
        this.roomBackgroundThumbnailUrl = properties.roomBackgroundThumbnailUrl;
        this.roomKnockPageBackgroundImageUrl = properties.roomKnockPageBackgroundImageUrl;
        this.roomKnockPageBackgroundThumbnailUrl = properties.roomKnockPageBackgroundThumbnailUrl;
        this.preferences = properties.preferences;
        this.onboardingSurvey = properties.onboardingSurvey;
        this.type = properties.type;
    }
    static fromJson(data) {
        const parsedData = assertInstanceOf(data, Object, "data");
        const preferences = ((parsedData === null || parsedData === void 0 ? void 0 : parsedData.preferences) || {});
        const onboardingSurvey = ((parsedData === null || parsedData === void 0 ? void 0 : parsedData.onboardingSurvey) || null);
        const permissions = assertInstanceOf(parsedData.permissions, Object, "permissions");
        return new Organization({
            organizationId: assertString(parsedData.organizationId, "organizationId"),
            organizationName: assertString(parsedData.organizationName, "organizationName"),
            subdomain: assertString(parsedData.subdomain, "subdomain"),
            permissions,
            limits: createOrganizationLimits(assertInstanceOf(parsedData.limits, Object, "limits")),
            account: parsedData.account ? Account.fromJson(parsedData.account) : null,
            logoImageUrl: typeof parsedData.logoImageUrl === "string" ? parsedData.logoImageUrl : null,
            roomBackgroundImageUrl: typeof parsedData.roomBackgroundImageUrl === "string" ? parsedData.roomBackgroundImageUrl : null,
            roomBackgroundThumbnailUrl: typeof parsedData.roomBackgroundThumbnailUrl === "string"
                ? parsedData.roomBackgroundThumbnailUrl
                : null,
            roomKnockPageBackgroundImageUrl: typeof parsedData.roomKnockPageBackgroundImageUrl === "string"
                ? parsedData.roomKnockPageBackgroundImageUrl
                : null,
            roomKnockPageBackgroundThumbnailUrl: typeof parsedData.roomKnockPageBackgroundThumbnailUrl === "string"
                ? parsedData.roomKnockPageBackgroundThumbnailUrl
                : null,
            preferences,
            onboardingSurvey,
            type: typeof parsedData.type === "string" ? parsedData.type : null,
        });
    }
}
Organization.GLOBAL_ORGANIZATION_ID = "1";

class OrganizationService {
    constructor({ apiClient }) {
        // apiClient is added since it will be used eventually or rather soon.
        // this is to avoid the api to be broken again
        this._apiClient = apiClient;
    }
    /**
     * Creates an organization.
     */
    createOrganization({ organizationName, subdomain, owner, }) {
        const { displayName, consents } = owner || {};
        const email = "email" in owner
            ? {
                value: owner.email,
                verificationCode: assertString(owner.verificationCode, "owner.verificationCode"),
            }
            : null;
        const idToken = "idToken" in owner ? owner.idToken : null;
        assertString(subdomain, "subdomain");
        assertString(organizationName, "organizationName");
        assertString(displayName, "owner.displayName");
        assert$1.ok(email || idToken, "owner.email or owner.idToken is required");
        if (consents) {
            assertArray(consents, "consents");
            for (const { consentRevisionId, action } of consents) {
                assertString(consentRevisionId, "consentRevisionId");
                assertNullOrString(action, "action");
            }
        }
        return this._apiClient
            .request(`/organizations`, {
            method: "POST",
            data: {
                organizationName,
                type: "private",
                subdomain,
                owner: Object.assign(Object.assign(Object.assign(Object.assign({}, (email && { email })), (idToken && { idToken })), (consents && { consents })), { displayName }),
            },
        })
            .then(({ data }) => {
            return extractString(data, "organizationId");
        });
    }
    /**
     * Retrieves the organization based on the subdomain.
     */
    getOrganizationBySubdomain(subdomain) {
        assertString(subdomain, "subdomain");
        return this._apiClient
            .request(`/organization-subdomains/${encodeURIComponent(subdomain)}/?fields=permissions,account,onboardingSurvey`, {
            method: "GET",
        })
            .then(({ data }) => {
            return Organization.fromJson(data);
        })
            .catch((res) => {
            if (res instanceof Response) {
                if (res.status === 404) {
                    return null;
                }
                throw new Error(res.statusText);
            }
            throw res;
        });
    }
    /**
     * Retrieves the organization based on the organizationId.
     *
     * Note: This endpoint should only be used to retrieve an organization when the device is linked
     * to a user in that organization. Use getOrganizationBySubdomain instead if you just want the information
     * about an organization that is mapped to a given subdomain.
     */
    getOrganizationByOrganizationId(organizationId) {
        assertString(organizationId, "organizationId");
        return this._apiClient
            .request(`/organizations/${encodeURIComponent(organizationId)}?fields=permissions,account`, {
            method: "GET",
        })
            .then(({ data }) => {
            return Organization.fromJson(data);
        })
            .catch((res) => {
            if (res instanceof Response) {
                if (res.status === 404) {
                    return null;
                }
                throw new Error(res.statusText);
            }
            throw res;
        });
    }
    /**
     * Retrieves the organizations that contain a user
     * matching provided the email+code or phoneNumber+code
     * combination.
     */
    getOrganizationsByContactPoint(options) {
        const { code } = options;
        const email = "email" in options ? options.email : null;
        const phoneNumber = "phoneNumber" in options ? options.phoneNumber : null;
        assert$1.ok((email || phoneNumber) && !(email && phoneNumber), "either email or phoneNumber is required");
        assertString(code, "code");
        const contactPoint = email ? { type: "email", value: email } : { type: "phoneNumber", value: phoneNumber };
        return this._apiClient
            .request("/organization-queries", {
            method: "POST",
            data: {
                contactPoint,
                code,
            },
        })
            .then(({ data }) => {
            return extractArray(data, "organizations", (organization) => Organization.fromJson(organization));
        });
    }
    /**
     * Retrieves the organizations that contain a user
     * matching provided the idToken
     */
    getOrganizationsByIdToken({ idToken }) {
        assertString(idToken, "idToken");
        return this._apiClient
            .request("/organization-queries", {
            method: "POST",
            data: {
                idToken,
            },
        })
            .then(({ data }) => {
            return extractArray(data, "organizations", (organization) => {
                return Organization.fromJson(Object.assign({ permissions: {}, limits: {} }, assertRecord(organization, "organization")));
            });
        });
    }
    /**
     * Retrieves the organizations containing a user
     * with either the email or phoneNumber matching the logged in user.
     *
     * This is useful for showing the possible organization that the current
     * user could log in to.
     */
    getOrganizationsByLoggedInUser() {
        return this._apiClient
            .request("/user/organizations", {
            method: "GET",
        })
            .then(({ data }) => {
            return extractArray(data, "organizations", (o) => {
                return Organization.fromJson(Object.assign({ permissions: {}, limits: {} }, assertRecord(o, "organization")));
            });
        });
    }
    /**
     * Checks if a subdomain is available and verifies its format.
     */
    getSubdomainAvailability(subdomain) {
        assertString(subdomain, "subdomain");
        return this._apiClient
            .request(`/organization-subdomains/${encodeURIComponent(subdomain)}/availability`, {
            method: "GET",
        })
            .then(({ data }) => {
            assertInstanceOf(data, Object, "data");
            return {
                status: extractString(data, "status"),
            };
        });
    }
    /**
     * Updates preferences of the organization.
     */
    updatePreferences({ organizationId, preferences, }) {
        assertTruthy(organizationId, "organizationId");
        assertTruthy(preferences, "preferences");
        return this._apiClient
            .request(`/organizations/${encodeURIComponent(organizationId)}/preferences`, {
            method: "PATCH",
            data: preferences,
        })
            .then(() => undefined);
    }
    /**
     * Delete organization
     */
    deleteOrganization({ organizationId }) {
        assertTruthy(organizationId, "organizationId");
        return this._apiClient
            .request(`/organizations/${encodeURIComponent(organizationId)}`, {
            method: "DELETE",
        })
            .then(() => undefined);
    }
}

class OrganizationServiceCache {
    constructor({ organizationService, subdomain }) {
        this._organizationService = organizationService;
        this._subdomain = subdomain;
        this._organizationPromise = null;
    }
    initOrganization() {
        return this.fetchOrganization().then(() => undefined);
    }
    fetchOrganization() {
        if (!this._organizationPromise) {
            this._organizationPromise = this._organizationService.getOrganizationBySubdomain(this._subdomain);
        }
        return this._organizationPromise;
    }
}

// @ts-nocheck
class Room {
    constructor(properties = {}) {
        assert$1.ok(properties instanceof Object, "properties<object> must be empty or an object");
        this.isClaimed = false;
        this.isBanned = false;
        this.isLocked = false;
        this.knockPage = {
            backgroundImageUrl: null,
            backgroundThumbnailUrl: null,
        };
        this.logoUrl = null;
        this.backgroundImageUrl = null;
        this.backgroundThumbnailUrl = null;
        this.type = null;
        this.legacyRoomType = null;
        this.mode = null;
        this.product = null;
        this.roomName = null;
        this.theme = null;
        this.preferences = {};
        this.protectedPreferences = {};
        this.publicProfile = null;
        // Only allow existing property names to be modified
        const validProperties = {};
        Object.getOwnPropertyNames(properties).forEach((prop) => {
            if (Object.getOwnPropertyNames(this).indexOf(prop) !== -1) {
                validProperties[prop] = properties[prop];
            }
        });
        if (properties.ownerId !== undefined) {
            this.ownerId = properties.ownerId;
        }
        if (properties.meeting !== undefined) {
            this.meeting = properties.meeting;
        }
        Object.assign(this, validProperties);
    }
}

class Meeting {
    constructor({ meetingId, roomName, roomUrl, startDate, endDate, hostRoomUrl, viewerRoomUrl }) {
        assertString(meetingId, "meetingId");
        assertString(roomName, "roomName");
        assertString(roomUrl, "roomUrl");
        assertInstanceOf(startDate, Date, "startDate");
        assertInstanceOf(endDate, Date, "endDate");
        this.meetingId = meetingId;
        this.roomName = roomName;
        this.roomUrl = roomUrl;
        this.startDate = startDate;
        this.endDate = endDate;
        this.hostRoomUrl = hostRoomUrl;
        this.viewerRoomUrl = viewerRoomUrl;
    }
    static fromJson(data) {
        return new Meeting({
            meetingId: extractString(data, "meetingId"),
            roomName: extractString(data, "roomName"),
            roomUrl: extractString(data, "roomUrl"),
            startDate: extractDate(data, "startDate"),
            endDate: extractDate(data, "endDate"),
            hostRoomUrl: extractNullOrString(data, "hostRoomUrl"),
            viewerRoomUrl: extractNullOrString(data, "viewerRoomUrl"),
        });
    }
}

// @ts-nocheck
function createRoomUrl(roomName, path = "") {
    const encodedDisplayName = encodeURIComponent(roomName.substring(1));
    return `/room/${encodedDisplayName}${path}`;
}
/**
 * Service for talking to the Room related APIs
 */
class RoomService {
    /**
     * @param {object} organizationApiClient (required)
     */
    constructor({ organizationApiClient }) {
        this._organizationApiClient = assertInstanceOf(organizationApiClient, OrganizationApiClient);
    }
    /**
     * Gets the list of visited rooms
     *
     * @param {Object} args
     * @param {Array<string>} [types=["team"]] - The type of rooms that should be fetched.
     * @param {Array<string>} [fields=["meeting"]] - The fields of room that should be fetched.
     * @returns {Promise<array>} - It will resolve with an array.
     */
    getRooms({ types, fields = [] } = {}) {
        assertArray(types, "types");
        assertArray(fields, "fields");
        return this._organizationApiClient
            .request("/room", {
            method: "GET",
            params: { types: types.join(","), fields: fields.join(","), includeOnlyLegacyRoomType: "false" },
        })
            .then(({ data }) => data.rooms.map((room) => new Room(room)));
    }
    /**
     * Gets the specified room.
     *
     * Currently information is implicitly alluded to via the servers
     * `/room/roomName` response. This method patches the data
     * tempoarily, until the day it comes back from the server.
     *
     * @returns {Promise<Room>} - It will resolve with the Room.
     */
    getRoom({ roomName, fields }) {
        assertRoomName(roomName);
        const encodedDisplayName = encodeURIComponent(roomName.substring(1));
        return this._organizationApiClient
            .request(`/rooms/${encodedDisplayName}`, {
            method: "GET",
            params: Object.assign({ includeOnlyLegacyRoomType: "false" }, (fields && { fields: fields.join(",") })),
        })
            .then(({ data }) => new Room(Object.assign({}, data, Object.assign({ roomName }, (data.meeting && { meeting: Meeting.fromJson(data.meeting) })))))
            .catch((response) => {
            if (response.status === 404) {
                return new Room({
                    roomName,
                    isClaimed: false,
                    mode: "normal",
                    product: {
                        categoryName: "personal_free",
                    },
                    type: "personal",
                    legacyRoomType: "free",
                });
            }
            if (response.status === 400 && response.data.error === "Banned room") {
                return new Room({ roomName, isBanned: true });
            }
            // Either server error or something else.
            throw new Error(response.data ? response.data.error : "Could not fetch room information");
        });
    }
    /**
     * Claims the specified room.
     *
     * @param {Object} args
     * @param {String} args.roomName - The roomName to claim
     * @param {String} args.type - The type of room to claim
     * @param {String} args.mode - The optional mode of room to claim
     * @param {[Boolean]} args.isLocked - The optional lock status of room to claim
     * @returns {Promise} - It will resolve with undefined.
     */
    claimRoom({ roomName, type, mode, isLocked }) {
        assertRoomName(roomName);
        assertString(type, "type");
        return this._organizationApiClient
            .request("/room/claim", {
            method: "POST",
            data: Object.assign(Object.assign({ roomName,
                type }, (typeof mode === "string" && { mode })), (typeof isLocked === "boolean" && { isLocked })),
        })
            .then(() => undefined)
            .catch((response) => {
            throw new Error(response.data.error || "Failed to claim room");
        });
    }
    /**
     * Unclaims the specified room.
     *
     * @param {string} roomName - the room name to unclaim.
     * @returns {Promise.<undefined>} - It will resolve with undefined.
     */
    unclaimRoom(roomName) {
        assertRoomName(roomName);
        const encodedDisplayName = encodeURIComponent(roomName.substring(1));
        return this._organizationApiClient
            .request(`/room/${encodedDisplayName}`, {
            method: "DELETE",
        })
            .then(() => undefined);
    }
    /**
     * Changes the name of the room
     *
     * @param {Object} args
     * @param {string} args.roomName - The name of the room to rename
     * @param {string} args.newRoomName - The new name
     * @returns {Promise<undefined>} - It will resolve if the room was renamed, reject for all other cases
     */
    renameRoom({ roomName, newRoomName }) {
        assertRoomName(roomName);
        assertString(newRoomName, "newRoomName");
        const encodedRoomName = encodeURIComponent(roomName.substring(1));
        return this._organizationApiClient
            .request(`/room/${encodedRoomName}/roomName`, {
            method: "PUT",
            data: { newRoomName },
        })
            .then(() => undefined);
    }
    /**
     * Changes the room mode (experimental)
     *
     * @param {string} roomName - The name of the room to change mode of
     * @param {string} mode - The name of mode to set, currently only "group" is supported
     * @returns {Promise<undefined>} - It will resolve if mode was changed, rejects for all other cases
     */
    changeMode({ roomName, mode }) {
        assertRoomName(roomName);
        assertString(mode, "mode");
        const encodedDisplayName = encodeURIComponent(roomName.substring(1));
        return this._organizationApiClient
            .request(`/room/${encodedDisplayName}/mode`, {
            method: "PUT",
            data: { mode },
        })
            .then(() => undefined);
    }
    /**
     * Updates the room prefs
     *
     * @param {string} roomName - The name of the room to change mode of
     * @param {object} preferences - The prefs you want to update and their values
     * @returns {Promise<undefined>} - It will resolve if updated, rejects for all other cases
     */
    updatePreferences({ roomName, preferences }) {
        assertRoomName(roomName);
        assertInstanceOf(preferences, Object, "preferences");
        const encodedDisplayName = encodeURIComponent(roomName.substring(1));
        return this._organizationApiClient
            .request(`/room/${encodedDisplayName}/preferences`, {
            method: "PATCH",
            data: preferences,
        })
            .then(() => undefined);
    }
    /**
     * Updates the protected room prefs
     *
     * @param {string} roomName - The name of the room to change mode of
     * @param {object} preferences - The protected prefs you want to update and their values
     * @returns {Promise<undefined>} - It will resolve if updated, rejects for all other cases
     */
    updateProtectedPreferences({ roomName, preferences }) {
        assertRoomName(roomName);
        assertInstanceOf(preferences, Object, "preferences");
        const encodedDisplayName = encodeURIComponent(roomName.substring(1));
        return this._organizationApiClient
            .request(`/room/${encodedDisplayName}/protected-preferences`, {
            method: "PATCH",
            data: preferences,
        })
            .then(() => undefined);
    }
    getRoomPermissions(roomName, { roomKey } = {}) {
        assertRoomName(roomName);
        return this._organizationApiClient
            .request(createRoomUrl(roomName, "/permissions"), Object.assign({ method: "GET" }, (roomKey && { headers: { "X-Whereby-Room-Key": roomKey } })))
            .then((response) => {
            const { permissions, limits } = response.data;
            return {
                permissions,
                limits,
            };
        });
    }
    /**
     * Gets the specified room metrics
     *
     * @param {Object} args
     * @param {string} args.roomName - The name of the room to get metrics from.
     * @param {string} args.metrics - Comma-separated list of metrics to include.
     * @param {string} args.from (optional) - Start time (inclusive) to count from in
     * ISO format. Defaults to counting from the start of time.
     * @param {string} args.to (optional) - End time (exclusive) to count up to in
     * ISO format. Defaults to counting up to the current time.
     * @returns {Promise<Object>} - It will resolve with the requested metrics.
     */
    getRoomMetrics({ roomName, metrics, from, to }) {
        assertRoomName(roomName);
        assertString(metrics, "metrics");
        return this._organizationApiClient
            .request(createRoomUrl(roomName, "/metrics"), {
            method: "GET",
            params: { metrics, from, to },
        })
            .then((response) => response.data);
    }
    /**
     * Changes the room type
     *
     * @param {Object} args
     * @param {string} args.roomName - The name of the room to change mode of
     * @param {"personal" | "personal_xl"} args.type - Room type that should be set
     * @returns {Promise<undefined>} - It will resolve if type was changed, rejects for all other cases
     */
    changeType({ roomName, type }) {
        assertRoomName(roomName);
        assertOneOf(type, ["personal", "personal_xl"], "type");
        const encodedDisplayName = encodeURIComponent(roomName.substring(1));
        return this._organizationApiClient
            .request(`/room/${encodedDisplayName}/type`, {
            method: "PUT",
            data: { type },
        })
            .then(() => undefined);
    }
    /** Gets a Forest campaign social image
     *
     * @param {Object} args
     * @param {string} args.roomName - The name of the room to get metrics from.
     * @param {number} args.count - Number to be displayed in the image as tree count.
     * @returns {Promise<string>} - It will resolve with the image url.
     */
    getForestSocialImage({ roomName, count }) {
        assertRoomName(roomName);
        assertNumber(count, "count");
        return this._organizationApiClient
            .request(createRoomUrl(roomName, `/forest-social-image/${count}`), {
            method: "GET",
        })
            .then((response) => response.data.imageUrl);
    }
}

class RoomParticipant {
    constructor({ displayName, id, stream, isAudioEnabled, isVideoEnabled }) {
        this.isLocalParticipant = false;
        this.displayName = displayName;
        this.id = id;
        this.stream = stream;
        this.isAudioEnabled = isAudioEnabled;
        this.isVideoEnabled = isVideoEnabled;
    }
}
class RemoteParticipant extends RoomParticipant {
    constructor({ displayName, id, newJoiner, streams, isAudioEnabled, isVideoEnabled, }) {
        super({ displayName, id, isAudioEnabled, isVideoEnabled });
        this.newJoiner = newJoiner;
        this.streams = streams.map((streamId) => ({ id: streamId, state: newJoiner ? "new_accept" : "to_accept" }));
    }
    addStream(streamId, state) {
        this.streams.push({ id: streamId, state });
    }
    removeStream(streamId) {
        const index = this.streams.findIndex((s) => s.id === streamId);
        if (index !== -1) {
            this.streams.splice(index, 1);
        }
    }
    updateStreamState(streamId, state) {
        const stream = this.streams.find((s) => s.id === streamId);
        if (stream) {
            stream.state = state;
        }
    }
}
class LocalParticipant extends RoomParticipant {
    constructor({ displayName, id, stream, isAudioEnabled, isVideoEnabled }) {
        super({ displayName, id, stream, isAudioEnabled, isVideoEnabled });
        this.isLocalParticipant = true;
    }
}

const sdkVersion = "2.0.0-beta3";

class RoomConnectionEvent extends CustomEvent {
    constructor(eventType, eventInitDict) {
        super(eventType, eventInitDict);
    }
}
const API_BASE_URL = "https://api.whereby.dev" ;
const SIGNAL_BASE_URL = "wss://signal.appearin.net" ;
const NON_PERSON_ROLES = ["recorder", "streamer"];
// cache last reported stream resolutions
const reportedStreamResolutions = new Map();
function createSocket() {
    const parsedUrl = new URL(SIGNAL_BASE_URL);
    const socketHost = parsedUrl.origin;
    const socketOverrides = {
        autoConnect: false,
    };
    return new ServerSocket(socketHost, socketOverrides);
}
function handleStreamAdded(remoteParticipants, { clientId, stream, streamId, streamType }) {
    if (!streamId) {
        streamId = stream.id;
    }
    const remoteParticipant = remoteParticipants.find((p) => p.id === clientId);
    if (!remoteParticipant) {
        return;
    }
    const remoteParticipantStream = remoteParticipant.streams.find((s) => s.id === streamId) || remoteParticipant.streams[0];
    if ((remoteParticipant.stream && remoteParticipant.stream.id === streamId) ||
        (!remoteParticipant.stream && streamType === "webcam") ||
        (!remoteParticipant.stream && !streamType && remoteParticipant.streams.indexOf(remoteParticipantStream) < 1)) {
        return new RoomConnectionEvent("participant_stream_added", {
            detail: { participantId: clientId, stream, streamId },
        });
    }
    // screenshare
    return new RoomConnectionEvent("screenshare_started", {
        detail: {
            participantId: clientId,
            stream,
            id: streamId,
            isLocal: false,
            hasAudioTrack: stream.getAudioTracks().length > 0,
        },
    });
}
const noop = () => {
    return;
};
const TypedEventTarget = EventTarget;
class RoomConnection extends TypedEventTarget {
    constructor(roomUrl, { displayName, localMedia, localMediaOptions: localMediaConstraints, logger, roomKey, externalId, }) {
        super();
        this.remoteParticipants = [];
        this.screenshares = [];
        this._deviceCredentials = null;
        this._ownsLocalMedia = false;
        this.organizationId = "";
        this.connectionStatus = "initializing";
        this.selfId = null;
        this.roomUrl = new URL(roomUrl); // Throw if invalid Whereby room url
        const searchParams = new URLSearchParams(this.roomUrl.search);
        this._roomKey = roomKey || searchParams.get("roomKey");
        this.roomName = this.roomUrl.pathname;
        this.logger = logger || {
            debug: noop,
            error: noop,
            info: noop,
            log: noop,
            warn: noop,
        };
        this.displayName = displayName;
        this.externalId = externalId;
        this.localMediaConstraints = localMediaConstraints;
        const urls = fromLocation({ host: this.roomUrl.host });
        // Set up local media
        if (localMedia) {
            this.localMedia = localMedia;
        }
        else if (localMediaConstraints) {
            this.localMedia = new LocalMedia(localMediaConstraints);
            this._ownsLocalMedia = true;
        }
        else {
            throw new Error("Missing constraints");
        }
        // Set up services
        this.credentialsService = CredentialsService.create({ baseUrl: API_BASE_URL });
        this.apiClient = new ApiClient({
            fetchDeviceCredentials: this.credentialsService.getCredentials.bind(this.credentialsService),
            baseUrl: API_BASE_URL,
        });
        this.organizationService = new OrganizationService({ apiClient: this.apiClient });
        this.organizationServiceCache = new OrganizationServiceCache({
            organizationService: this.organizationService,
            subdomain: urls.subdomain,
        });
        this.organizationApiClient = new OrganizationApiClient({
            apiClient: this.apiClient,
            fetchOrganization: () => __awaiter(this, void 0, void 0, function* () {
                const organization = yield this.organizationServiceCache.fetchOrganization();
                return organization || undefined;
            }),
        });
        this.roomService = new RoomService({ organizationApiClient: this.organizationApiClient });
        // Create signal socket and set up event listeners
        this.signalSocket = createSocket();
        this.signalSocket.on("new_client", this._handleNewClient.bind(this));
        this.signalSocket.on("chat_message", this._handleNewChatMessage.bind(this));
        this.signalSocket.on("client_left", this._handleClientLeft.bind(this));
        this.signalSocket.on("audio_enabled", this._handleClientAudioEnabled.bind(this));
        this.signalSocket.on("video_enabled", this._handleClientVideoEnabled.bind(this));
        this.signalSocket.on("client_metadata_received", this._handleClientMetadataReceived.bind(this));
        this.signalSocket.on("knock_handled", this._handleKnockHandled.bind(this));
        this.signalSocket.on("knocker_left", this._handleKnockerLeft.bind(this));
        this.signalSocket.on("room_joined", this._handleRoomJoined.bind(this));
        this.signalSocket.on("room_knocked", this._handleRoomKnocked.bind(this));
        this.signalSocket.on("cloud_recording_started", this._handleCloudRecordingStarted.bind(this));
        this.signalSocket.on("cloud_recording_stopped", this._handleCloudRecordingStopped.bind(this));
        this.signalSocket.on("screenshare_started", this._handleScreenshareStarted.bind(this));
        this.signalSocket.on("screenshare_stopped", this._handleScreenshareStopped.bind(this));
        this.signalSocket.on("streaming_stopped", this._handleStreamingStopped.bind(this));
        this.signalSocket.on("disconnect", this._handleDisconnect.bind(this));
        this.signalSocket.on("connect_error", this._handleDisconnect.bind(this));
        this.signalSocketManager = this.signalSocket.getManager();
        this.signalSocketManager.on("reconnect", this._handleReconnect.bind(this));
        // Set up local media listeners
        this.localMedia.addEventListener("camera_enabled", (e) => {
            const { enabled } = e.detail;
            this.signalSocket.emit("enable_video", { enabled });
            this.dispatchEvent(new RoomConnectionEvent("local_camera_enabled", { detail: { enabled } }));
        });
        this.localMedia.addEventListener("microphone_enabled", (e) => {
            const { enabled } = e.detail;
            this.signalSocket.emit("enable_audio", { enabled });
            this.dispatchEvent(new RoomConnectionEvent("local_microphone_enabled", { detail: { enabled } }));
        });
        const webrtcProvider = {
            getMediaConstraints: () => ({
                audio: this.localMedia.isMicrophoneEnabled(),
                video: this.localMedia.isCameraEnabled(),
            }),
            deferrable(clientId) {
                return !clientId;
            },
        };
        this.rtcManagerDispatcher = new RtcManagerDispatcher({
            emitter: {
                emit: this._handleRtcEvent.bind(this),
            },
            serverSocket: this.signalSocket,
            webrtcProvider,
            features: {
                lowDataModeEnabled: false,
                sfuServerOverrideHost: undefined,
                turnServerOverrideHost: undefined,
                useOnlyTURN: undefined,
                vp9On: false,
                h264On: false,
                simulcastScreenshareOn: false,
            },
            logger: this.logger,
        });
    }
    get roomKey() {
        return this._roomKey;
    }
    _handleNewChatMessage(message) {
        this.dispatchEvent(new RoomConnectionEvent("chat_message", { detail: message }));
    }
    _handleCloudRecordingStarted(event) {
        // Only handle the start failure event here. The recording is
        // considered started when the recorder client joins.
        if (event.error) {
            this.dispatchEvent(new RoomConnectionEvent("cloud_recording_started_error", {
                detail: { error: event.error, status: "error" },
            }));
        }
    }
    _handleRecorderClientJoined({ client }) {
        this.dispatchEvent(new RoomConnectionEvent("cloud_recording_started", {
            detail: {
                status: "recording",
                startedAt: client.startedCloudRecordingAt
                    ? new Date(client.startedCloudRecordingAt).getTime()
                    : new Date().getTime(),
            },
        }));
    }
    _handleStreamingStarted() {
        this.dispatchEvent(new RoomConnectionEvent("streaming_started", {
            detail: {
                status: "streaming",
                // We don't have the streaming start time stored on the
                // server, so we use the current time instead. This gives
                // an invalid timestamp for "Client B" if "Client A" has
                // been streaming for a while before "Client B" joins.
                startedAt: new Date().getTime(),
            },
        }));
    }
    _handleNewClient({ client }) {
        if (client.role.roleName === "recorder") {
            this._handleRecorderClientJoined({ client });
        }
        if (client.role.roleName === "streamer") {
            this._handleStreamingStarted();
        }
        if (NON_PERSON_ROLES.includes(client.role.roleName)) {
            return;
        }
        const remoteParticipant = new RemoteParticipant(Object.assign(Object.assign({}, client), { newJoiner: true }));
        this.remoteParticipants = [...this.remoteParticipants, remoteParticipant];
        this._handleAcceptStreams([remoteParticipant]);
        this.dispatchEvent(new RoomConnectionEvent("participant_joined", {
            detail: { remoteParticipant },
        }));
    }
    _handleClientLeft({ clientId }) {
        const remoteParticipant = this.remoteParticipants.find((p) => p.id === clientId);
        this.remoteParticipants = this.remoteParticipants.filter((p) => p.id !== clientId);
        if (!remoteParticipant) {
            return;
        }
        this.dispatchEvent(new RoomConnectionEvent("participant_left", { detail: { participantId: remoteParticipant.id } }));
    }
    _handleClientAudioEnabled({ clientId, isAudioEnabled }) {
        const remoteParticipant = this.remoteParticipants.find((p) => p.id === clientId);
        if (!remoteParticipant) {
            return;
        }
        this.dispatchEvent(new RoomConnectionEvent("participant_audio_enabled", {
            detail: { participantId: remoteParticipant.id, isAudioEnabled },
        }));
    }
    _handleClientVideoEnabled({ clientId, isVideoEnabled }) {
        const remoteParticipant = this.remoteParticipants.find((p) => p.id === clientId);
        if (!remoteParticipant) {
            return;
        }
        this.dispatchEvent(new RoomConnectionEvent("participant_video_enabled", {
            detail: { participantId: remoteParticipant.id, isVideoEnabled },
        }));
    }
    _handleClientMetadataReceived({ payload: { clientId, displayName } }) {
        const remoteParticipant = this.remoteParticipants.find((p) => p.id === clientId);
        if (!remoteParticipant) {
            return;
        }
        this.dispatchEvent(new RoomConnectionEvent("participant_metadata_changed", {
            detail: { participantId: remoteParticipant.id, displayName },
        }));
    }
    _handleKnockHandled(payload) {
        const { clientId, resolution } = payload;
        // If the knocker is not the local participant, ignore the event
        if (clientId !== this.selfId) {
            return;
        }
        if (resolution === "accepted") {
            this._roomKey = payload.metadata.roomKey;
            this._joinRoom();
        }
        else if (resolution === "rejected") {
            this.connectionStatus = "knock_rejected";
            this.dispatchEvent(new RoomConnectionEvent("connection_status_changed", {
                detail: {
                    connectionStatus: this.connectionStatus,
                },
            }));
        }
    }
    _handleKnockerLeft(payload) {
        const { clientId } = payload;
        this.dispatchEvent(new RoomConnectionEvent("waiting_participant_left", {
            detail: { participantId: clientId },
        }));
    }
    _handleRoomJoined(event) {
        const { error, isLocked, room, selfId } = event;
        this.selfId = selfId;
        if (error === "room_locked" && isLocked) {
            this.connectionStatus = "room_locked";
            this.dispatchEvent(new RoomConnectionEvent("connection_status_changed", {
                detail: {
                    connectionStatus: this.connectionStatus,
                },
            }));
            return;
        }
        // Check if we have an error
        // Check if it is a room joined error
        // Set state to connect_failed_locked
        // Set state to connect_failed_no_host
        if (room) {
            const { clients, knockers } = room;
            const localClient = clients.find((c) => c.id === selfId);
            if (!localClient)
                throw new Error("Missing local client");
            this.localParticipant = new LocalParticipant(Object.assign(Object.assign({}, localClient), { stream: this.localMedia.stream || undefined }));
            const recorderClient = clients.find((c) => c.role.roleName === "recorder");
            if (recorderClient) {
                this._handleRecorderClientJoined({ client: recorderClient });
            }
            const streamerClient = clients.find((c) => c.role.roleName === "streamer");
            if (streamerClient) {
                this._handleStreamingStarted();
            }
            this.remoteParticipants = clients
                .filter((c) => c.id !== selfId)
                .filter((c) => !NON_PERSON_ROLES.includes(c.role.roleName))
                .map((c) => new RemoteParticipant(Object.assign(Object.assign({}, c), { newJoiner: false })));
            this.connectionStatus = "connected";
            this.dispatchEvent(new RoomConnectionEvent("room_joined", {
                detail: {
                    localParticipant: this.localParticipant,
                    remoteParticipants: this.remoteParticipants,
                    waitingParticipants: knockers.map((knocker) => {
                        return { id: knocker.clientId, displayName: knocker.displayName };
                    }),
                },
            }));
        }
    }
    _handleRoomKnocked(event) {
        const { clientId, displayName } = event;
        this.dispatchEvent(new RoomConnectionEvent("waiting_participant_joined", {
            detail: { participantId: clientId, displayName },
        }));
    }
    _handleReconnect() {
        this.logger.log("Reconnected to signal socket");
        this.signalSocket.emit("identify_device", { deviceCredentials: this._deviceCredentials });
        this.signalSocket.once("device_identified", () => {
            this._joinRoom();
        });
    }
    _handleDisconnect() {
        this.connectionStatus = "disconnected";
        this.dispatchEvent(new RoomConnectionEvent("connection_status_changed", {
            detail: {
                connectionStatus: this.connectionStatus,
            },
        }));
    }
    _handleCloudRecordingStopped() {
        this.dispatchEvent(new RoomConnectionEvent("cloud_recording_stopped"));
    }
    _handleStreamingStopped() {
        this.dispatchEvent(new RoomConnectionEvent("streaming_stopped"));
    }
    _handleScreenshareStarted(screenshare) {
        const { clientId: participantId, streamId: id, hasAudioTrack } = screenshare;
        const remoteParticipant = this.remoteParticipants.find((p) => p.id === participantId);
        if (!remoteParticipant) {
            this.logger.log("WARN: Could not find participant for screenshare");
            return;
        }
        const foundScreenshare = this.screenshares.find((s) => s.id === id);
        if (foundScreenshare) {
            this.logger.log("WARN: Screenshare already exists");
            return;
        }
        remoteParticipant.addStream(id, "to_accept");
        this._handleAcceptStreams([remoteParticipant]);
        this.screenshares = [
            ...this.screenshares,
            { participantId, id, hasAudioTrack, stream: undefined, isLocal: false },
        ];
    }
    _handleScreenshareStopped(screenshare) {
        const { clientId: participantId, streamId: id } = screenshare;
        const remoteParticipant = this.remoteParticipants.find((p) => p.id === participantId);
        if (!remoteParticipant) {
            this.logger.log("WARN: Could not find participant for screenshare");
            return;
        }
        remoteParticipant.removeStream(id);
        this.screenshares = this.screenshares.filter((s) => !(s.participantId === participantId && s.id === id));
        this.dispatchEvent(new RoomConnectionEvent("screenshare_stopped", { detail: { participantId, id } }));
    }
    _handleRtcEvent(eventName, data) {
        if (eventName === "rtc_manager_created") {
            return this._handleRtcManagerCreated(data);
        }
        else if (eventName === "stream_added") {
            return this._handleStreamAdded(data);
        }
        else if (eventName === "rtc_manager_destroyed") {
            return this._handleRtcManagerDestroyed();
        }
        else {
            this.logger.log(`Unhandled RTC event ${eventName}`);
        }
    }
    _handleRtcManagerCreated({ rtcManager }) {
        var _a;
        this.rtcManager = rtcManager;
        this.localMedia.addRtcManager(rtcManager);
        if (this.localMedia.stream) {
            (_a = this.rtcManager) === null || _a === void 0 ? void 0 : _a.addNewStream("0", this.localMedia.stream, !this.localMedia.isMicrophoneEnabled(), !this.localMedia.isCameraEnabled());
        }
        if (this.remoteParticipants.length) {
            this._handleAcceptStreams(this.remoteParticipants);
        }
    }
    _handleRtcManagerDestroyed() {
        this.rtcManager = undefined;
    }
    _handleAcceptStreams(remoteParticipants) {
        var _a, _b;
        if (!this.rtcManager) {
            this.logger.log("Unable to accept streams, no rtc manager");
            return;
        }
        const shouldAcceptNewClients = (_b = (_a = this.rtcManager).shouldAcceptStreamsFromBothSides) === null || _b === void 0 ? void 0 : _b.call(_a);
        const activeBreakout = false; // TODO: Remove this once breakout is implemented
        remoteParticipants.forEach((participant) => {
            const { id: participantId, streams, newJoiner } = participant;
            streams.forEach((stream) => {
                var _a, _b;
                const { id: streamId, state: streamState } = stream;
                let newState = undefined;
                {
                    if (streamState !== "done_accept") {
                        newState = `${newJoiner && streamId === "0" ? "new" : "to"}_accept`;
                    }
                }
                if (!newState) {
                    return;
                }
                if (newState === "to_accept" ||
                    (newState === "new_accept" && shouldAcceptNewClients) ||
                    (newState === "old_accept" && !shouldAcceptNewClients)) {
                    this.logger.log(`Accepting stream ${streamId} from ${participantId}`);
                    (_a = this.rtcManager) === null || _a === void 0 ? void 0 : _a.acceptNewStream({
                        streamId: streamId === "0" ? participantId : streamId,
                        clientId: participantId,
                        shouldAddLocalVideo: streamId === "0",
                        activeBreakout,
                    });
                }
                else if (newState === "new_accept" || newState === "old_accept") ;
                else if (newState === "to_unaccept") {
                    this.logger.log(`Disconnecting stream ${streamId} from ${participantId}`);
                    (_b = this.rtcManager) === null || _b === void 0 ? void 0 : _b.disconnect(streamId === "0" ? participantId : streamId, activeBreakout);
                }
                else if (newState !== "done_accept") {
                    this.logger.warn(`Stream state not handled: ${newState} for ${participantId}-${streamId}`);
                    return;
                }
                else ;
                // Update stream state
                participant.updateStreamState(streamId, streamState.replace(/to_|new_|old_/, "done_"));
            });
        });
    }
    _handleStreamAdded(args) {
        const streamAddedEvent = handleStreamAdded(this.remoteParticipants, args);
        if (streamAddedEvent) {
            this.dispatchEvent(streamAddedEvent);
        }
    }
    _joinRoom() {
        this.signalSocket.emit("join_room", {
            avatarUrl: null,
            config: {
                isAudioEnabled: this.localMedia.isMicrophoneEnabled(),
                isVideoEnabled: this.localMedia.isCameraEnabled(),
            },
            deviceCapabilities: { canScreenshare: true },
            displayName: this.displayName,
            isCoLocated: false,
            isDevicePermissionDenied: false,
            kickFromOtherRooms: false,
            organizationId: this.organizationId,
            roomKey: this.roomKey,
            roomName: this.roomName,
            selfId: "",
            userAgent: `browser-sdk:${sdkVersion }`,
            externalId: this.externalId,
        });
    }
    join() {
        return __awaiter(this, void 0, void 0, function* () {
            if (["connected", "connecting"].includes(this.connectionStatus)) {
                console.warn(`Trying to join when room state is already ${this.connectionStatus}`);
                return;
            }
            this.signalSocket.connect();
            this.connectionStatus = "connecting";
            this.dispatchEvent(new RoomConnectionEvent("connection_status_changed", {
                detail: {
                    connectionStatus: this.connectionStatus,
                },
            }));
            const organization = yield this.organizationServiceCache.fetchOrganization();
            if (!organization) {
                throw new Error("Invalid room url");
            }
            this.organizationId = organization.organizationId;
            if (this._ownsLocalMedia) {
                yield this.localMedia.start();
            }
            // Identify device on signal connection
            this._deviceCredentials = yield this.credentialsService.getCredentials();
            this.logger.log("Connected to signal socket");
            this.signalSocket.emit("identify_device", { deviceCredentials: this._deviceCredentials });
            this.signalSocket.once("device_identified", () => {
                this._joinRoom();
            });
        });
    }
    knock() {
        this.connectionStatus = "knocking";
        this.dispatchEvent(new RoomConnectionEvent("connection_status_changed", {
            detail: {
                connectionStatus: this.connectionStatus,
            },
        }));
        this.signalSocket.emit("knock_room", {
            displayName: this.displayName,
            imageUrl: null,
            kickFromOtherRooms: true,
            liveVideo: false,
            organizationId: this.organizationId,
            roomKey: this._roomKey,
            roomName: this.roomName,
            externalId: this.externalId,
        });
    }
    leave() {
        this.connectionStatus = "disconnecting";
        if (this._ownsLocalMedia) {
            this.localMedia.stop();
        }
        if (this.rtcManager) {
            this.localMedia.removeRtcManager(this.rtcManager);
            this.rtcManager.disconnectAll();
            this.rtcManager = undefined;
        }
        if (!this.signalSocket) {
            return;
        }
        this.signalSocket.emit("leave_room");
        this.signalSocket.disconnect();
        this.connectionStatus = "disconnected";
    }
    sendChatMessage(text) {
        this.signalSocket.emit("chat_message", {
            text,
        });
    }
    setDisplayName(displayName) {
        this.signalSocket.emit("send_client_metadata", {
            type: "UserData",
            payload: {
                displayName,
            },
        });
    }
    acceptWaitingParticipant(participantId) {
        this.signalSocket.emit("handle_knock", {
            action: "accept",
            clientId: participantId,
            response: {},
        });
    }
    rejectWaitingParticipant(participantId) {
        this.signalSocket.emit("handle_knock", {
            action: "reject",
            clientId: participantId,
            response: {},
        });
    }
    updateStreamResolution({ streamId, width, height }) {
        var _a, _b;
        if (!streamId || !this.rtcManager) {
            return;
        }
        // no need to report resolution for local participant
        if (((_b = (_a = this.localParticipant) === null || _a === void 0 ? void 0 : _a.stream) === null || _b === void 0 ? void 0 : _b.id) === streamId) {
            return;
        }
        const old = reportedStreamResolutions.get(streamId);
        if (!old || old.width !== width || old.height !== height) {
            this.rtcManager.updateStreamResolution(streamId, null, { width: width || 1, height: height || 1 });
        }
        reportedStreamResolutions.set(streamId, { width, height });
    }
    startScreenshare() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const screenshareStream = this.localMedia.screenshareStream || (yield this.localMedia.startScreenshare());
            const onEnded = () => {
                this.stopScreenshare();
            };
            if ("oninactive" in screenshareStream) {
                // Chrome
                screenshareStream.addEventListener("inactive", onEnded);
            }
            else {
                // FF
                (_a = screenshareStream.getVideoTracks()[0]) === null || _a === void 0 ? void 0 : _a.addEventListener("ended", onEnded);
            }
            (_b = this.rtcManager) === null || _b === void 0 ? void 0 : _b.addNewStream(screenshareStream.id, screenshareStream, false, true);
            this.screenshares = [
                ...this.screenshares,
                {
                    participantId: this.selfId || "",
                    id: screenshareStream.id,
                    hasAudioTrack: false,
                    stream: screenshareStream,
                    isLocal: true,
                },
            ];
            this.dispatchEvent(new RoomConnectionEvent("screenshare_started", {
                detail: {
                    participantId: this.selfId || "",
                    id: screenshareStream.id,
                    hasAudioTrack: false,
                    stream: screenshareStream,
                    isLocal: true,
                },
            }));
        });
    }
    stopScreenshare() {
        var _a;
        if (this.localMedia.screenshareStream) {
            const { id } = this.localMedia.screenshareStream;
            (_a = this.rtcManager) === null || _a === void 0 ? void 0 : _a.removeStream(id, this.localMedia.screenshareStream, null);
            this.screenshares = this.screenshares.filter((s) => s.id !== id);
            this.dispatchEvent(new RoomConnectionEvent("screenshare_stopped", { detail: { participantId: this.selfId || "", id } }));
            this.localMedia.stopScreenshare();
        }
    }
    startCloudRecording() {
        this.signalSocket.emit("start_recording", {
            recording: "cloud",
        });
        this.dispatchEvent(new RoomConnectionEvent("cloud_recording_request_started"));
    }
    stopCloudRecording() {
        this.signalSocket.emit("stop_recording");
    }
}

const initialState = {
    chatMessages: [],
    remoteParticipants: [],
    connectionStatus: "initializing",
    screenshares: [],
    waitingParticipants: [],
};
function updateParticipant(remoteParticipants, participantId, updates) {
    const existingParticipant = remoteParticipants.find((p) => p.id === participantId);
    if (!existingParticipant) {
        return remoteParticipants;
    }
    const index = remoteParticipants.indexOf(existingParticipant);
    return [
        ...remoteParticipants.slice(0, index),
        Object.assign(Object.assign({}, existingParticipant), updates),
        ...remoteParticipants.slice(index + 1),
    ];
}
// omit the internal props
function convertRemoteParticipantToRemoteParticipantState(p) {
    return {
        displayName: p.displayName,
        id: p.id,
        isAudioEnabled: p.isAudioEnabled,
        isLocalParticipant: p.isLocalParticipant,
        isVideoEnabled: p.isVideoEnabled,
        stream: p.stream,
    };
}
function addScreenshare(screenshares, screenshare) {
    const existingScreenshare = screenshares.find((ss) => ss.id === screenshare.id);
    if (existingScreenshare) {
        return screenshares;
    }
    return [...screenshares, screenshare];
}
function reducer(state, action) {
    switch (action.type) {
        case "CHAT_MESSAGE":
            return Object.assign(Object.assign({}, state), { chatMessages: [...state.chatMessages, action.payload] });
        case "CLOUD_RECORDING_REQUEST_STARTED":
            return Object.assign(Object.assign({}, state), { cloudRecording: {
                    status: "requested",
                } });
        case "CLOUD_RECORDING_STARTED":
            return Object.assign(Object.assign({}, state), { cloudRecording: {
                    status: action.payload.status,
                    startedAt: action.payload.startedAt,
                } });
        case "CLOUD_RECORDING_STARTED_ERROR":
            return Object.assign(Object.assign({}, state), { cloudRecording: {
                    status: action.payload.status,
                    error: action.payload.error,
                } });
        case "CLOUD_RECORDING_STOPPED":
            delete state.cloudRecording;
            return Object.assign({}, state);
        case "ROOM_JOINED":
            return Object.assign(Object.assign({}, state), { localParticipant: action.payload.localParticipant, remoteParticipants: action.payload.remoteParticipants, waitingParticipants: action.payload.waitingParticipants, connectionStatus: "connected" });
        case "CONNECTION_STATUS_CHANGED":
            return Object.assign(Object.assign({}, state), { connectionStatus: action.payload.connectionStatus });
        case "PARTICIPANT_AUDIO_ENABLED":
            return Object.assign(Object.assign({}, state), { remoteParticipants: updateParticipant(state.remoteParticipants, action.payload.participantId, {
                    isAudioEnabled: action.payload.isAudioEnabled,
                }) });
        case "PARTICIPANT_JOINED":
            return Object.assign(Object.assign({}, state), { remoteParticipants: [...state.remoteParticipants, action.payload.paritipant] });
        case "PARTICIPANT_LEFT":
            return Object.assign(Object.assign({}, state), { remoteParticipants: [...state.remoteParticipants.filter((p) => p.id !== action.payload.participantId)] });
        case "PARTICIPANT_STREAM_ADDED":
            return Object.assign(Object.assign({}, state), { remoteParticipants: updateParticipant(state.remoteParticipants, action.payload.participantId, {
                    stream: action.payload.stream,
                }) });
        case "PARTICIPANT_VIDEO_ENABLED":
            return Object.assign(Object.assign({}, state), { remoteParticipants: updateParticipant(state.remoteParticipants, action.payload.participantId, {
                    isVideoEnabled: action.payload.isVideoEnabled,
                }) });
        case "PARTICIPANT_METADATA_CHANGED":
            return Object.assign(Object.assign({}, state), { remoteParticipants: [
                    ...state.remoteParticipants.map((p) => p.id === action.payload.participantId ? Object.assign(Object.assign({}, p), { displayName: action.payload.displayName }) : p),
                ] });
        case "LOCAL_CLIENT_DISPLAY_NAME_CHANGED":
            if (!state.localParticipant)
                return state;
            return Object.assign(Object.assign({}, state), { localParticipant: Object.assign(Object.assign({}, state.localParticipant), { displayName: action.payload.displayName }) });
        case "SCREENSHARE_STARTED":
            return Object.assign(Object.assign({}, state), { screenshares: addScreenshare(state.screenshares, {
                    participantId: action.payload.participantId,
                    id: action.payload.id,
                    hasAudioTrack: action.payload.hasAudioTrack,
                    stream: action.payload.stream,
                    isLocal: action.payload.isLocal,
                }) });
        case "SCREENSHARE_STOPPED":
            return Object.assign(Object.assign({}, state), { screenshares: state.screenshares.filter((ss) => ss.id !== action.payload.id) });
        case "LOCAL_SCREENSHARE_START_ERROR":
            return Object.assign(Object.assign({}, state), { localScreenshareStatus: undefined });
        case "LOCAL_SCREENSHARE_STARTING":
            return Object.assign(Object.assign({}, state), { localScreenshareStatus: "starting" });
        case "LOCAL_SCREENSHARE_STARTED":
            return Object.assign(Object.assign({}, state), { localScreenshareStatus: "active" });
        case "LOCAL_SCREENSHARE_STOPPED":
            return Object.assign(Object.assign({}, state), { localScreenshareStatus: undefined, screenshares: state.screenshares.filter((ss) => !ss.isLocal) });
        case "LOCAL_CAMERA_ENABLED":
            if (!state.localParticipant)
                return state;
            return Object.assign(Object.assign({}, state), { localParticipant: Object.assign(Object.assign({}, state.localParticipant), { isVideoEnabled: action.payload }) });
        case "LOCAL_MICROPHONE_ENABLED":
            if (!state.localParticipant)
                return state;
            return Object.assign(Object.assign({}, state), { localParticipant: Object.assign(Object.assign({}, state.localParticipant), { isAudioEnabled: action.payload }) });
        case "STREAMING_STARTED":
            return Object.assign(Object.assign({}, state), { liveStream: {
                    status: action.payload.status,
                    startedAt: action.payload.startedAt,
                } });
        case "STREAMING_STOPPED":
            delete state.liveStream;
            return Object.assign({}, state);
        case "WAITING_PARTICIPANT_JOINED":
            return Object.assign(Object.assign({}, state), { waitingParticipants: [
                    ...state.waitingParticipants,
                    { id: action.payload.participantId, displayName: action.payload.displayName },
                ] });
        case "WAITING_PARTICIPANT_LEFT":
            return Object.assign(Object.assign({}, state), { waitingParticipants: state.waitingParticipants.filter((wp) => wp.id !== action.payload.participantId) });
        default:
            throw state;
    }
}
const defaultRoomConnectionOptions = {
    localMediaOptions: {
        audio: true,
        video: true,
    },
};
function useRoomConnection(roomUrl, roomConnectionOptions = defaultRoomConnectionOptions) {
    const [roomConnection] = useState(() => {
        var _a;
        return new RoomConnection(roomUrl, Object.assign(Object.assign({}, roomConnectionOptions), { localMedia: ((_a = roomConnectionOptions === null || roomConnectionOptions === void 0 ? void 0 : roomConnectionOptions.localMedia) === null || _a === void 0 ? void 0 : _a._ref) || undefined }));
    });
    const [state, dispatch] = useReducer(reducer, initialState);
    function createEventListener(eventName, listener, options) {
        return {
            eventName,
            listener,
            options,
        };
    }
    const eventListeners = React.useMemo(() => [
        createEventListener("chat_message", (e) => {
            dispatch({ type: "CHAT_MESSAGE", payload: e.detail });
        }),
        createEventListener("cloud_recording_request_started", () => {
            dispatch({ type: "CLOUD_RECORDING_REQUEST_STARTED" });
        }),
        createEventListener("cloud_recording_started", (e) => {
            const { status, startedAt } = e.detail;
            dispatch({ type: "CLOUD_RECORDING_STARTED", payload: { status, startedAt } });
        }),
        createEventListener("cloud_recording_started_error", (e) => {
            dispatch({ type: "CLOUD_RECORDING_STARTED_ERROR", payload: e.detail });
        }),
        createEventListener("cloud_recording_stopped", () => {
            dispatch({ type: "CLOUD_RECORDING_STOPPED" });
        }),
        createEventListener("local_camera_enabled", (e) => {
            const { enabled } = e.detail;
            dispatch({ type: "LOCAL_CAMERA_ENABLED", payload: enabled });
        }),
        createEventListener("local_microphone_enabled", (e) => {
            const { enabled } = e.detail;
            dispatch({ type: "LOCAL_MICROPHONE_ENABLED", payload: enabled });
        }),
        createEventListener("participant_audio_enabled", (e) => {
            const { participantId, isAudioEnabled } = e.detail;
            dispatch({ type: "PARTICIPANT_AUDIO_ENABLED", payload: { participantId, isAudioEnabled } });
        }),
        createEventListener("participant_joined", (e) => {
            const { remoteParticipant } = e.detail;
            dispatch({
                type: "PARTICIPANT_JOINED",
                payload: {
                    paritipant: convertRemoteParticipantToRemoteParticipantState(remoteParticipant),
                },
            });
        }),
        createEventListener("participant_left", (e) => {
            const { participantId } = e.detail;
            dispatch({ type: "PARTICIPANT_LEFT", payload: { participantId } });
        }),
        createEventListener("participant_metadata_changed", (e) => {
            const { participantId, displayName } = e.detail;
            dispatch({ type: "PARTICIPANT_METADATA_CHANGED", payload: { participantId, displayName } });
        }),
        createEventListener("participant_stream_added", (e) => {
            const { participantId, stream } = e.detail;
            dispatch({ type: "PARTICIPANT_STREAM_ADDED", payload: { participantId, stream } });
        }),
        createEventListener("participant_video_enabled", (e) => {
            const { participantId, isVideoEnabled } = e.detail;
            dispatch({ type: "PARTICIPANT_VIDEO_ENABLED", payload: { participantId, isVideoEnabled } });
        }),
        createEventListener("connection_status_changed", (e) => {
            const { connectionStatus } = e.detail;
            dispatch({
                type: "CONNECTION_STATUS_CHANGED",
                payload: { connectionStatus },
            });
        }),
        createEventListener("room_joined", (e) => {
            const { localParticipant, remoteParticipants, waitingParticipants } = e.detail;
            dispatch({
                type: "ROOM_JOINED",
                payload: {
                    localParticipant,
                    remoteParticipants: remoteParticipants.map(convertRemoteParticipantToRemoteParticipantState),
                    waitingParticipants,
                },
            });
        }),
        createEventListener("screenshare_started", (e) => {
            const { participantId, stream, id, hasAudioTrack, isLocal } = e.detail;
            dispatch({
                type: "SCREENSHARE_STARTED",
                payload: { participantId, stream, id, hasAudioTrack, isLocal },
            });
        }),
        createEventListener("screenshare_stopped", (e) => {
            var _a;
            const { participantId, id } = e.detail;
            dispatch({
                type: "SCREENSHARE_STOPPED",
                payload: { participantId, id },
            });
            // dispach LOCAL_SCREENSHARE_STOPPED here because the exposed
            // stopScreenshare method is not called when the screenshare is
            // stopped by the browser's native stop screenshare button
            if (participantId === ((_a = state.localParticipant) === null || _a === void 0 ? void 0 : _a.id)) {
                dispatch({ type: "LOCAL_SCREENSHARE_STOPPED" });
            }
        }),
        createEventListener("streaming_started", (e) => {
            const { status, startedAt } = e.detail;
            dispatch({ type: "STREAMING_STARTED", payload: { status, startedAt } });
        }),
        createEventListener("streaming_stopped", () => {
            dispatch({ type: "STREAMING_STOPPED" });
        }),
        createEventListener("waiting_participant_joined", (e) => {
            const { participantId, displayName } = e.detail;
            dispatch({
                type: "WAITING_PARTICIPANT_JOINED",
                payload: { participantId, displayName },
            });
        }),
        createEventListener("waiting_participant_left", (e) => {
            const { participantId } = e.detail;
            dispatch({
                type: "WAITING_PARTICIPANT_LEFT",
                payload: { participantId },
            });
        }),
    ], []);
    useEffect(() => {
        eventListeners.forEach(({ eventName, listener }) => {
            roomConnection.addEventListener(eventName, listener);
        });
        roomConnection.join();
        return () => {
            eventListeners.forEach(({ eventName, listener }) => {
                roomConnection.removeEventListener(eventName, listener);
            });
            roomConnection.leave();
        };
    }, []);
    return {
        state,
        actions: {
            knock: () => {
                roomConnection.knock();
            },
            sendChatMessage: (text) => {
                roomConnection.sendChatMessage(text);
            },
            setDisplayName: (displayName) => {
                roomConnection.setDisplayName(displayName);
                dispatch({ type: "LOCAL_CLIENT_DISPLAY_NAME_CHANGED", payload: { displayName } });
            },
            toggleCamera: (enabled) => {
                roomConnection.localMedia.toggleCameraEnabled(enabled);
            },
            toggleMicrophone: (enabled) => {
                roomConnection.localMedia.toggleMichrophoneEnabled(enabled);
            },
            acceptWaitingParticipant: (participantId) => {
                roomConnection.acceptWaitingParticipant(participantId);
            },
            rejectWaitingParticipant: (participantId) => {
                roomConnection.rejectWaitingParticipant(participantId);
            },
            startCloudRecording: () => {
                var _a;
                // don't start recording if it's already started or requested
                if (state.cloudRecording && ["recording", "requested"].includes((_a = state.cloudRecording) === null || _a === void 0 ? void 0 : _a.status)) {
                    return;
                }
                roomConnection.startCloudRecording();
            },
            stopCloudRecording: () => {
                roomConnection.stopCloudRecording();
            },
            startScreenshare: () => __awaiter(this, void 0, void 0, function* () {
                dispatch({ type: "LOCAL_SCREENSHARE_STARTING" });
                try {
                    yield roomConnection.startScreenshare();
                    dispatch({ type: "LOCAL_SCREENSHARE_STARTED" });
                }
                catch (error) {
                    dispatch({ type: "LOCAL_SCREENSHARE_START_ERROR", payload: error });
                }
            }),
            stopScreenshare: () => {
                roomConnection.stopScreenshare();
            },
        },
        components: {
            VideoView: (props) => React.createElement(VideoView, Object.assign({}, props, {
                onResize: ({ stream, width, height, }) => {
                    roomConnection.updateStreamResolution({
                        streamId: stream.id,
                        width,
                        height,
                    });
                },
            })),
        },
        _ref: roomConnection,
    };
}

export { VideoView, sdkVersion, useLocalMedia, useRoomConnection };
